import React from "react";
import { newId } from "./lib/ids";
import { Icon, confirmDialog, nextJobNumber, notify, useNarrowScreen, estimateRoom, limitPrompt } from "./registry";
import { ensureLeaflet, destroyMap } from "./lib/leaflet";
import { measuredGrade } from "./lib/grade";
import { freshDraft, writeDraft, clearDraft, draftAge, DRAFT_LABEL } from "./lib/draft";
/* ============================================================
   Fence Works — Estimator
   Pulls parcel data, auto-outlines the property, splits it into
   selectable, pre-measured fence runs (with gold drag handles).
   Manual freehand draw is the fallback when no parcel is found.
   ============================================================ */
const E_money = window.VFP.money, E_money0 = window.VFP.money0;
const M_TO_FT = 3.28084;
const segFeet = (a, b) => window.L.latLng(a[0], a[1]).distanceTo(window.L.latLng(b[0], b[1])) * M_TO_FT;
const COMPASS = ["East", "South", "West", "North"];

/* ---- local-planar geometry (meters) for corner squaring ---- */
function toXY(p, o) { const k = Math.cos(o[0] * Math.PI / 180); return [(p[1] - o[1]) * k * 111320, (p[0] - o[0]) * 110540]; }
function toLL(xy, o) { const k = Math.cos(o[0] * Math.PI / 180); return [o[0] + xy[1] / 110540, o[1] + xy[0] / (k * 111320)]; }
// parcel owner strings are ALL CAPS ("THOMAS JOOSTEN") — make them presentable
function titleCaseName(s) { return String(s || "").toLowerCase().replace(/\b([a-z])/g, m => m.toUpperCase()).trim(); }
// compose the single-line `address` from structured parts — the quote, project &
// crew sheet all read customer.address, so keep it the composed source of truth.
function composeAddress(p: any) { return [p.street, [p.city, p.state].filter(Boolean).join(", "), p.zip].filter(Boolean).join(" ").replace(/\s+,/g, ",").trim(); }
// map a stored customer record into the estimator's customer state; seed `street`
// from a legacy single-line `address` when the structured parts are empty.
function seedCustomer(c: any) {
  const v: any = { name: c.name || "", phone: c.phone || "", email: c.email || "", address: c.address || "",
    street: c.street || "", city: c.city || "", state: c.state || "", zip: c.zip || "", customerId: c.id };
  if (!v.street && !v.city && !v.state && !v.zip && v.address) v.street = v.address;
  return v;
}
// angle (deg) at corner C formed by B-C-D
function cornerAngle(B, C, D) {
  const b = toXY(B, C), d = toXY(D, C);
  let a = Math.abs(Math.atan2(b[1], b[0]) - Math.atan2(d[1], d[0])) * 180 / Math.PI;
  return a > 180 ? 360 - a : a;
}
// project C onto the Thales circle of B,D so angle B-C-D becomes 90° (B,D fixed)
function squareCorner(B, C, D) {
  const o = C, b = toXY(B, o), d = toXY(D, o);
  const mx = (b[0] + d[0]) / 2, my = (b[1] + d[1]) / 2;
  const r = Math.hypot(b[0] - d[0], b[1] - d[1]) / 2;
  let vx = -mx, vy = -my, len = Math.hypot(vx, vy);
  if (len < 1e-6) { vx = -(d[1] - b[1]); vy = d[0] - b[0]; len = Math.hypot(vx, vy) || 1; }
  return toLL([mx + r * vx / len, my + r * vy / len], o);
}

function centroid(poly) {
  const n = poly.length;
  return [poly.reduce((s, p) => s + p[0], 0) / n, poly.reduce((s, p) => s + p[1], 0) / n];
}
function dirName(mid, c) {
  const dy = mid[0] - c[0], dx = mid[1] - c[1];
  return Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "East" : "West") : (dy > 0 ? "North" : "South");
}
// gate geometry: span (at real width) + swing arc, given side endpoints a,b, position t, width(ft), swing(0..3)
function gateGeometry(a, b, t, widthFt, swing) {
  const o = a, B = toXY(b, o);
  const len = Math.hypot(B[0], B[1]) || 1;
  const u = [B[0] / len, B[1] / len];
  const center = [u[0] * t * len, u[1] * t * len];
  const wm = (widthFt || 4) / 3.28084, half = wm / 2;
  const p1 = [center[0] - u[0] * half, center[1] - u[1] * half];
  const p2 = [center[0] + u[0] * half, center[1] + u[1] * half];
  const hingeLeft = !(swing & 1);
  const hinge = hingeLeft ? p1 : p2;
  const closedDir = hingeLeft ? u : [-u[0], -u[1]];
  const sideSign = (swing & 2) ? 1 : -1;
  const arc = [];
  for (let k = 0; k <= 10; k++) {
    const th = sideSign * (k / 10) * (Math.PI / 2), c = Math.cos(th), s = Math.sin(th);
    const d = [closedDir[0] * c - closedDir[1] * s, closedDir[0] * s + closedDir[1] * c];
    arc.push(toLL([hinge[0] + d[0] * wm, hinge[1] + d[1] * wm], o));
  }
  return { p1: toLL(p1, o), p2: toLL(p2, o), hinge: toLL(hinge, o), leafEnd: arc[arc.length - 1], arc };
}

/* ---- layout geometry: de-noise GIS rings (simplify) + setback (inset) --------
   County/statewide parcel rings are digitized with far more vertices than a
   fence has corners — an irregular boundary (treeline, creek, road edge) can
   carry dozens of points only a few feet apart. Used as-is, EVERY one becomes
   its own run + posts (that's the "1000 posts on the back line" problem). The
   fence is laid out FROM the property line, not traced onto its raw jitter:
   simplifyRing collapses the jitter to real corners; insetRing pulls the whole
   boundary inward by a setback (a fence almost never sits on the surveyed line).
   Both work in the file's local-planar XY (meters). */
const SIMPLIFY_TOL_FT = 2.5;                 // a point this far off the straight chord (or less) is jitter → drop it
const ftToM = (ft) => (Number(ft) || 0) / M_TO_FT;

// perpendicular distance (m) from point P to the segment A–B, in XY meters
function perpDistXY(P, A, B) {
  const dx = B[0] - A[0], dy = B[1] - A[1], len2 = dx * dx + dy * dy;
  if (len2 < 1e-9) return Math.hypot(P[0] - A[0], P[1] - A[1]);
  let t = ((P[0] - A[0]) * dx + (P[1] - A[1]) * dy) / len2;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(P[0] - (A[0] + t * dx), P[1] - (A[1] + t * dy));
}
// Ramer–Douglas–Peucker on an OPEN XY polyline (endpoints always kept)
function rdpXY(pts, epsM) {
  if (pts.length < 3) return pts.slice();
  let dmax = 0, idx = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    const d = perpDistXY(pts[i], pts[0], pts[pts.length - 1]);
    if (d > dmax) { dmax = d; idx = i; }
  }
  if (dmax > epsM) {
    const left = rdpXY(pts.slice(0, idx + 1), epsM);
    const right = rdpXY(pts.slice(idx), epsM);
    return left.slice(0, -1).concat(right);
  }
  return [pts[0], pts[pts.length - 1]];
}
// collapse a closed parcel ring's digitizing jitter into clean corners
function simplifyRing(polygon, tolFt) {
  if (!Array.isArray(polygon) || polygon.length < 5) return polygon ? polygon.map(v => v.slice()) : polygon;
  const o = centroid(polygon);
  // start the ring at the vertex farthest from the centroid — a dependable real
  // corner, so RDP's split anchors never land inside the jagged cluster.
  let start = 0, far = -1;
  polygon.forEach((p, i) => { const q = toXY(p, o); const d = q[0] * q[0] + q[1] * q[1]; if (d > far) { far = d; start = i; } });
  const xy = polygon.slice(start).concat(polygon.slice(0, start)).map(p => toXY(p, o));
  xy.push(xy[0].slice());                                   // close the loop so RDP keeps it whole
  const kept = rdpXY(xy, ftToM(tolFt || SIMPLIFY_TOL_FT));
  const out = kept.slice(0, -1).map(q => toLL(q, o));       // drop the duplicated closing point
  return out.length >= 3 ? out : polygon.map(v => v.slice());
}
// signed area of an XY ring (shoelace); the sign encodes winding direction
function ringAreaXY(P) { let a = 0; for (let i = 0, n = P.length; i < n; i++) { const u = P[i], v = P[(i + 1) % n]; a += u[0] * v[1] - v[0] * u[1]; } return a / 2; }
// intersection of two lines given as point + unit direction; null when parallel
function lineMeet(p1, u1, p2, u2) {
  const den = u1[0] * u2[1] - u1[1] * u2[0];
  if (Math.abs(den) < 1e-9) return null;
  const t = ((p2[0] - p1[0]) * u2[1] - (p2[1] - p1[1]) * u2[0]) / den;
  return [p1[0] + u1[0] * t, p1[1] + u1[1] * t];
}
// inset a closed ring inward by a setback. `dists` = feet, either one number for
// every side or a per-edge array (edge i = polygon[i]→polygon[i+1]). Returns a
// new lat/lng ring; null if the setback collapses or inverts the lot.
function insetRing(polygon, dists) {
  const n = polygon.length;
  if (n < 3) return polygon.map(v => v.slice());
  const o = centroid(polygon);
  const P = polygon.map(p => toXY(p, o));
  const ccw = ringAreaXY(P) > 0;                            // interior is LEFT of edge dir when CCW
  const distOf = (e) => ftToM(Array.isArray(dists) ? dists[e] : dists);
  const lines = [];
  for (let e = 0; e < n; e++) {
    const a = P[e], b = P[(e + 1) % n];
    const dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy) || 1;
    const ux = dx / len, uy = dy / len;
    const nx = ccw ? -uy : uy, ny = ccw ? ux : -ux;         // inward unit normal
    const d = distOf(e);
    lines.push({ p: [a[0] + nx * d, a[1] + ny * d], u: [ux, uy] });
  }
  const outXY = [];
  for (let k = 0; k < n; k++) {
    const A = lines[(k - 1 + n) % n], B = lines[k];
    outXY.push(lineMeet(A.p, A.u, B.p, B.u) || B.p.slice()); // parallel neighbours → shifted point
  }
  const a0 = ringAreaXY(P), a1 = ringAreaXY(outXY);
  if (Math.sign(a1) !== Math.sign(a0) || Math.abs(a1) < Math.abs(a0) * 0.04) return null;  // ate the lot
  return outXY.map(q => toLL(q, o));
}
// the cleanest 4-corner enclosure: a bounding rectangle oriented to the lot's
// longest edge (residential lots are mostly rectangular, so this nails them)
function boundingRect(polygon) {
  const n = polygon.length;
  if (n < 3) return polygon.map(v => v.slice());
  const o = centroid(polygon);
  const P = polygon.map(p => toXY(p, o));
  let li = 0, ll = -1;
  for (let i = 0; i < n; i++) { const a = P[i], b = P[(i + 1) % n], d = Math.hypot(b[0] - a[0], b[1] - a[1]); if (d > ll) { ll = d; li = i; } }
  const a = P[li], b = P[(li + 1) % n], ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
  const c = Math.cos(-ang), s = Math.sin(-ang);
  let lo0 = 1e9, lo1 = 1e9, hi0 = -1e9, hi1 = -1e9;
  P.forEach(p => { const x = p[0] * c - p[1] * s, y = p[0] * s + p[1] * c; lo0 = Math.min(lo0, x); hi0 = Math.max(hi0, x); lo1 = Math.min(lo1, y); hi1 = Math.max(hi1, y); });
  const c2 = Math.cos(ang), s2 = Math.sin(ang);
  return [[lo0, lo1], [hi0, lo1], [hi0, hi1], [lo0, hi1]].map(([x, y]) => toLL([x * c2 - y * s2, x * s2 + y * c2], o));
}
// point-in-ring + nearest-point-on-segment, both in XY meters
function pointInRingXY(P, R) { let c = false; for (let i = 0, j = R.length - 1; i < R.length; j = i++) { const xi = R[i][0], yi = R[i][1], xj = R[j][0], yj = R[j][1]; if (((yi > P[1]) !== (yj > P[1])) && (P[0] < (xj - xi) * (P[1] - yi) / (yj - yi) + xi)) c = !c; } return c; }
function nearestOnSegXY(P, A, B) { const dx = B[0] - A[0], dy = B[1] - A[1], l2 = dx * dx + dy * dy; let t = l2 < 1e-9 ? 0 : ((P[0] - A[0]) * dx + (P[1] - A[1]) * dy) / l2; t = t < 0 ? 0 : t > 1 ? 1 : t; return [A[0] + t * dx, A[1] + t * dy]; }
// pull any vertex that landed OUTSIDE the lot back onto the nearest lot edge, so
// "Rectangle"/"Square up" (which can bulge a corner past the boundary) never
// place the fence outside the surveyed property line.
function clampToLot(poly, lot) {
  if (!Array.isArray(lot) || lot.length < 3) return poly;
  const o = centroid(lot);
  const L = lot.map(p => toXY(p, o));
  return poly.map(v => {
    const V = toXY(v, o);
    if (pointInRingXY(V, L)) return v;            // already inside — leave it
    let best = null, bd = Infinity;               // else snap to the closest point on the lot boundary
    for (let i = 0; i < L.length; i++) { const q = nearestOnSegXY(V, L[i], L[(i + 1) % L.length]); const d = Math.hypot(q[0] - V[0], q[1] - V[1]); if (d < bd) { bd = d; best = q; } }
    return best ? toLL(best, o) : v;
  });
}
// "square up": straighten a shape WITHOUT forcing a bounding box. Snap the
// corners that already sit near 90° to true right angles — leaving intentional
// angles and short runs/jogs (gates, stubs) alone. Same safe pass as the
// "Square all" button, factored out so a freshly de-jagged ring comes out clean.
function squareCornersPoly(poly, closed) {
  const p = poly.map(v => v.slice());
  const MIN_SIDE_FT = 14;   // never distort runs shorter than this (gates, jogs, stubs)
  const ANG_TOL = 28;       // only true-up corners already within this of 90°
  for (let pass = 0; pass < 3; pass++) {
    const src = p.map(v => v.slice()), n = src.length;
    const lo = closed ? 0 : 1, hi = closed ? n : n - 1;
    for (let i = lo; i < hi; i++) {
      const B = src[(i - 1 + n) % n], C = src[i], D = src[(i + 1) % n];
      if (segFeet(B, C) < MIN_SIDE_FT || segFeet(C, D) < MIN_SIDE_FT) continue;
      if (Math.abs(cornerAngle(B, C, D) - 90) > ANG_TOL) continue;
      p[i] = squareCorner(B, C, D);
    }
  }
  return p;
}

function buildSides(polygon, prev, closed?) {
  closed = closed !== false;
  const c = centroid(polygon);
  const count = closed ? polygon.length : polygon.length - 1;
  const sides = [];
  for (let i = 0; i < count; i++) {
    const a = polygon[i], b = polygon[(i + 1) % polygon.length];
    const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const pv = prev && prev[i];
    sides.push({ i, a, b, lf: segFeet(a, b), name: dirName(mid, c), selected: pv ? pv.selected : true, grade: (pv && pv.grade) || "level", gradeAuto: pv ? (pv.gradeAuto !== false) : true, autoSlope: pv ? pv.autoSlope : null, profile: (pv && pv.profile) || null });
  }
  // suffix sides that share a compass direction (e.g. after a split): South — A / B
  const counts = {};
  sides.forEach(s => { counts[s.name] = (counts[s.name] || 0) + 1; });
  const seen = {};
  sides.forEach(s => { if (counts[s.name] > 1) { seen[s.name] = (seen[s.name] || 0) + 1; s.suffix = String.fromCharCode(64 + seen[s.name]); } });
  return sides;
}

// signed inches label for a measured grade point: −3.5″ / +12″
const fmtIn = (v) => ((v || 0) < 0 ? "−" : "+") + (+Math.abs(v || 0).toFixed(1)) + "″";

// run length that tolerates number OR string lf — the quote's Edit-estimate
// panel feeds its controlled <input> value (a string, "" mid-edit) straight
// into the grade-point widgets. Never NaN; 0 when blank/invalid so callers
// can refuse to place a point until a real length exists.
const lfFeet = (lf) => { const n = parseFloat(lf); return isFinite(n) && n > 0 ? n : 0; };

/* ---- absolute corner elevations (parcel.cornerElev = { [cornerIdx]: inches }) ----
   Sparse map keyed by polygon vertex index, so it must be remapped whenever
   corners are inserted / removed / rotated — exactly like gates are. */
function remapCornerElev(ce, f) {
  if (!ce) return null;
  const out = {};
  let any = false;
  Object.keys(ce).forEach(k => { const nk = f(+k); if (nk != null && nk >= 0 && isFinite(+ce[k])) { out[nk] = +ce[k]; any = true; } });
  return any ? out : null;
}
const ceInsertAt = (ce, at) => remapCornerElev(ce, k => (k >= at ? k + 1 : k));   // vertex inserted at index `at`
const ceRemoveAt = (ce, at) => remapCornerElev(ce, k => (k === at ? null : k > at ? k - 1 : k)); // vertex removed

const GRADES = {
  level:      { label: "Level",                        factor: 0 },
  slope_to:   { label: "Sloped \u2014 Towards House",       factor: 0.15 },
  slope_away: { label: "Sloped \u2014 Away From House",     factor: 0.15 },
  step_to:    { label: "Stepped \u2014 Towards House",      factor: 0.25 },
  step_away:  { label: "Stepped \u2014 Away From House",    factor: 0.25 },
};

/* ---- near-perfect panel fit (design aid) ----
   For a run of L ft and panel width w: leftover r = L - floor(L/w)*w. When the
   run is within FIT_TOL_FT of a clean multiple (but not already exact, +/-0.1 ft),
   suggest the smaller nudge -- shorten by r or lengthen by w-r -- to land on a
   whole number of full panels with no cut panel. Pure hint: nothing changes
   unless the contractor explicitly taps "Snap to fit". */
const FIT_TOL_FT = 2;
function panelFitSuggestion(L, w) {
  if (!(L > 0) || !(w > 0)) return null;
  const k = Math.floor(L / w + 1e-9);
  const r = L - k * w;
  if (r <= 0.1 || w - r <= 0.1) return null; // already an exact fit (within ~1")
  // smaller move wins; can't shorten below one panel, and a tie keeps footage (lengthen)
  const pick = (k >= 1 && r < w - r)
    ? { dir: "shorten",  delta: r,     target: k * w,       panels: k }
    : { dir: "lengthen", delta: w - r, target: (k + 1) * w, panels: k + 1 };
  return pick.delta <= FIT_TOL_FT ? pick : null;
}

/* recent address searches — remembered across sessions so you can re-pick a
   property without retyping (shown when the search box is focused & empty). */
const RECENTS_KEY = "vfp.recentSearches";
function loadRecents() { try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]"); } catch { return []; } }
function saveRecent(s) {
  try {
    if (!s || s.lat == null || s.lng == null) return;
    const key = (x) => (x.line1 || "") + "|" + (x.line2 || "");
    const entry = { line1: s.line1, line2: s.line2 || "", lat: s.lat, lng: s.lng, address: s.address || s.line1 };
    const next = [entry, ...loadRecents().filter((x) => key(x) !== key(entry))].slice(0, 8);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch { /* private mode / quota — recents are best-effort */ }
}
// drop duplicate suggestions (same address can come back twice from the geocoder)
function dedupeSugg(rows) {
  const seen = new Set(), out = [];
  (rows || []).forEach((r) => { const k = (r.line1 || "") + "|" + (r.line2 || ""); if (!seen.has(k)) { seen.add(k); out.push(r); } });
  return out;
}

function Estimator({ store, go, params, settings, auth }: any) {
  const mapEl = React.useRef(null);
  const map = React.useRef(null);
  const lay = React.useRef<any>({ items: [] });
  const draggingRef = React.useRef(false);
  const shiftRef = React.useRef(false);
  const squareDragRef = React.useRef(false);
  const cmdRef = React.useRef(false);
  const lpRef = React.useRef(null);   // long-press timer
  const hist = React.useRef<any>({ stack: [], idx: -1, applying: false });
  const lfRef = React.useRef<any>(0);   // live footage, read by the sandbox tour

  const [parcel, setParcelRaw] = React.useState<any>(null);   // {polygon, sides, owner, parcelId, address, provider}
  // Every function updater below edits the parcel that is loaded. With none
  // (manual draw, cleared, a stale handle on a phone) a tap on a side control
  // is a no-op, not "null is not an object (evaluating 'p.sides')" — the one
  // crash the ops page has from /estimator on iOS.
  const setParcel = React.useCallback((v: any) => setParcelRaw((p: any) => (typeof v === "function" ? (p == null ? p : v(p)) : v)), []);
  const [manual, setManual] = React.useState<any>(false);
  const [mVerts, setMVerts] = React.useState<any>([]);
  const [loading, setLoading] = React.useState<any>(false);
  const [productId, setProductId] = React.useState<any>(window.VFP.PRODUCTS[0].id);
  const [corners, setCorners] = React.useState<any>(0);
  const [gates, setGates] = React.useState<any>(0);
  const [removal, setRemoval] = React.useState<any>("none");
  const [clearingHours, setClearingHours] = React.useState<any>(0);
  const [slope, setSlope] = React.useState<any>(false);
  const [rockClause, setRockClause] = React.useState<any>(false);
  const [permit, setPermit] = React.useState<any>(false);
  const [jchannel, setJchannel] = React.useState<any>(false);   // premium clean-finish post trim (vinyl only, off by default)
  // per-estimate add-on adjustments (blank = the workspace default) — each
  // toggle expands a small editor when on, so "not always 15% / $100" is a
  // two-second tweak on the estimate instead of a Settings trip.
  const [slopeAdj, setSlopeAdj] = React.useState<any>({ mode: "pct", value: "" });   // % uplift or flat $
  const [permitFeeVal, setPermitFeeVal] = React.useState<any>("");                    // $ pass-through
  const [uchannelCostVal, setUchannelCostVal] = React.useState<any>("");              // $ per channel
  const [rockNoteVal, setRockNoteVal] = React.useState<any>("");                      // clause wording
  const [travelMiles, setTravelMiles] = React.useState<any>(0);
  const [customer, setCustomer] = React.useState<any>({ name: "", phone: "", email: "", address: "", street: "", city: "", state: "", zip: "" });
  const [editingProject, setEditingProject] = React.useState<any>(() => (params && params.loadProject) ? { id: params.loadProject } : null);
  const [sentQuoteId, setSentQuoteId] = React.useState<any>(() => (params && params.quoteId) || null);
  const [quoteNotes, setQuoteNotes] = React.useState<any>("");
  const [siteVisitRequired, setSiteVisitRequired] = React.useState<any>(false);
  const [leadSource, setLeadSource] = React.useState<any>("");   // how this lead found you (marketing ROI)
  const [repName, setRepName] = React.useState<any>("");         // sales rep credited with this estimate (dashboard leaderboard)
  // default the rep to the signed-in user once auth hydrates — but never
  // overwrite a rep that was typed, picked, or restored from a saved estimate.
  React.useEffect(() => { const u = auth && auth.user; if (u) setRepName(r => r || u.name || u.email || ""); }, [auth && auth.user]);
  // rep suggestions: the roster (Business Health → Sales Team), the signed-in
  // user, and every rep already credited on a quote/project
  const repOptions = React.useMemo(() => {
    const set = new Set<string>();
    ((store.settings && store.settings.salesReps) || []).forEach((r: any) => { if (r.name) set.add(r.name); });
    const u = auth && auth.user; if (u && (u.name || u.email)) set.add(u.name || u.email);
    (store.quotes || []).forEach((q: any) => { if (q.rep) set.add(q.rep); });
    (store.projects || []).forEach((p: any) => { if (p.rep) set.add(p.rep); });
    return [...set].sort();
  }, [store.quotes, store.projects, store.settings && store.settings.salesReps, auth && auth.user]);
  // open on a desktop; folded on a phone, where the sheet's height is the
  // property + customer + fence panel's to spend
  const [showCost, setShowCost] = React.useState<any>(() => { try { return !window.matchMedia("(max-width: 880px)").matches; } catch (e) { return true; } });
  const [search, setSearch] = React.useState<any>("");
  const [sugg, setSugg] = React.useState<any>([]);
  const [showSugg, setShowSugg] = React.useState<any>(false);
  const [suggIdx, setSuggIdx] = React.useState<any>(-1);
  const suggTimer = React.useRef(null);
  const suggSeq = React.useRef(0);
  const [toast, setToast] = React.useState<any>(null);
  const [mapLocked, setMapLocked] = React.useState<any>(false);
  const [squareDrag, setSquareDrag] = React.useState<any>(false);   // snap a corner to 90° after you release it
  const [showPosts, setShowPosts] = React.useState<any>(false);     // overlay post positions from the product spacing
  const [showPropLine, setShowPropLine] = React.useState<any>(true);// faint TRUE county boundary under the editable fence
  const [setbackStr, setSetbackStr] = React.useState<any>("");      // global "X ft off property line" input
  const [setbackScope, setSetbackScope] = React.useState<any>("all");// "all" sides | "side" (selected / rear line only)
  const [boxSelect, setBoxSelect] = React.useState<any>(false);     // marquee-delete corners (drag a box on the map)
  const [showKeys, setShowKeys] = React.useState<any>(false);       // keyboard-shortcuts cheat sheet (centered modal)
  const [showMapKeys, setShowMapKeys] = React.useState<any>(true);  // persistent on-map shortcut legend
  const [showHouse, setShowHouse] = React.useState<any>(true);      // show the detected house footprint
  const [houseBusy, setHouseBusy] = React.useState<any>(false);     // house detection in flight
  const houseTok = React.useRef(0);                                 // supersede a stale detect when a new parcel loads
  const [fences, setFences] = React.useState<any>([]);              // additional saved fences on this estimate
  const FENCE_COLORS = ["#2ff3d6", "#f59e6b", "#a78bfa", "#f5c451", "#6bd98a", "#6ba8f5"];
  const [cornerMenu, setCornerMenu] = React.useState<any>(null);   // { idx, x, y, angle }
  const [sideMenu, setSideMenu] = React.useState<any>(null);       // { i, x, y }
  const [gradeMode, setGradeMode] = React.useState<any>(false);    // click a run to drop a measured grade point
  const gradeModeRef = React.useRef(false);
  React.useEffect(() => { gradeModeRef.current = gradeMode; }, [gradeMode]);
  const [gradePop, setGradePop] = React.useState<any>(null);       // add: { i, t, x, y } · edit: { i, idx, t, deltaIn, x, y }
  const [cornerElevPop, setCornerElevPop] = React.useState<any>(null); // { idx, x, y } — absolute corner elevation editor
  const [gpOpen, setGpOpen] = React.useState<any>({});             // per-side "Grade points" editor expanded
  const [activeSide, setActiveSide] = React.useState<any>(null);   // last-touched side index (keyboard target)
  const activeSideRef = React.useRef(null);
  React.useEffect(() => { activeSideRef.current = activeSide; }, [activeSide]);
  const [activeCorner, setActiveCorner] = React.useState<any>(null);  // selected corner — Delete removes it
  const activeCornerRef = React.useRef(null);
  React.useEffect(() => { activeCornerRef.current = activeCorner; }, [activeCorner]);
  React.useEffect(() => { if (activeSide != null) setActiveCorner(null); }, [activeSide]);   // selecting a side clears the corner selection
  React.useEffect(() => { squareDragRef.current = squareDrag; }, [squareDrag]);

  const refs = React.useRef<any>({});
  refs.current = { parcel, manual, mVerts, mapLocked };
  const tourMarks = React.useRef<any>({});   // what the sandbox walkthrough coaches (H, hold-for-actions)

  const product = window.VFP.PRODUCTS.find(p => p.id === productId);
  const isVinyl = !!product && product.category === "Vinyl";   // U-channel post trim is a vinyl-only add-on
  const lf = manual ? mVerts.slice(1).reduce((t, v, i) => t + segFeet(mVerts[i], v), 0)
                    : (parcel ? parcel.sides.filter(s => s.selected).reduce((t, s) => t + s.lf, 0) : 0);
  const selCount = parcel ? parcel.sides.filter(s => s.selected).length : 0;
  // per-run lengths drive accurate panel/post counts; a fully-selected parcel is a closed loop
  const runs = manual
    ? (mVerts.length > 1 ? [lf] : [])
    : (parcel ? parcel.sides.filter(s => s.selected).map(s => s.lf) : []);
  const closedLoop = !manual && !!parcel && parcel.closed !== false && selCount > 0 && selCount === parcel.sides.length;
  // per-side grade -> blended uplift % weighted by selected footage
  const gSel = parcel ? parcel.sides.filter(s => s.selected) : [];
  const gNum = gSel.reduce((t, s) => t + s.lf * ((GRADES[s.grade] || GRADES.level).factor), 0);
  const gDen = gSel.reduce((t, s) => t + s.lf, 0) || 1;
  const gradePct = manual ? 0 : gNum / gDen;
  const placedGates = (parcel && parcel.gates) ? parcel.gates : [];
  const gateCount = parcel ? placedGates.length : gates;
  // per-gate widths drive the size-dependent "Gate Installed" line; on a manual
  // quote there's only a count (no drawn gates) → null lets the engine default it.
  const gateSizes = placedGates.length ? placedGates.map(g => g.width) : null;
  // parcel rides along so the engine can price measured grade profiles (Settings → Quoting)
  // add-on overrides ride the input only when actually typed (null = defaults)
  const addonAdj = {
    slopeAdj: slope && slopeAdj.value !== "" ? { mode: slopeAdj.mode, value: Math.max(0, +slopeAdj.value || 0) } : null,
    permitFee: permit && permitFeeVal !== "" ? Math.max(0, +permitFeeVal || 0) : null,
    uchannelCost: jchannel && uchannelCostVal !== "" ? Math.max(0, +uchannelCostVal || 0) : null,
    rockNote: rockClause && rockNoteVal.trim() ? rockNoteVal.trim() : null,
  };
  const quoteInput = { linearFeet: lf, runs, closed: closedLoop, gradePct, corners, gates: gateCount, gateSizes, removal, clearingHours, slope, rockClause, permit, jchannel: isVinyl && jchannel, travelMiles, parcel: manual ? null : parcel, ...addonAdj };
  const quote = window.VFP.computeQuote(quoteInput, product, settings);

  // combined totals across all fences on this estimate (saved + the one being drawn)
  const savedQuotes = fences.map(f => ({ ...f, q: window.VFP.computeQuote(f.snapshot, window.VFP.PRODUCTS.find(p => p.id === f.snapshot.productId) || product, settings) }));
  const savedTotal = savedQuotes.reduce((t, f) => t + f.q.customerTotal, 0);
  const savedLF = savedQuotes.reduce((t, f) => t + f.q.LF, 0);
  const grandTotal = (lf >= 1 ? quote.customerTotal : 0) + savedTotal;
  const grandLF = (lf >= 1 ? lf : 0) + savedLF;
  // an outlined-but-unsent estimate lives only in this tab: warn before a
  // refresh / close throws it away (the browser shows its own generic prompt)
  const unsentRef = React.useRef(false); unsentRef.current = lf > 0 && !sentQuoteId;
  React.useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (unsentRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  /* EST-05: the warning above stops a refresh; it never KEPT anything. A phone
     call, a back gesture or a dropped connection took the traced geometry and
     the customer's details with it. The snapshot/restore pair already existed
     for saved projects — this puts it behind an autosave so an interruption
     costs nothing. "Saved on this device" is said as exactly that; it is not
     the cloud, and if the phone is gone so is the draft. */
  const DRAFT_KEY = "fw_estimator_draft";
  const [draftState, setDraftState] = React.useState<any>("idle");
  const [resume, setResume] = React.useState<any>(() => freshDraft(DRAFT_KEY));
  const draftTimer = React.useRef<any>(null);
  React.useEffect(() => {
    if (!(lf > 0) || sentQuoteId) return;
    setDraftState("unsaved");
    clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      setDraftState("saving");
      const label = (customer && customer.name) || (parcel && parcel.address) || `${Math.round(lf)} ft traced`;
      setDraftState(writeDraft(DRAFT_KEY, { snapshot: snapshot(), fences: fences.map(f => ({ name: f.name, snapshot: f.snapshot })) }, label));
    }, 900);
    return () => clearTimeout(draftTimer.current);
  }, [lf, runs, gateCount, productId, customer, sentQuoteId]);
  // a sent estimate is no longer a draft
  React.useEffect(() => { if (sentQuoteId) { clearDraft(DRAFT_KEY); setDraftState("idle"); } }, [sentQuoteId]);
  function resumeDraft() {
    const d: any = resume; setResume(null);
    if (!d) return;
    try {
      restore(d.data.snapshot);
      if (Array.isArray(d.data.fences) && d.data.fences.length) setFences(d.data.fences);
      notify("Picked up where you left off");
    } catch (e) { notify("That draft couldn't be reopened", { kind: "error" }); }
  }
  function discardDraft() { clearDraft(DRAFT_KEY); setResume(null); }
  lfRef.current = lf;
  const activeName = "Fence " + (fences.length + 1);
  const allFenceSnaps = () => [...fences.map(f => ({ name: f.name, snapshot: f.snapshot })), ...(lf >= 1 ? [{ name: activeName, snapshot: snapshot() }] : [])];

  function flash(m) { setToast(m); clearTimeout(refs.current.t); refs.current.t = setTimeout(() => setToast(null), 2800); }

  /* ---- init map (Leaflet loads lazily — only this screen pays for it) ---- */
  React.useEffect(() => {
    let dead = false;
    let m: any = null;
    let sizeTimer: any = null;
    ensureLeaflet().then((L) => {
      if (dead || !mapEl.current) return;
      // raise drag threshold so small click-jitter never starts a drag on any handle (fixes "fence walks on click")
      if (L.Draggable && L.Draggable.prototype && L.Draggable.prototype.options) L.Draggable.prototype.options.clickTolerance = 10;
      m = L.map(mapEl.current, { zoomControl: false, attributionControl: false }).setView([44.5213, -88.0241], 19);
      // Esri World Imagery has true detail to z20 across the US (NAIP-backed);
      // capping native at 19 made the auto-fit view (z20) upscale & look soft.
      // maxNativeZoom 20 keeps it crisp at the zoom we fit parcels to.
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 21, maxNativeZoom: 20 }).addTo(m);
      L.control.zoom({ position: "topright" }).addTo(m);
      m.on("click", (e) => {
        if (draggingRef.current) return;   // ignore the click that ends a handle drag
        if (refs.current.manual) { setMVerts(v => [...v, [e.latlng.lat, e.latlng.lng]]); return; }
        if (gradeModeRef.current) return;  // grade mode: clicks are for dropping points on runs
        if (refs.current.mapLocked) return;   // locked: don't replace the parcel on a stray click
        loadParcel(e.latlng.lat, e.latlng.lng);   // click any property
      });
      map.current = m;
      // trackpad two-finger drag → pan the map (macOS fires wheel events for it).
      // Pinch (ctrl+wheel) and a real mouse wheel still zoom. Capture-phase +
      // stopPropagation so Leaflet's scroll-zoom doesn't also fire; works even when
      // the map is locked, so you can reposition without unlocking.
      mapEl.current.addEventListener("wheel", (e) => {
        if (e.ctrlKey) return;                                                       // pinch → let Leaflet zoom
        const pan = Math.abs(e.deltaX) > 0.5 || (e.deltaMode === 0 && Math.abs(e.deltaY) < 50);
        if (!pan) return;                                                            // mouse wheel notch → let it zoom
        e.preventDefault(); e.stopPropagation();
        m.panBy([e.deltaX, e.deltaY], { animate: false });
      }, { passive: false, capture: true });
      // suppress the browser's right-click menu over the map so our own corner/side menus show
      mapEl.current.addEventListener("contextmenu", (e) => e.preventDefault());
      sizeTimer = setTimeout(() => { if (!dead) try { m.invalidateSize(); } catch { /* unmounted */ } }, 60);
      if (params && params.loadProject) {
        const proj = store.projects.find(p => p.id === params.loadProject);
        if (proj) { setEditingProject({ id: proj.id, job: proj.job }); if (proj.snapshot) restore(proj.snapshot); }
      } else if (params && params.restoreSnapshot) {
        restore(params.restoreSnapshot);
      } else if (params && params.prefillCustomer) {
        const cust = (store.customers || []).find(x => x.id === params.prefillCustomer);
        if (cust) setCustomer(seedCustomer(cust));
      }
    });
    return () => { dead = true; if (sizeTimer) clearTimeout(sizeTimer); if (m) destroyMap(m); if (map.current === m) map.current = null; };
  }, []);

  /* keep editingProject bound to the opened project even if the project list
     hydrates from the cloud AFTER this component mounted (prevents save dupes) */
  React.useEffect(() => {
    const want = params && params.loadProject;
    if (!want) return;
    const proj = store.projects.find(p => p.id === want);
    if (proj && (!editingProject || editingProject.id !== proj.id || !editingProject.job)) {
      setEditingProject({ id: proj.id, job: proj.job, createdAt: proj.createdAt, status: proj.status });
    }
  }, [store.projects, params && params.loadProject]);

  /* reopening a saved estimate while already ON the estimator (from the
     customer's past-estimates list): the mount effect only restores once, so
     re-restore the snapshot when loadProject CHANGES after mount. */
  const loadedRef = React.useRef(params && params.loadProject);
  React.useEffect(() => {
    const want = params && params.loadProject;
    if (!want || want === loadedRef.current) return;   // initial load handled at mount
    const proj = store.projects.find(p => p.id === want);
    if (proj && proj.snapshot) { loadedRef.current = want; restore(proj.snapshot); }
  }, [params && params.loadProject]);

  /* this customer's saved estimates — projects (re-openable drafts) merged with
     their quotes (viewable), newest first; shown once an existing customer is picked. */
  const pastEstimates = React.useMemo(() => {
    const cid = customer.customerId;
    if (!cid) return [];
    const rows = (store.projects || []).filter(p => p.customerId === cid).map(p => ({
      key: "p" + p.id, projectId: p.id, quoteId: null as any, label: p.productName || p.job || "Estimate",
      total: p.total || 0, at: p.createdAt || 0, status: p.status || "draft",
    }));
    const byProj: any = {}; rows.forEach(r => { byProj[r.projectId] = r; });
    (store.quotes || []).filter(q => q.customerId === cid).forEach(q => {
      const r = q.projectId && byProj[q.projectId];
      if (r) { r.quoteId = q.id; r.status = q.status || r.status; r.total = q.total || r.total; }
      else rows.push({ key: "q" + q.id, projectId: null, quoteId: q.id, label: q.productName || "Estimate", total: q.total || 0, at: q.createdAt || 0, status: q.status || "pending" });
    });
    return rows.sort((a, b) => (b.at || 0) - (a.at || 0));
  }, [customer.customerId, store.projects, store.quotes]);

  async function loadParcel(lat, lng, addr?, pre?) {
    setLoading(true); setManual(false); setMVerts([]); setSetbackStr("");
    // `pre` = an already-resolved parcel (the sandbox's seeded sample yard) — skips the live lookup
    const p = pre || await window.VFP_PARCEL.resolve(lat, lng, addr);
    // sandbox: the parcel is public record, but a public demo shouldn't put a
    // homeowner's name on screen for every visitor who types an address
    try { const d: any = window.VFP_DEMO; if (d && d.active && p && p.owner) p.owner = "Owner on record"; } catch (e) {}
    // keep the TRUE GIS ring as a reference, but lay the fence out from a CLEANED
    // version — collapsing digitizing jitter so an irregular line doesn't spawn a
    // post at every survey point. rawPolygon drives the "Property line" overlay
    // and the Reset/Setback tools (they always derive from the real boundary).
    const raw = (p.polygon || []).map(v => v.slice());
    const cleaned = simplifyRing(raw, SIMPLIFY_TOL_FT);
    const dropped = raw.length - cleaned.length;
    const sides = buildSides(cleaned, null);
    setParcel({ ...p, rawPolygon: raw, polygon: cleaned, sides, closed: true });
    detectHouseFor(raw);   // best-effort house footprint (OSM → imagery CV); merges onto the parcel when it resolves
    // prefill from the parcel — owner → customer name, address → address — but
    // only when blank and NOT an already-picked existing customer.
    // Only a LIVE parcel record may prefill the customer: a cached/synth
    // fallback carries no real owner (and its address is just what was typed).
    const ownerName = (p.live && p.owner && p.owner !== "—") ? titleCaseName(p.owner) : "";
    const parcelAddr = p.address || addr || "";
    if (p.live) setCustomer(c => {
      if (c.customerId) return c;                                  // a picked existing customer — never overwrite
      // Refresh the owner/address prefill when a NEW parcel loads. Touch only data
      // that's blank OR was itself auto-filled from a parcel (tracked by
      // _fromParcel) — so a name/address the contractor typed by hand is kept.
      const auto = c._fromParcel === true || (!c.name && !c.phone && !c.email && !c.address);
      if (!auto) return c;
      return { ...c, _fromParcel: true, name: ownerName, address: parcelAddr, street: parcelAddr, city: "", state: "", zip: "" };
    });
    // drop the parcel reference into the notes for the contractor's record
    if (p.live && ((p.parcelId && p.parcelId !== "—") || ownerName)) {
      const ref = "Parcel " + (p.parcelId && p.parcelId !== "—" ? p.parcelId : "—")
        + (ownerName ? " · Owner: " + ownerName : "")
        + (p.county ? " · " + p.county + " County" : "");
      setQuoteNotes(n => (n && n.trim()) ? n : ref);
    }
    if (map.current) { try { map.current.fitBounds(cleaned, { padding: [90, 90], maxZoom: 20 }); } catch { /* the map went away mid-load */ } }
    setLoading(false);
    setMapLocked(true);   // auto-lock after outlining so editing handles is safe
    if (p.live) flash("Parcel loaded · " + (dropped > 2 ? `cleaned ${raw.length} survey points → ${cleaned.length} corners` : (p.parcelId && p.parcelId !== "—" ? p.parcelId : p.provider)));
    else if (p.noCoverage) flash("No parcel record here — switch provider in Settings or draw manually");
    else if (p.error) flash("Parcel service unreachable — outlined an estimate; drag handles or draw manually");
    else flash("Parcel outlined · adjust handles to fit");
  }

  // geocode a typed address → outline its parcel (also the sandbox tour's
  // "use the sample yard" / prefilled-address entry point)
  async function searchAddress(text) {
    const q = String(text || "").trim();
    if (!q) return false;
    setSearch(q); setShowSugg(false); setLoading(true);
    try {
      const g = await window.VFP_PARCEL.geocode(q);
      if (g) {
        saveRecent({ line1: g.address || q, line2: "", lat: g.lat, lng: g.lng, address: g.address || q });
        await loadParcel(g.lat, g.lng, g.address || q);
        try { const d: any = window.VFP_DEMO; if (d && d.active) d.track("address_entered", { via: "search" }); } catch (e) {}
        return true;
      }
      flash("No address match — click the property on the map instead"); setLoading(false);
    } catch (err) {
      flash("Address lookup unavailable — click the property on the map"); setLoading(false);
    }
    return false;
  }
  async function doSearch(e) {
    e && e.preventDefault();
    if (!search.trim()) return;
    setShowSugg(false);
    if (sugg.length) { pickSugg(sugg[suggIdx >= 0 ? suggIdx : 0]); return; }
    await searchAddress(search);
  }
  // sandbox: expose the search + live footage to the tour, and take the
  // address the marketing site's hero box handed over
  React.useEffect(() => {
    const d: any = window.VFP_DEMO;
    if (!(d && d.active)) return;
    d.estimator = {
      search: searchAddress, lf: () => lfRef.current, hasParcel: () => !!parcel,
      // what the walkthrough coaches: the house outline, H, hold-for-actions, a gate
      house: () => { const p = refs.current.parcel; return !!(p && Array.isArray(p.house) && p.house.length >= 3); },
      houseFitAt: () => tourMarks.current.houseFitAt || 0,
      sideMenuAt: () => tourMarks.current.sideMenuAt || 0,
      gates: () => { const p = refs.current.parcel; return p && Array.isArray(p.gates) ? p.gates.length : 0; },
      fitToHouse: () => fitToHouse(),
      addGate: () => { const p = refs.current.parcel; const sides = (p && p.sides) || []; const s = sides.find((x: any) => x.selected) || sides[0]; if (s) addGate(s.i, 0.5); },
      showKeys: () => setShowKeys(true),
      gradeMode: (on: boolean) => setGradeMode(!!on),
      // the seeded sample yard: a residential lot drawn from the fixture, no geocoder, no parcel service
      loadSample: async (yard: any) => {
        setSearch(yard.address || "Sample yard");
        const lot = yard.lot && yard.lot.parcel;
        if (!lot) return searchAddress(yard.address);
        await loadParcel(yard.lat, yard.lng, yard.address, Object.assign({}, lot, { address: yard.address, live: false, provider: "Sample yard" }));
        return true;
      },
    };
    const pre = d.prefill && d.prefill.address;
    if (pre && !(params && params.loadProject)) { d.clearPrefill(); setTimeout(() => searchAddress(pre), 400); }
    return () => { if (d.estimator && d.estimator.lf === undefined) return; d.estimator = null; };
  }, []);

  function onSearchChange(v) {
    setSearch(v); setSuggIdx(-1);
    clearTimeout(suggTimer.current);
    if (v.trim().length < 3) {   // too short to geocode → offer recent searches instead
      const r = loadRecents();
      setSugg(r.map((x) => ({ ...x, _recent: true }))); setShowSugg(r.length > 0);
      return;
    }
    suggTimer.current = setTimeout(async () => {
      const seq = ++suggSeq.current;
      try {
        const rows = dedupeSugg(await window.VFP_PARCEL.suggest(v));
        if (seq === suggSeq.current) { setSugg(rows); setShowSugg(rows.length > 0); }
      } catch { /* ignore */ }
    }, 280);
  }
  async function pickSugg(s) {
    if (!s) return;
    setSearch(s.line1 + (s.line2 ? ", " + s.line2 : ""));
    setShowSugg(false); setSugg([]);
    saveRecent(s);
    await loadParcel(s.lat, s.lng, s.address);
    try { const d: any = window.VFP_DEMO; if (d && d.active) d.track("address_entered", { via: "suggest" }); } catch (e) {}
  }
  function onSearchKey(e) {
    if (!showSugg || !sugg.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSuggIdx(i => Math.min(sugg.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSuggIdx(i => Math.max(0, i - 1)); }
    else if (e.key === "Escape") { setShowSugg(false); }
  }

  function toggleSide(i) {
    setParcel(p => ({ ...p, sides: p.sides.map(s => s.i === i ? { ...s, selected: !s.selected } : s) }));
  }
  function setAll(v) { setParcel(p => ({ ...p, sides: p.sides.map(s => ({ ...s, selected: v })) })); }
  function setGrade(i, g) { setParcel(p => ({ ...p, sides: p.sides.map(s => s.i === i ? { ...s, grade: g, gradeAuto: false } : s) })); }
  const prevMap = (sides) => { const m = {}; sides.forEach(s => m[s.i] = { selected: s.selected, grade: s.grade, gradeAuto: s.gradeAuto, autoSlope: s.autoSlope, profile: s.profile }); return m; };
  const sideKey = (s) => s.a[0].toFixed(5) + "," + s.a[1].toFixed(5) + "|" + s.b[0].toFixed(5) + "," + s.b[1].toFixed(5);
  const slopePending = React.useRef<any>({});

  // split a run at an arbitrary fraction (keeps gates mapped onto the correct sub-side)
  function splitSideAt(i, frac, msg) {
    setParcel(p => {
      const cl = p.closed !== false;
      const poly = p.polygon.slice(), n = poly.length;
      const a = poly[i], b = poly[(i + 1) % n];
      const f = Math.max(0.04, Math.min(0.96, frac));
      poly.splice(i + 1, 0, [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f]);
      const prev = {};
      p.sides.forEach(s => { if (s.i < i) prev[s.i] = pOf(s); else if (s.i === i) { const [pa, pb] = splitProfile(s.profile, f); prev[i] = { ...pOf(s), profile: pa }; prev[i + 1] = { ...pOf(s), profile: pb }; } else prev[s.i + 1] = pOf(s); });
      const gates = (p.gates || []).map(g => {
        if (g.side < i) return g;
        if (g.side > i) return { ...g, side: g.side + 1 };
        return g.t <= f ? { ...g, t: g.t / f } : { ...g, side: i + 1, t: (g.t - f) / (1 - f) };
      });
      return { ...p, polygon: poly, sides: buildSides(poly, prev, cl), gates, cornerElev: ceInsertAt(p.cornerElev, i + 1) };
    });
    flash(msg || "Point added");
  }
  // drop a point just past a gate so the leftover stub (e.g. through the house) is its own deletable wall
  function splitPastGate(g) {
    const poly = parcel.polygon, n = poly.length, a = poly[g.side], b = poly[(g.side + 1) % n];
    const lenFt = segFeet(a, b) || 1;
    splitSideAt(g.side, g.t + (g.width / 2) / lenFt + 0.012, "Point dropped past the gate — delete the stub if it runs through the house");
  }
  // split a run at its midpoint -> two runs (A / B)
  function splitSide(i) {
    setParcel(p => {
      const cl = p.closed !== false;
      const poly = p.polygon.slice(), n = poly.length;
      const a = poly[i], b = poly[(i + 1) % n];
      poly.splice(i + 1, 0, [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]);
      const prev = {};
      p.sides.forEach(s => {
        if (s.i < i) prev[s.i] = pOf(s);
        else if (s.i === i) { const [pa, pb] = splitProfile(s.profile, 0.5); prev[i] = { ...pOf(s), profile: pa }; prev[i + 1] = { ...pOf(s), profile: pb }; }
        else prev[s.i + 1] = pOf(s);
      });
      return { ...p, polygon: poly, sides: buildSides(poly, prev, cl), cornerElev: ceInsertAt(p.cornerElev, i + 1) };
    });
    flash("Run split — drag the new handle");
  }
  // merge a run with the next one (removes the shared corner)
  function mergeSide(i) {
    setParcel(p => {
      const cl = p.closed !== false;
      const n = p.polygon.length; if (n <= 3) { flash("Can't merge below 3 sides"); return p; }
      const removeIdx = (i + 1) % n; if (removeIdx === 0) { flash("Pick a different side to merge"); return p; }
      const poly = p.polygon.slice(); poly.splice(removeIdx, 1);
      const prev = {};
      p.sides.forEach(s => {
        if (s.i === i) prev[i] = pOf(s);
        else if (s.i === removeIdx) { /* merged away */ }
        else prev[s.i > removeIdx ? s.i - 1 : s.i] = pOf(s);
      });
      // stitch the two runs' measured profiles into the merged run (t rescaled by footage)
      const sA = p.sides[i], sB = p.sides[removeIdx];
      if (prev[i] && ((sA && sA.profile) || (sB && sB.profile))) {
        const lfA = (sA && sA.lf) || 0, lfB = (sB && sB.lf) || 0, fM = (lfA + lfB) > 0 ? lfA / (lfA + lfB) : 0.5;
        const merged = [
          ...(((sA && sA.profile) || []).map(pt => ({ t: pt.t * fM, deltaIn: pt.deltaIn }))),
          ...(((sB && sB.profile) || []).map(pt => ({ t: fM + pt.t * (1 - fM), deltaIn: pt.deltaIn }))),
        ];
        prev[i] = { ...prev[i], profile: merged.length ? merged : null };
      }
      return { ...p, polygon: poly, sides: buildSides(poly, prev, cl), cornerElev: ceRemoveAt(p.cornerElev, removeIdx) };
    });
    flash("Runs merged");
  }
  // type an exact length -> moves the end corner along the run direction
  function setSideLength(i, ft) {
    const want = parseFloat(ft); if (!(want > 0)) return;
    setParcel(p => {
      const cl = p.closed !== false;
      const poly = p.polygon.slice(), n = poly.length;
      const a = poly[i], b = poly[(i + 1) % n];
      const cur = segFeet(a, b); if (cur < 0.1) return p;
      const sc = want / cur;
      poly[(i + 1) % n] = [a[0] + (b[0] - a[0]) * sc, a[1] + (b[1] - a[1]) * sc];
      return { ...p, polygon: poly, sides: buildSides(poly, prevMap(p.sides), cl) };
    });
  }
  // is this run roughly collinear with the next? (i.e. a split pair we can merge)
  function isSplitPair(p, i) {
    const n = p.polygon.length; if (n <= 3) return false;
    if (p.closed === false && i >= p.sides.length - 1) return false;
    const a = p.polygon[i], b = p.polygon[(i + 1) % n], cc = p.polygon[(i + 2) % n];
    const a1 = Math.atan2(b[0] - a[0], b[1] - a[1]), a2 = Math.atan2(cc[0] - b[0], cc[1] - b[1]);
    let d = Math.abs(a1 - a2); if (d > Math.PI) d = 2 * Math.PI - d;
    return d < 0.35; // ~20°
  }

  // imperative live drag (no React re-render) so the gesture isn't interrupted
  function liveMove(idx, ll) {
    const lc = lay.current; const poly = lc.poly; if (!poly) return;
    let lat = ll.lat, lng = ll.lng;
    // Shift = snap movement to nearest 45° (horizontal, vertical, or clean diagonal)
    if (shiftRef.current && lc.dragOrigin) {
      const o = lc.dragOrigin.orig;
      const k = Math.cos(o[0] * Math.PI / 180);
      let dx = (lng - o[1]) * k, dy = lat - o[0];
      const dist = Math.hypot(dx, dy);
      const snap = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
      lng = o[1] + (Math.cos(snap) * dist) / k;
      lat = o[0] + Math.sin(snap) * dist;
    }
    poly[idx] = [lat, lng];
    if (lc.handles[idx]) lc.handles[idx].setLatLng([lat, lng]);
    if (lc.fill) lc.fill.setLatLngs(lc.closed ? poly : poly);
    const n = poly.length;
    [(idx - 1 + n) % n, idx].forEach(si => {
      const a = poly[si], b = poly[(si + 1) % n];
      if (lc.sidePolys[si]) lc.sidePolys[si].setLatLngs([a, b]);
      if (lc.sideHit[si]) lc.sideHit[si].setLatLngs([a, b]);
      const lbl = lc.sideLabels[si];
      if (lbl) {
        lbl.setLatLng([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]);
        const el = lbl.getElement && lbl.getElement();
        const d = el && el.querySelector(".seg-label");
        if (d) d.textContent = Math.round(segFeet(a, b)) + " ft";
      }
    });
  }
  function commitVertex(idx, ll) {
    setParcel(p => {
      const poly = p.polygon.map((q, k) => k === idx ? [ll.lat, ll.lng] : q);
      return { ...p, polygon: poly, sides: buildSides(poly, prevMap(p.sides), p.closed !== false) };
    });
  }
  // Shift+drag a whole wall: translate the edge, neighbors stretch to follow
  function liveEdgeMove(i, dLat, dLng) {
    const lc = lay.current; const poly = lc.poly; if (!poly) return;
    const n = poly.length, j = (i + 1) % n, o = lc.edgeOrigin; if (!o) return;
    poly[i] = [o.a[0] + dLat, o.a[1] + dLng];
    poly[j] = [o.b[0] + dLat, o.b[1] + dLng];
    if (lc.handles[i]) lc.handles[i].setLatLng(poly[i]);
    if (lc.handles[j]) lc.handles[j].setLatLng(poly[j]);
    if (lc.fill) lc.fill.setLatLngs(poly);
    [(i - 1 + n) % n, i, j].forEach(si => {
      const a = poly[si], b = poly[(si + 1) % n];
      if (lc.sidePolys[si]) lc.sidePolys[si].setLatLngs([a, b]);
      if (lc.sideHit[si]) lc.sideHit[si].setLatLngs([a, b]);
      const lbl = lc.sideLabels[si];
      if (lbl) { lbl.setLatLng([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]); const el = lbl.getElement && lbl.getElement(); const d = el && el.querySelector(".seg-label"); if (d) d.textContent = Math.round(segFeet(a, b)) + " ft"; }
    });
  }
  function commitEdge(i) {
    const lc = lay.current, poly = lc.poly; if (!poly) return;
    const a = poly[i].slice(), b = poly[(i + 1) % poly.length].slice();
    setParcel(p => {
      const np = p.polygon.map(v => v.slice()); np[i] = a; np[(i + 1) % np.length] = b;
      return { ...p, polygon: np, sides: buildSides(np, prevMap(p.sides), p.closed !== false) };
    });
  }
  // live-translate the WHOLE fence (move handle) — moves every layer, commits on release
  function liveTranslateAll(dLat, dLng) {
    const lc = lay.current, o = lc.moveOrigin; if (!o || !lc.poly) return;
    const poly = lc.poly, n = poly.length;
    for (let k = 0; k < n; k++) { poly[k] = [o.poly[k][0] + dLat, o.poly[k][1] + dLng]; if (lc.handles[k]) lc.handles[k].setLatLng(poly[k]); }
    if (lc.fill) lc.fill.setLatLngs(poly);
    Object.keys(lc.sidePolys).forEach(key => {
      const si = +key, a = poly[si], b = poly[(si + 1) % n];
      if (lc.sidePolys[si]) lc.sidePolys[si].setLatLngs([a, b]);
      if (lc.sideHit[si]) lc.sideHit[si].setLatLngs([a, b]);
      const lbl = lc.sideLabels[si]; if (lbl) lbl.setLatLng([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]);
    });
    (o.gates || []).forEach(g => { const gm = lc.gateMarks && lc.gateMarks[g.id]; if (gm) gm.setLatLng([g.pos[0] + dLat, g.pos[1] + dLng]); });
    if (lc.moveHandle) lc.moveHandle.setLatLng([o.center[0] + dLat, o.center[1] + dLng]);
  }
  function commitTranslate(dLat, dLng) {
    setParcel(p => {
      if (!p) return p;
      const np = p.polygon.map(v => [v[0] + dLat, v[1] + dLng]);
      return { ...p, polygon: np, sides: buildSides(np, prevMap(p.sides), p.closed !== false) };
    });
  }

  /* ---- corner actions (right-click / long-press menu) ---- */
  function openCornerMenu(idx, oe) {
    const t = oe && oe.touches ? oe.touches[0] : oe;
    const P = parcel && parcel.polygon, n = P ? P.length : 0;
    const angle = (P && n >= 3) ? cornerAngle(P[(idx - 1 + n) % n], P[idx], P[(idx + 1) % n]) : null;
    setCornerMenu({ idx, x: t ? t.clientX : window.innerWidth / 2, y: t ? t.clientY : window.innerHeight / 2, angle });
  }
  function squareCornerAt(idx) {
    setParcel(p => {
      const n = p.polygon.length; if (n < 3) return p;
      const B = p.polygon[(idx - 1 + n) % n], C = p.polygon[idx], D = p.polygon[(idx + 1) % n];
      const nc = squareCorner(B, C, D);
      const poly = p.polygon.map((q, k) => k === idx ? nc : q);
      return { ...p, polygon: poly, sides: buildSides(poly, prevMap(p.sides), p.closed !== false) };
    });
    flash("Corner squared to 90°");
  }
  /* E / corner menu: EXTEND an open run past its end — one more section, ready
     to drag. The new corner continues the end segment's direction one panel
     (~8 ft) out and becomes the selected corner, so E-drag-E-drag walks the
     fence wherever it needs to go (dog runs, returns along the house, …).
     On a closed shape or a mid-run corner it falls back to inserting a point. */
  function extendFrom(idx) {
    const par = refs.current.parcel;
    if (!par) return;
    const poly = par.polygon || [];
    const n = poly.length;
    if (par.closed !== false || n < 2 || (idx !== 0 && idx !== n - 1)) { insertCornerAfter(Math.max(0, Math.min(idx, n - 1))); return; }
    const fromStart = idx === 0;
    const o = poly[fromStart ? 0 : n - 1];
    const A = toXY(poly[fromStart ? 1 : n - 2], o);   // the end's neighbour, in metres around the end
    const L2 = Math.hypot(A[0], A[1]) || 1;
    const dir = [-A[0] / L2, -A[1] / L2];             // straight on, away from the run
    const EXT = 2.44;                                  // one ~8 ft panel
    const np = toLL([dir[0] * EXT, dir[1] * EXT], o);
    setParcel(p => {
      const out = fromStart ? [np, ...p.polygon.map(v => v.slice())] : [...p.polygon.map(v => v.slice()), np];
      const prev = {}; p.sides.forEach(s => { prev[fromStart ? s.i + 1 : s.i] = pOf(s); });
      const gates = fromStart ? (p.gates || []).map(g => ({ ...g, side: g.side + 1 })) : (p.gates || []);
      return { ...p, polygon: out, gates, sides: buildSides(out, prev, false), cornerElev: fromStart ? ceInsertAt(p.cornerElev, 0) : p.cornerElev };
    });
    setActiveCorner(fromStart ? 0 : n);
    flash("Run extended — drag the new corner into place · E adds another");
  }
  function insertCornerAfter(idx) {
    // open chain's last corner has nothing "after" it — that's an extend, not a
    // wrap-around midpoint to vertex 0 (which drew a line across the yard)
    const par0 = refs.current.parcel;
    if (par0 && par0.closed === false && idx >= (par0.polygon || []).length - 1) { extendFrom((par0.polygon || []).length - 1); return; }
    setParcel(p => {
      const n = p.polygon.length, a = p.polygon[idx], b = p.polygon[(idx + 1) % n];
      const poly = p.polygon.slice(); poly.splice(idx + 1, 0, [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]);
      const prev = {}; p.sides.forEach(s => { if (s.i <= idx) prev[s.i] = pOf(s); else prev[s.i + 1] = pOf(s); });
      if (p.sides[idx]) { const [pa, pb] = splitProfile(p.sides[idx].profile, 0.5); prev[idx] = { ...prev[idx], profile: pa }; prev[idx + 1] = { ...pOf(p.sides[idx]), profile: pb }; }
      const gates = (p.gates || []).map(g => g.side > idx ? { ...g, side: g.side + 1 } : g);   // gates past the split keep their wall
      return { ...p, polygon: poly, gates, sides: buildSides(poly, prev, p.closed !== false), cornerElev: ceInsertAt(p.cornerElev, idx + 1) };
    });
    flash("Point added — drag to place");
  }
  function insertCornerAt(idx, pos) {
    setParcel(p => {
      const poly = p.polygon.slice(); poly.splice(idx + 1, 0, pos);
      const prev = {}; p.sides.forEach(s => { if (s.i <= idx) prev[s.i] = pOf(s); else prev[s.i + 1] = pOf(s); });
      if (p.sides[idx]) {
        const n = p.polygon.length, a = p.polygon[idx], b = p.polygon[(idx + 1) % n];
        const B = toXY(b, a), C = toXY(pos, a), len2 = (B[0] * B[0] + B[1] * B[1]) || 1;
        const f = Math.max(0.02, Math.min(0.98, (C[0] * B[0] + C[1] * B[1]) / len2));
        const [pa, pb] = splitProfile(p.sides[idx].profile, f);
        prev[idx] = { ...prev[idx], profile: pa }; prev[idx + 1] = { ...pOf(p.sides[idx]), profile: pb };
      }
      const gates = (p.gates || []).map(g => g.side > idx ? { ...g, side: g.side + 1 } : g);   // gates past the split keep their wall
      return { ...p, polygon: poly, gates, sides: buildSides(poly, prev, p.closed !== false), cornerElev: ceInsertAt(p.cornerElev, idx + 1) };
    });
    flash("New line added");
  }
  function deleteCorner(idx) {
    setParcel(p => {
      const n = p.polygon.length; if (n <= 3) { flash("A fence needs at least 3 corners"); return p; }
      const poly = p.polygon.slice(); poly.splice(idx, 1);
      const prev = {}; p.sides.forEach(s => { if (s.i < idx) prev[s.i] = pOf(s); else if (s.i > idx) prev[s.i - 1] = pOf(s); });
      const gates = (p.gates || []).filter(g => g.side !== idx && g.side !== (idx - 1 + n) % n).map(g => ({ ...g, side: g.side > idx ? g.side - 1 : g.side }));
      return { ...p, polygon: poly, sides: buildSides(poly, prev, p.closed !== false), gates, cornerElev: ceRemoveAt(p.cornerElev, idx) };
    });
    flash("Corner removed");
  }
  // delete a SIDE (wall). A fence is open, so deleting a wall opens the loop instead of re-closing.
  function deleteSide(i) {
    setParcel(p => {
      const cl = p.closed !== false;
      const n = p.polygon.length;
      if (cl) {
        if (n < 3) { flash("Need at least one wall"); return p; }
        // open the loop: rotate so the chain starts right after the deleted wall
        const poly = p.polygon.slice(i + 1).concat(p.polygon.slice(0, i + 1));
        const prev = {}; p.sides.forEach(s => { if (s.i === i) return; prev[(s.i - (i + 1) + n) % n] = pOf(s); });
        const gates = (p.gates || []).filter(g => g.side !== i).map(g => ({ ...g, side: (g.side - (i + 1) + n) % n }));
        flash("Wall removed — fence is now open");
        return { ...p, closed: false, polygon: poly, sides: buildSides(poly, prev, false), gates, cornerElev: remapCornerElev(p.cornerElev, k => (k - (i + 1) + n) % n) };
      }
      // already open
      const sideCount = p.sides.length;
      if (sideCount <= 1) { flash("Need at least one wall"); return p; }
      let poly, mapIdx, dropVtx;
      if (i === 0) { poly = p.polygon.slice(1); mapIdx = (k) => k - 1; dropVtx = 0; }
      else if (i === sideCount - 1) { poly = p.polygon.slice(0, p.polygon.length - 1); mapIdx = (k) => (k === i ? -1 : k); dropVtx = p.polygon.length - 1; }
      else { poly = p.polygon.slice(); poly.splice(i + 1, 1); mapIdx = (k) => (k === i + 1 ? -1 : (k > i + 1 ? k - 1 : k)); dropVtx = i + 1; }
      const prev = {}; p.sides.forEach(s => { if (s.i === i) return; const ni = mapIdx(s.i); if (ni >= 0) prev[ni] = pOf(s); });
      const gates = (p.gates || []).filter(g => g.side !== i).map(g => { const ns = mapIdx(g.side); return ns >= 0 ? { ...g, side: ns } : null; }).filter(Boolean);
      flash("Wall removed");
      return { ...p, polygon: poly, sides: buildSides(poly, prev, false), gates, cornerElev: ceRemoveAt(p.cornerElev, dropVtx) };
    });
    setActiveSide(null);
  }
  function openSideMenu(i, oe, latlng?) {
    tourMarks.current.sideMenuAt = Date.now();   // the walkthrough's "hold a wall for actions" check
    const t = oe && oe.touches ? oe.touches[0] : oe;
    setActiveSide(i);
    let frac = null, pos = null;
    if (latlng) { frac = gateTOnSide(i, latlng); const poly = parcel.polygon, n = poly.length, a = poly[i], b = poly[(i + 1) % n]; pos = [a[0] + (b[0] - a[0]) * frac, a[1] + (b[1] - a[1]) * frac]; }
    setSideMenu({ i, x: t ? t.clientX : window.innerWidth / 2, y: t ? t.clientY : window.innerHeight / 2, t: frac, pos });
  }
  function squareAllCorners() {
    setParcel(p => { const cl = p.closed !== false; const poly = squareCornersPoly(p.polygon, cl); return { ...p, polygon: poly, sides: buildSides(poly, prevMap(p.sides), cl) }; });
    flash("Squared near-90° corners · short runs kept");
  }
  /* ---- house footprint: auto-detect (OSM → imagery CV) + one-click backyard ---- */
  function detectHouseFor(poly) {
    if (!poly || !window.VFP_BUILDINGS) return;
    const tok = ++houseTok.current;        // a newer parcel/load supersedes an in-flight detect
    setHouseBusy(true);
    window.VFP_BUILDINGS.detectHouse(poly).then((res) => {
      if (tok !== houseTok.current) return;
      if (res && res.polygon) {
        hist.current.applying = true;   // a detected house is context, not an undo step (don't pollute history)
        setParcel(p => p ? { ...p, house: res.polygon, houseSource: res.source } : p);
        flash(res.source === "cv" ? "House estimated from imagery — drag to fit" : res.source === "usa" ? "House detected (USA Structures)" : "House detected (OpenStreetMap)");
      }
    }).catch(() => {}).finally(() => { if (tok === houseTok.current) setHouseBusy(false); });
  }
  function backyardLayout() {
    const par = refs.current.parcel;   // live state — callable from the mount-time key handler (H)
    if (!par || !par.house) { flash("Detect the house first"); return; }
    const lot = simplifyRing(par.rawPolygon || par.polygon, SIMPLIFY_TOL_FT);
    const fence = window.VFP_BUILDINGS && window.VFP_BUILDINGS.backyardFence(lot, par.house, 0.5);
    if (!fence || fence.length < 3) { flash("Couldn't fit a backyard here — adjust the house, or use the lot tools"); return; }
    setParcel(p => ({ ...p, polygon: fence, closed: false, gates: [], cornerElev: null, sides: buildSides(fence, null, false) }));
    setActiveSide(null);
    flash("Backyard laid out — ties into the house halfway up. Drag corners to fine-tune.");
  }
  // H = fit the CURRENT fence to the house: move ONLY the two corners that tie
  // into the house — onto its side walls, halfway up the house — and leave every
  // other corner (your cleaned-up back line included) exactly where you put it.
  // Open chain → its two ends tie in; closed loop → the wall nearest the house
  // gets its endpoints tied. The Backyard button still re-lays the whole shape.
  function fitToHouse() {
    const par = refs.current.parcel;
    if (!par || refs.current.manual) return;
    if (!Array.isArray(par.house) || par.house.length < 3) { flash("Detecting the house — press H again when the amber outline appears"); detectHouseFor(par.rawPolygon || par.polygon); return; }
    const poly = par.polygon;
    if (!Array.isArray(poly) || poly.length < 2) return;
    tourMarks.current.houseFitAt = Date.now();   // the sandbox walkthrough watches for the H key
    const o = centroid(par.house);
    const H = par.house.map(v => toXY(v, o));
    const P = poly.map(v => toXY(v, o));
    const cl = par.closed !== false;
    const n = P.length;
    // RAW yard hint: from the house toward the fence's bulk; if the fence wraps
    // the house evenly, "away from the street" (the house's offset from the lot
    // centre points at the road). Good enough to pick the street-side wall —
    // but NOT for the tie geometry: when the house sits off-centre this hint
    // tilts sideways, the mid-depth cut runs diagonal, and the ties land on the
    // wrong house walls. So: pick the reference wall with the hint, then take
    // the TRUE yard direction perpendicular to that wall.
    let dRaw;
    const fc = toXY(centroid(poly), o);
    const flen = Math.hypot(fc[0], fc[1]);
    if (flen > 3) dRaw = [fc[0] / flen, fc[1] / flen];
    else {
      const hc = toXY(centroid(par.house), centroid(par.rawPolygon || poly));
      const l2 = Math.hypot(hc[0], hc[1]);
      if (l2 < 1) { flash("Couldn't work out the yard direction — drag the front corners onto the house instead"); return; }
      dRaw = [-hc[0] / l2, -hc[1] / l2];
    }
    // reference wall: closed → the most street-ward wall (lowest mean
    // projection on the hint); open → the line between the chain's two ends
    let iF = 0;
    let axis;   // runs ALONG the street
    if (cl) {
      if (n < 3) return;
      let fb = Infinity;
      for (let i = 0; i < n; i++) {
        const a = P[i], b = P[(i + 1) % n];
        const depth = (a[0] * dRaw[0] + a[1] * dRaw[1] + b[0] * dRaw[0] + b[1] * dRaw[1]) / 2;
        if (depth < fb) { fb = depth; iF = i; }
      }
      axis = [P[(iF + 1) % n][0] - P[iF][0], P[(iF + 1) % n][1] - P[iF][1]];
    } else {
      axis = [P[n - 1][0] - P[0][0], P[n - 1][1] - P[0][1]];
    }
    const alen = Math.hypot(axis[0], axis[1]);
    let d;
    if (alen > 1) {
      d = [-axis[1] / alen, axis[0] / alen];                      // ⟂ to the street
      if (d[0] * dRaw[0] + d[1] * dRaw[1] < 0) d = [-d[0], -d[1]];  // …pointing into the yard
    } else d = dRaw;
    const s = [-d[1], d[0]];
    // tie points: the house's side walls ∩ the cut line halfway up its depth
    // (the cut now runs parallel to the street, so it crosses the true sides)
    const hp = H.map(h => h[0] * d[0] + h[1] * d[1]);
    const hpMin = Math.min(...hp), hpMax = Math.max(...hp);
    const Q = [d[0] * (hpMin + 0.5 * (hpMax - hpMin)), d[1] * (hpMin + 0.5 * (hpMax - hpMin))];
    const hits = [];
    for (let i = 0; i < H.length; i++) {
      const A = H[i], B = H[(i + 1) % H.length];
      const sa = (A[0] - Q[0]) * d[0] + (A[1] - Q[1]) * d[1], sb = (B[0] - Q[0]) * d[0] + (B[1] - Q[1]) * d[1];
      if ((sa < 0) !== (sb < 0)) { const t = sa / (sa - sb); hits.push([A[0] + t * (B[0] - A[0]), A[1] + t * (B[1] - A[1])]); }
    }
    if (hits.length < 2) { flash("Couldn't find the house's side walls — drag the amber outline to fit, then press H again"); return; }
    const sOf = (p) => (p[0] - Q[0]) * s[0] + (p[1] - Q[1]) * s[1];
    const dOf = (pt) => (pt[0] - Q[0]) * d[0] + (pt[1] - Q[1]) * d[1];
    hits.sort((a, b) => sOf(a) - sOf(b));
    const HL = hits[0], HR = hits[hits.length - 1];
    // The tie is TWO segments, not a diagonal: the side run stays on the lot
    // line down to the house's mid-depth, then a short 90° return jumps
    // sideways into the wall. J = the jog corner. Prefer the point on the
    // original side line (C→F, preserves a slightly-angled lot line); when
    // that line is degenerate (re-running H on an already-tied chain), drop
    // plumb from the neighbour corner instead. Sub-2 ft returns skip the jog.
    const jogOf = (tieXY, CXY, FXY) => {
      const dC = dOf(CXY), dT = dOf(tieXY);
      let J = null;
      if (FXY) {
        const dF = dOf(FXY), den = dF - dC;
        if (Math.abs(den) > 1e-6) {
          const t = (dT - dC) / den;
          if (t > 0.02 && t < 0.98) J = [CXY[0] + (FXY[0] - CXY[0]) * t, CXY[1] + (FXY[1] - CXY[1]) * t];
        }
      }
      if (!J) J = [CXY[0] + d[0] * (dT - dC), CXY[1] + d[1] * (dT - dC)];
      if (Math.hypot(J[0] - tieXY[0], J[1] - tieXY[1]) < 0.6) return null;   // return too short to draw
      if (Math.hypot(J[0] - CXY[0], J[1] - CXY[1]) < 0.6) return null;       // corner already at tie depth
      return J;
    };
    const sHL = sOf(HL), sHR = sOf(HR);
    if (!cl) {
      // open chain: first peel any INTERIOR corner sitting in front of the
      // house's mid-line within its walls — fence-through-the-front territory,
      // left over from a pre-fix H run — then tie both ends with squared
      // returns. Interior-corner removal invalidates side indexing, so per-
      // wall settings rebuild fresh only on that repair path.
      const keep: any[] = [];
      for (let i = 0; i < n; i++) {
        const spike = i !== 0 && i !== n - 1 && dOf(P[i]) < -0.5 && sOf(P[i]) > sHL + 0.5 && sOf(P[i]) < sHR - 0.5;
        if (!spike) keep.push(i);
      }
      const m2 = keep.length;
      if (m2 < 2) return;
      const KP = keep.map(i => P[i]);
      const aLeft = sOf(KP[0]) <= sOf(KP[m2 - 1]);
      const T0 = aLeft ? HL : HR, TN = aLeft ? HR : HL;
      const J0 = jogOf(T0, KP[1], KP[0]);
      const JN = jogOf(TN, KP[m2 - 2], KP[m2 - 1]);
      const mid = keep.slice(1, m2 - 1).map(i => poly[i].slice());
      const out = [toLL(T0, o), ...(J0 ? [toLL(J0, o)] : []), ...mid, ...(JN ? [toLL(JN, o)] : []), toLL(TN, o)];
      const repaired = m2 !== n;
      const shift = J0 ? 1 : 0;
      const oldPrevO = prevMap(par.sides || []);
      const prevO = {};
      if (!repaired) Object.keys(oldPrevO).forEach(k => { prevO[+k + shift] = oldPrevO[k]; });
      const gatesO = repaired ? [] : (par.gates || []).map(g => ({ ...g, side: g.side + shift }));
      setParcel(p => ({ ...p, polygon: out, gates: gatesO, sides: buildSides(out, prevO, false) }));
      flash(repaired ? "Squared into the house — removed the run that cut across the front" : "Fence squared into the house — 90° returns at both ends · every other corner kept");
      return;
    }
    // Closed loop: the house replaces the STREET side of the loop. On a plain
    // rectangular lot that's one wall — but angled/cul-de-sac lots follow the
    // street with SEVERAL short walls (36+34, 33+33 …), and removing only one
    // left the other front corners hanging as spikes down to the road. So:
    // grow the removed arc outward from the most street-ward wall while the
    // next corner is still street-side AND either laterally in front of the
    // house or reached by a street-parallel wall. Wrap-around yards stay —
    // their corners sit far outside the house laterally on angled walls.
    const arcOK = (V, wallA, wallB) => {
      if (dOf(V) >= 0.5) return false;                                  // corner already yard-side
      if (sOf(V) >= sHL - 2 && sOf(V) <= sHR + 2) return true;          // right in front of the house
      const w = [wallB[0] - wallA[0], wallB[1] - wallA[1]];
      const wl = Math.hypot(w[0], w[1]) || 1;
      return Math.abs((w[0] * s[0] + w[1] * s[1]) / wl) > 0.82;         // street-parallel approach wall
    };
    let bStart = iF, bEnd = (iF + 1) % n, arcWalls = 1;
    for (let g = 0; g < n && arcWalls < n - 2; g++) {                   // expand toward the start side
      const w = (bStart - 1 + n) % n, V = P[w];
      if (!arcOK(V, P[w], P[bStart])) break;
      bStart = w; arcWalls++;
    }
    for (let g = 0; g < n && arcWalls < n - 2; g++) {                   // expand toward the end side
      const V = P[(bEnd + 1) % n];
      if (!arcOK(V, P[bEnd], P[(bEnd + 1) % n])) break;
      bEnd = (bEnd + 1) % n; arcWalls++;
    }
    // walk the yard way round: from the arc's end corner back to its start
    // corner — every corner strictly inside the removed arc is gone
    const order = [];
    for (let k = bEnd; ; k = (k + 1) % n) { order.push(k); if (k === bStart) break; }
    const len = order.length;
    if (len < 2) return;
    const aLeft = sOf(P[order[0]]) <= sOf(P[order[len - 1]]);
    const T0 = aLeft ? HL : HR, TN = aLeft ? HR : HL;
    // jog corners: side runs hold the lot line, 90° returns jump into the wall
    const J0 = jogOf(T0, P[order[1]], P[order[0]]);
    const JN = jogOf(TN, P[order[len - 2]], P[order[len - 1]]);
    const mid = order.slice(1, len - 1).map(idx => poly[idx].slice());
    const chain = [toLL(T0, o), ...(J0 ? [toLL(J0, o)] : []), ...mid, ...(JN ? [toLL(JN, o)] : []), toLL(TN, o)];
    // every kept wall carries its settings (product / grade / selection) and
    // gates to its new index; the removed front arc's gates go with it, and
    // the inserted return shifts everything after it by one
    const shift = J0 ? 1 : 0;
    const oldPrev = prevMap(par.sides || []);
    const remap = {};   // old side index → new side index (front arc dropped)
    for (let k = 0; k < len - 1; k++) remap[order[k]] = k + shift;
    const prev = {};
    Object.keys(remap).forEach(oi => { if (oldPrev[oi]) prev[remap[oi]] = oldPrev[oi]; });
    const gates = (par.gates || []).filter(g => remap[g.side] != null).map(g => ({ ...g, side: remap[g.side] }));
    setParcel(p => ({ ...p, polygon: chain, closed: false, gates, cornerElev: null, sides: buildSides(chain, prev, false) }));
    setActiveSide(null);
    flash("Front opened into the house — squared in with 90° returns · your back & side corners kept");
  }
  /* ---- one-click layouts + setback (always derived from the TRUE property
     line, so they're predictable; they replace the working shape, which clears
     manual corner edits & gates — that's the point of a "reset" action) ---- */
  function relayout(kind) {
    setParcel(p => {
      if (!p) return p;
      const raw = p.rawPolygon || p.polygon;
      const cl = kind === "square" ? (p.closed !== false) : true;   // square refines what you have; reset/rectangle re-close from the lot line
      let poly;
      if (kind === "rectangle") poly = clampToLot(boundingRect(raw), raw);                          // box, pulled inside the lot line
      else if (kind === "square") poly = clampToLot(squareCornersPoly(simplifyRing(p.polygon, 6), cl), raw);  // de-jag + square, kept inside the lot
      else poly = simplifyRing(raw, SIMPLIFY_TOL_FT);
      return { ...p, polygon: poly, closed: cl, gates: [], cornerElev: null, sides: buildSides(poly, null, cl) };
    });
    setSetbackStr(""); setActiveSide(null);
    flash(kind === "rectangle" ? "Snapped to a clean rectangle · adjust corners to fit"
      : kind === "square" ? "Squared up · jagged runs straightened to the lot axis"
      : "Reset to the property line · cleaned to straight runs");
  }
  // fit the CURRENT fence onto the property line, wall by wall — keeps your
  // edits (deleted posts, custom corners, side count). Each wall that RUNS
  // ALONG a lot edge (roughly parallel and nearby) slides out onto that edge's
  // line and the corners re-miter where neighbouring walls meet; a wall far
  // from any parallel lot line (e.g. a front run you dragged up to the house)
  // stays exactly where you put it. Pass a side index (Shift+F / side menu) to
  // force-fit just that wall, however far off the line it sits.
  const FIT_PAR_DEG = 25;   // wall vs lot edge: max angle to count as "runs along it"
  const FIT_NEAR_FT = 25;   // ...and how close before the automatic fit grabs it
  function fitToLotLine(onlyIdx?: any) {
    const par = refs.current.parcel;   // live state — this also runs from the mount-time key handler
    if (!par || refs.current.manual) return;
    if (!Array.isArray(par.rawPolygon) || par.rawPolygon.length < 3) { flash("No property line to fit to — search the address again to reload the parcel"); return; }
    const lot = simplifyRing(par.rawPolygon, SIMPLIFY_TOL_FT);
    const o = centroid(lot);
    const ring = lot.map(v => toXY(v, o));
    const edges = ring.map((a, j) => {
      const b = ring[(j + 1) % ring.length];
      const dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy) || 1;
      return { a, b, u: [dx / len, dy / len], len };
    }).filter(eg => eg.len > ftToM(6));   // ignore leftover jitter stubs
    if (!edges.length) { flash("No property line to fit to"); return; }
    // computed (and validated) BEFORE setParcel — same pattern as applySetback
    const P = par.polygon.map(v => toXY(v, o));
    const n = P.length, cl = par.closed !== false;
    const sideCount = cl ? n : n - 1;
    if (onlyIdx != null && (onlyIdx < 0 || onlyIdx >= sideCount)) return;
    const lines = [];
    let fitted = 0;
    for (let i = 0; i < sideCount; i++) {
      const A = P[i], B = P[(i + 1) % n];
      const dx = B[0] - A[0], dy = B[1] - A[1], len = Math.hypot(dx, dy) || 1;
      const u = [dx / len, dy / len], M = [(A[0] + B[0]) / 2, (A[1] + B[1]) / 2];
      const force = onlyIdx === i;
      const cosPar = Math.cos((force ? 60 : FIT_PAR_DEG) * Math.PI / 180);   // force mode is looser about the angle
      let best = null, bd = Infinity;
      edges.forEach(eg => {
        if (Math.abs(u[0] * eg.u[0] + u[1] * eg.u[1]) < cosPar) return;      // wall doesn't run along this edge
        const q = nearestOnSegXY(M, eg.a, eg.b);
        const d = Math.hypot(q[0] - M[0], q[1] - M[1]);
        if (d < bd) { bd = d; best = eg; }
      });
      if (best && (onlyIdx != null ? force : bd <= ftToM(FIT_NEAR_FT))) { lines.push({ p: best.a, u: best.u, moved: true }); fitted++; }
      else lines.push({ p: A, u, moved: false });
    }
    if (!fitted) {
      flash(onlyIdx != null ? "That wall doesn't run along any lot line" : `No wall is within ${FIT_NEAR_FT} ft of a parallel lot line — click a wall, then Shift+F fits just it`);
      return;
    }
    const MAX_MITER_M = ftToM(80);   // a corner shooting past this = near-parallel walls; project instead
    const projOnto = (Ln2, V) => { const t = (V[0] - Ln2.p[0]) * Ln2.u[0] + (V[1] - Ln2.p[1]) * Ln2.u[1]; return [Ln2.p[0] + Ln2.u[0] * t, Ln2.p[1] + Ln2.u[1] * t]; };
    const out = P.map(v => v.slice());
    for (let k = 0; k < n; k++) {
      const iPrev = cl ? (k - 1 + sideCount) % sideCount : k - 1;            // wall ENDING at this corner
      const iNext = cl ? k : (k < sideCount ? k : -1);                       // wall STARTING at this corner
      const Lp = iPrev >= 0 ? lines[iPrev] : null, Ln = iNext >= 0 ? lines[iNext] : null;
      if ((!Lp || !Lp.moved) && (!Ln || !Ln.moved)) continue;                // neither wall moved — corner stays
      if (Lp && Ln) {
        const meet = lineMeet(Lp.p, Lp.u, Ln.p, Ln.u);
        out[k] = (meet && Math.hypot(meet[0] - P[k][0], meet[1] - P[k][1]) <= MAX_MITER_M) ? meet : projOnto(Ln.moved ? Ln : Lp, P[k]);
      } else out[k] = projOnto(Lp && Lp.moved ? Lp : Ln, P[k]);              // open-chain endpoint: drop straight onto the new line
    }
    const poly = out.map(q => toLL(q, o));
    setParcel(p => ({ ...p, polygon: poly, sides: buildSides(poly, prevMap(p.sides), p.closed !== false) }));
    setSetbackStr("");
    const kept = sideCount - fitted;
    flash(onlyIdx != null ? `Side ${onlyIdx + 1} fit onto the lot line`
      : kept === 0 ? "All walls fit onto the property line"
      : `${fitted} wall${fitted > 1 ? "s" : ""} fit onto the lot line · ${kept} kept where you put ${kept > 1 ? "them" : "it"}`);
  }
  function applySetback(ft) {
    const d = Math.max(0, Math.min(50, +ft || 0));   // cap: a deeper inset swallows most residential lots
    // compute (and validate) the inset polygon BEFORE setParcel — a setState
    // updater runs after this function returns, so we can't read its result here.
    if (!parcel || parcel.closed === false) { flash("Re-close the loop before setting a setback"); return; }
    // SELECTED-SIDE scope: inset just the highlighted wall (e.g. the rear lot
    // line) off its current spot, leaving every other side, gate & elevation be.
    if (setbackScope === "side") {
      let target = activeSide;
      if (target == null && parcel.house && window.VFP_BUILDINGS) {   // nothing picked → use the detected rear lot line
        const fr = window.VFP_BUILDINGS.frontRearEdges(parcel.polygon, parcel.house);
        if (fr) { target = fr.rear; setActiveSide(fr.rear); }
      }
      if (target == null) { flash("Click the rear (or any) lot line first, then Apply"); return; }
      if (!(d > 0)) { flash("Enter a setback distance first"); return; }
      const dists = parcel.polygon.map((_, i) => (i === target ? d : 0));
      const poly = insetRing(parcel.polygon, dists);
      if (!poly) { flash("That setback is too deep for this side"); return; }
      setParcel(p => ({ ...p, polygon: poly, sides: buildSides(poly, prevMap(p.sides), p.closed !== false) }));
      flash(`Side ${target + 1} set back ${d} ft off the line${activeSide == null ? " (rear, auto)" : ""}`);
      return;
    }
    // ALL-SIDES scope: inset the whole fence to d ft inside the property line.
    const base = simplifyRing(parcel.rawPolygon || parcel.polygon, SIMPLIFY_TOL_FT);
    const poly = d > 0 ? insetRing(base, d) : base;
    if (!poly) { flash("That setback is too deep for this lot"); return; }
    setParcel(p => ({ ...p, polygon: poly, closed: true, gates: [], cornerElev: null, sides: buildSides(poly, null, true) }));
    setActiveSide(null);
    flash(d > 0 ? `Fence set ${d} ft inside the property line · all sides` : "Fence reset onto the property line");
  }
  // nudge ONE wall in/out from its current spot by a precise distance (a precision
  // version of Shift-dragging a wall) — for a per-side setback off the lot line.
  // +ft = inward (onto the lot), −ft = outward (toward / over the line).
  function offsetSide(i, deltaFt) {
    setParcel(p => {
      const n = p.polygon.length; if (i >= n) return p;
      const o = centroid(p.polygon);
      const A = toXY(p.polygon[i], o), B = toXY(p.polygon[(i + 1) % n], o);
      const dx = B[0] - A[0], dy = B[1] - A[1], len = Math.hypot(dx, dy) || 1;
      const ccw = ringAreaXY(p.polygon.map(v => toXY(v, o))) > 0;
      const nx = (ccw ? -dy : dy) / len, ny = (ccw ? dx : -dx) / len;   // inward unit normal
      const d = ftToM(deltaFt);
      const poly = p.polygon.slice();
      poly[i] = toLL([A[0] + nx * d, A[1] + ny * d], o);
      poly[(i + 1) % n] = toLL([B[0] + nx * d, B[1] + ny * d], o);
      return { ...p, polygon: poly, sides: buildSides(poly, prevMap(p.sides), p.closed !== false) };
    });
  }
  const pOf = (s) => ({ selected: s.selected, grade: s.grade, gradeAuto: s.gradeAuto, autoSlope: s.autoSlope, profile: s.profile });
  // remove every corner whose on-screen position falls inside a pixel rectangle
  // (the box-delete marquee) — clears a whole jagged survey cluster in one drag.
  // Gates / corner elevations on still-intact sides are remapped; the rest drop.
  function deleteCornersInPixelRect(r) {
    const m = map.current, p = refs.current.parcel;
    if (!m || !p || refs.current.manual || !Array.isArray(p.polygon)) return;
    const n = p.polygon.length;
    const inBox = p.polygon.map(v => { const pt = m.latLngToContainerPoint(v); return pt.x >= r.x0 && pt.x <= r.x1 && pt.y >= r.y0 && pt.y <= r.y1; });
    const removeCount = inBox.filter(Boolean).length;
    if (!removeCount) { flash("Box had no corners in it"); return; }
    const keep = []; for (let i = 0; i < n; i++) if (!inBox[i]) keep.push(i);
    if (keep.length < 3) { flash("Kept — a fence needs at least 3 corners"); return; }
    const poly = keep.map(i => p.polygon[i].slice());
    const newOf = new Map(); keep.forEach((oldI, newI) => newOf.set(oldI, newI));
    // a side (edge oldI→oldI+1) survives only if both ends are kept AND stay adjacent
    const sideSurvives = (oldI) => { const a = oldI, b = (oldI + 1) % n; if (!newOf.has(a) || !newOf.has(b)) return false; return newOf.get(b) === (newOf.get(a) + 1) % keep.length; };
    const prev = {}; p.sides.forEach(s => { if (sideSurvives(s.i)) prev[newOf.get(s.i)] = pOf(s); });
    const gates = (p.gates || []).filter(g => sideSurvives(g.side)).map(g => ({ ...g, side: newOf.get(g.side) }));
    const ce = {}; if (p.cornerElev) Object.keys(p.cornerElev).forEach(k => { if (newOf.has(+k)) ce[newOf.get(+k)] = p.cornerElev[k]; });
    setParcel(pp => ({ ...pp, polygon: poly, sides: buildSides(poly, prev, pp.closed !== false), gates, cornerElev: Object.keys(ce).length ? ce : null }));
    setActiveSide(null);
    flash(`Removed ${removeCount} corner${removeCount > 1 ? "s" : ""} in the box`);
  }
  // split a measured profile at fraction f → [left, right], t rescaled to each new run
  function splitProfile(profile, f) {
    if (!Array.isArray(profile) || !profile.length || !(f > 0 && f < 1)) return [profile || null, null];
    const left = [], right = [];
    profile.forEach(pt => { if (pt.t <= f) left.push({ t: pt.t / f, deltaIn: pt.deltaIn }); else right.push({ t: (pt.t - f) / (1 - f), deltaIn: pt.deltaIn }); });
    return [left.length ? left : null, right.length ? right : null];
  }
  // rotate the whole fence around its centroid (fine alignment to the plot map)
  function rotateParcel(deg) {
    setParcel(p => {
      const c = centroid(p.polygon), k = Math.cos(c[0] * Math.PI / 180);
      const th = deg * Math.PI / 180, cos = Math.cos(th), sin = Math.sin(th);
      const poly = p.polygon.map(v => {
        const x = (v[1] - c[1]) * k, y = v[0] - c[0];
        return [c[0] + (x * sin + y * cos), c[1] + (x * cos - y * sin) / k];
      });
      return { ...p, polygon: poly, sides: buildSides(poly, prevMap(p.sides), p.closed !== false) };
    });
  }
  // translate the whole fence by a lat/lng delta (move handle + arrow keys)
  function translateParcel(dLat, dLng) {
    setParcel(p => {
      if (!p) return p;
      const poly = p.polygon.map(v => [v[0] + dLat, v[1] + dLng]);
      const gates = (p.gates || []).map(g => ({ ...g }));   // gates are stored by side+t, so they ride along
      return { ...p, polygon: poly, gates, sides: buildSides(poly, prevMap(p.sides), p.closed !== false) };
    });
  }
  // nudge by meters in a screen direction, converted to lat/lng at this latitude
  function nudgeParcel(dxM, dyM) {
    const R = refs.current, par = R.parcel, man = R.manual, mv = R.mVerts || [];
    const c = par ? centroid(par.polygon) : (mv.length ? centroid(mv) : null);
    if (!c) return;
    const dLat = dyM / 110540, dLng = dxM / (Math.cos(c[0] * Math.PI / 180) * 111320);
    if (par && !man) translateParcel(dLat, dLng);
    else if (man) setMVerts(vs => vs.map(v => [v[0] + dLat, v[1] + dLng]));
  }

  /* ---- gates placed on the drawing (feed the quote) ---- */
  function addGate(sideIdx, t?) {
    setParcel(p => {
      const gates = (p.gates || []).concat({ id: "g" + Date.now() + Math.round(Math.random() * 99), side: sideIdx, t: t == null ? 0.5 : t, width: 4, swing: 0 });
      return { ...p, gates };
    });
    flash("Gate added to Side " + (sideIdx + 1) + " — adds to quote");
  }
  function removeGate(id) { setParcel(p => ({ ...p, gates: (p.gates || []).filter(g => g.id !== id) })); flash("Gate removed"); }
  function setGateWidth(id, w) { setParcel(p => ({ ...p, gates: (p.gates || []).map(g => g.id === id ? { ...g, width: w } : g) })); }
  function cycleGateSwing(id) { setParcel(p => ({ ...p, gates: (p.gates || []).map(g => g.id === id ? { ...g, swing: ((g.swing || 0) + 1) % 4 } : g) })); }
  function moveGateLive(id, ll) {
    const p = lay.current.poly; if (!p) return;
    const g = (parcel.gates || []).find(x => x.id === id); if (!g) return;
    const n = p.length, a = p[g.side], b = p[(g.side + 1) % n];
    const A = toXY(a, a), B = toXY(b, a), C = toXY([ll.lat, ll.lng], a);
    const len2 = (B[0] * B[0] + B[1] * B[1]) || 1;
    let t = (C[0] * B[0] + C[1] * B[1]) / len2; t = Math.max(0.06, Math.min(0.94, t));
    lay.current.gateT = lay.current.gateT || {}; lay.current.gateT[id] = t;
    const pos = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    const mk = lay.current.gateMarks && lay.current.gateMarks[id]; if (mk) mk.setLatLng(pos);
  }
  function commitGate(id) {
    const t = lay.current.gateT && lay.current.gateT[id]; if (t == null) return;
    setParcel(p => ({ ...p, gates: (p.gates || []).map(g => g.id === id ? { ...g, t } : g) }));
  }
  const gatesOnSide = (i) => placedGates.filter(g => g.side === i);
  const sideForCorner = (idx) => idx;   // side i runs from corner i to corner i+1
  function projOnSide(i, latlng) {
    const poly = (parcel && parcel.polygon) || [], n = poly.length;
    const a = poly[i], b = poly[(i + 1) % n]; if (!a || !b) return [latlng.lat, latlng.lng];
    const t = gateTOnSide(i, latlng);
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  }
  function gateTOnSide(sideIdx, latlng) {
    const poly = (parcel && parcel.polygon) || []; const n = poly.length;
    const a = poly[sideIdx], b = poly[(sideIdx + 1) % n]; if (!a || !b) return 0.5;
    const B = toXY(b, a), C = toXY([latlng.lat, latlng.lng], a);
    const len2 = (B[0] * B[0] + B[1] * B[1]) || 1;
    let t = (C[0] * B[0] + C[1] * B[1]) / len2; return Math.max(0.06, Math.min(0.94, t));
  }

  /* ---- measured grade points (per-run elevation profile on side.profile) ----
     Each point = { t: 0..1 along a→b, deltaIn: inches vs the PREVIOUS point
     (first point vs run-start 0; − = drop, + = rise) }, kept sorted by t.
     Feeds pricing when Settings → Quoting → "Price slope from measured grade
     points" is on; otherwise display-only. */
  function tOnSide(sideIdx, latlng) {   // like gateTOnSide but spans nearly the full run
    const poly = (parcel && parcel.polygon) || []; const n = poly.length;
    const a = poly[sideIdx], b = poly[(sideIdx + 1) % n]; if (!a || !b) return 0.5;
    const B = toXY(b, a), C = toXY([latlng.lat, latlng.lng], a);
    const len2 = (B[0] * B[0] + B[1] * B[1]) || 1;
    return Math.max(0.01, Math.min(0.99, (C[0] * B[0] + C[1] * B[1]) / len2));
  }
  const profileOf = (i) => { const s = parcel && parcel.sides.find(x => x.i === i); return (s && s.profile) || []; };
  function setSideProfile(i, prof) {
    const norm = (prof || []).filter(pt => isFinite(+pt.t) && isFinite(+pt.deltaIn))
      .map(pt => ({ t: Math.max(0, Math.min(1, +pt.t)), deltaIn: +pt.deltaIn }))
      .sort((x, y) => x.t - y.t);
    setParcel(p => ({ ...p, sides: p.sides.map(s => s.i === i ? { ...s, profile: norm.length ? norm : null } : s) }));
  }
  function addGradePoint(i, t, deltaIn) { setSideProfile(i, [...profileOf(i), { t, deltaIn }]); flash(`Grade point ${fmtIn(deltaIn)} added to Side ${i + 1}`); }
  function updateGradePoint(i, idx, patch) { setSideProfile(i, profileOf(i).map((pt, k) => k === idx ? { ...pt, ...patch } : pt)); }
  function removeGradePoint(i, idx) { setSideProfile(i, profileOf(i).filter((_, k) => k !== idx)); }

  /* ---- absolute corner elevations (parcel.cornerElev) ----
     Contractors think in post heights: "this corner is base 0, the far
     corners are 72″ lower — slope gradually between." A corner's value is
     signed inches vs the base; runs derive a linear baseline between their
     two corners (lib/grade runElevSamples) and per-run profile points still
     add bumps on top. Sparse: only set corners; the rest interpolate. */
  const ceOf = (idx) => { const ce = parcel && parcel.cornerElev; const v = ce ? +ce[idx] : NaN; return isFinite(v) ? v : null; };
  function setCornerElev(idx, inches) {
    setParcel(p => {
      const ce = { ...(p.cornerElev || {}) };
      if (inches == null || !isFinite(+inches)) delete ce[idx]; else ce[idx] = +inches;
      return { ...p, cornerElev: Object.keys(ce).length ? ce : null };
    });
    flash(inches == null ? `Corner ${idx + 1} elevation cleared`
      : +inches === 0 ? `Corner ${idx + 1} set as base 0 — set the far corners relative to it`
      : `Corner ${idx + 1} set to ${fmtIn(+inches)} vs base — runs slope gradually between corners`);
  }

  /* ---- undo / redo history + keyboard ---- */
  React.useEffect(() => {
    const h = hist.current;
    if (h.applying) { h.applying = false; return; }
    // change key = only USER-meaningful state. Background auto-detect (autoSlope,
    // auto-grade, house footprint) churns the parcel object on every load but must
    // NOT create undo steps — that churn is what made undo feel like ~6 clicks to
    // reach a real edit. We still store the full state for an exact restore.
    const sd = (parcel && parcel.sides) || [];
    const key = JSON.stringify({
      manual, mVerts,
      poly: parcel && parcel.polygon, closed: parcel && parcel.closed,
      gates: parcel && parcel.gates, ce: parcel && parcel.cornerElev,
      sel: sd.map(s => s.selected), grade: sd.map(s => (s.gradeAuto ? "" : s.grade)), prof: sd.map(s => s.profile),
    });
    const top = h.stack[h.idx];
    if (top && top.key === key) return;                       // nothing the user changed
    h.stack = h.stack.slice(0, h.idx + 1);
    h.stack.push({ key, state: JSON.stringify({ parcel, manual, mVerts }) });
    if (h.stack.length > 60) h.stack.shift();
    h.idx = h.stack.length - 1;
  }, [parcel, manual, mVerts]);

  function applyHistory(entry) {
    const s = JSON.parse(entry.state);
    hist.current.applying = true;
    setParcel(s.parcel || null); setManual(!!s.manual); setMVerts(s.mVerts || []);
  }
  function undo() { const h = hist.current; if (h.idx > 0) { h.idx--; applyHistory(h.stack[h.idx]); flash("Undo"); } }
  function redo() { const h = hist.current; if (h.idx < h.stack.length - 1) { h.idx++; applyHistory(h.stack[h.idx]); flash("Redo"); } }

  React.useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const tag = t && t.tagName;
      const editing = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
      // the address search box keeps focus after a lookup (on macOS clicking a
      // button doesn't move focus), so Cmd/Ctrl+Z was being eaten by the input's
      // native text-undo and a fence edit (e.g. a box-delete) looked un-undoable.
      // Let undo/redo fire from the search box too — but leave real text fields
      // (quote notes, length inputs) their own native undo.
      const inSearch = editing && t.closest && t.closest(".map-search");
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "z" || e.key === "Z") && (!editing || inSearch)) { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (mod && (e.key === "y" || e.key === "Y") && (!editing || inSearch)) { e.preventDefault(); redo(); return; }
      if (editing) return;
      if (e.key === "Shift") shiftRef.current = true;
      if (e.key === "Meta" || e.key === "Control") cmdRef.current = true;
      if ((e.key === "Delete" || e.key === "Backspace") && refs.current.parcel && !refs.current.manual) {
        if (activeCornerRef.current != null) { e.preventDefault(); deleteCorner(activeCornerRef.current); setActiveCorner(null); }
        else if (activeSideRef.current != null) { e.preventDefault(); deleteSide(activeSideRef.current); }
      }
      else if (e.key.startsWith("Arrow") && (refs.current.parcel || refs.current.manual)) {
        e.preventDefault();
        const step = e.shiftKey ? 3.2 : 0.6;   // meters per press (Shift = bigger)
        const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
        const dy = e.key === "ArrowUp" ? step : e.key === "ArrowDown" ? -step : 0;
        nudgeParcel(dx, dy);
      }
      // ---- quick keys for laying out / adjusting a side (parcel mode only) ----
      else if (!mod && !e.altKey && refs.current.parcel && !refs.current.manual) {
        const par = refs.current.parcel, ids = (par.sides || []).map(s => s.i);
        const cur = activeSideRef.current, pos = ids.indexOf(cur);
        const k = e.key.toLowerCase();
        if (k === "]" || k === "}") { e.preventDefault(); if (ids.length) setActiveSide(ids[(pos + 1 + ids.length) % ids.length]); }
        else if (k === "[" || k === "{") { e.preventDefault(); if (ids.length) setActiveSide(ids[(pos - 1 + ids.length) % ids.length]); }
        else if (k === "?") { e.preventDefault(); setShowKeys(v => !v); }
        else if (k === "b") { e.preventDefault(); setBoxSelect(true); }         // B = box-delete mode
        else if (k === "v") { e.preventDefault(); setBoxSelect(false); }        // V = back to the standard selector
        else if (k === "l") { e.preventDefault(); setShowPropLine(v => !v); }   // L = lot line overlay
        else if (k === "q") { e.preventDefault(); relayout("square"); }         // Q = square up
        // F = fit walls onto the property line · Shift+F = just the highlighted wall
        else if (k === "f") { e.preventDefault(); fitToLotLine(e.shiftKey && activeSideRef.current != null ? activeSideRef.current : undefined); }
        else if (k === "h") { e.preventDefault(); fitToHouse(); }               // H = fit to house (tie the fence into the house sides)
        else if (k === "e") {                                                   // E = extend the open run past its end (add another section)
          e.preventDefault();
          const par2 = refs.current.parcel;
          if (par2 && par2.closed === false && (par2.polygon || []).length >= 2) {
            extendFrom(activeCornerRef.current === 0 ? 0 : (par2.polygon || []).length - 1);
          } else flash("Extend works on an open run — on a closed shape, click a wall → Split / add point");
        }
        else if (cur != null) {   // these act on the highlighted side
          if ((e.key === "Enter" || k === " ") && !(t && t.tagName === "BUTTON")) { e.preventDefault(); toggleSide(cur); }
          else if (k === "g") { e.preventDefault(); addGate(cur); }
          else if (k === "s") { e.preventDefault(); splitSide(cur); }
          else if (k === "i") { e.preventDefault(); offsetSide(cur, e.shiftKey ? 1 : 0.5); }    // In  (inset onto the lot)
          else if (k === "o") { e.preventDefault(); offsetSide(cur, e.shiftKey ? -1 : -0.5); }  // Out (toward the line)
        }
      }
    };
    const onUp = (e) => { if (e.key === "Shift") shiftRef.current = false; if (e.key === "Meta" || e.key === "Control") cmdRef.current = false; };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onUp);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("keyup", onUp); };
  }, []);

  /* ---- auto slope/grade detection (USGS EPQS -> Open-Elevation) ---- */
  const selKeys = (parcel && !manual) ? parcel.sides.filter(s => s.selected).map(sideKey).join("~") : "";
  React.useEffect(() => {
    if (!parcel || manual || !window.VFP_SLOPE) return;
    parcel.sides.forEach(s => {
      if (!s.selected) return;
      const k = sideKey(s);
      if (s.autoSlope && s.autoSlope.key === k && s.autoSlope.status !== "loading") return;
      if (slopePending.current[k]) return;
      slopePending.current[k] = true;
      setParcel(p => ({ ...p, sides: p.sides.map(x => sideKey(x) === k ? { ...x, autoSlope: { status: "loading", key: k } } : x) }));
      window.VFP_SLOPE.detectRun([s.a, s.b]).then(res => {
        slopePending.current[k] = false;
        setParcel(p => ({ ...p, sides: p.sides.map(x => {
          if (sideKey(x) !== k) return x;
          const grade = (x.gradeAuto && res.status === "done" && res.grade) ? res.grade : x.grade;
          return { ...x, autoSlope: { ...res, key: k }, grade };
        }) }));
      }).catch(() => {
        slopePending.current[k] = false;
        setParcel(p => ({ ...p, sides: p.sides.map(x => sideKey(x) === k ? { ...x, autoSlope: { status: "failed", key: k } } : x) }));
      });
    });
  }, [selKeys, manual]);

  /* ---- map lock: freeze pan/zoom so handle drags don't move the map ---- */
  React.useEffect(() => {
    const m = map.current; if (!m) return;
    if (mapLocked) { m.dragging.disable(); m.doubleClickZoom.disable(); m.boxZoom.disable(); if (m.tap) m.tap.disable(); }
    else { m.dragging.enable(); m.doubleClickZoom.enable(); m.boxZoom.enable(); if (m.tap) m.tap.enable(); }
  }, [mapLocked]);

  /* ---- box-delete: drag a rectangle to remove every corner inside it (clears a
     jagged survey cluster in one stroke). While on, the map's own pan/zoom is
     suspended and pointer drags draw the marquee instead (captured before the
     handles so a drag over them still boxes). ---- */
  React.useEffect(() => {
    const m = map.current, host = mapEl.current;
    if (!m || !host || !boxSelect) return;
    host.classList.add("box-mode");
    m.dragging.disable(); m.boxZoom.disable();
    let box = null, start = null, cur = null;
    const pt = (ev) => { const r = host.getBoundingClientRect(); const s = (ev.touches && ev.touches[0]) || (ev.changedTouches && ev.changedTouches[0]) || ev; return { x: s.clientX - r.left, y: s.clientY - r.top }; };
    const draw = () => { if (!box || !start || !cur) return; box.style.left = Math.min(start.x, cur.x) + "px"; box.style.top = Math.min(start.y, cur.y) + "px"; box.style.width = Math.abs(cur.x - start.x) + "px"; box.style.height = Math.abs(cur.y - start.y) + "px"; };
    const move = (ev) => { cur = pt(ev); ev.preventDefault(); draw(); };
    const up = (ev) => {
      document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up);
      document.removeEventListener("touchmove", move); document.removeEventListener("touchend", up);
      if (start && cur && box) { const x0 = Math.min(start.x, cur.x), y0 = Math.min(start.y, cur.y), x1 = Math.max(start.x, cur.x), y1 = Math.max(start.y, cur.y); if (x1 - x0 > 6 && y1 - y0 > 6) deleteCornersInPixelRect({ x0, y0, x1, y1 }); }
      if (box && box.parentNode) box.parentNode.removeChild(box);
      box = null; start = null; cur = null;
    };
    const down = (ev) => {
      if (ev.target && ev.target.closest && ev.target.closest(".leaflet-control")) return;   // keep zoom controls clickable
      ev.stopPropagation(); ev.preventDefault();
      start = cur = pt(ev);
      box = document.createElement("div"); box.className = "box-sel"; host.appendChild(box); draw();
      document.addEventListener("mousemove", move); document.addEventListener("mouseup", up);
      document.addEventListener("touchmove", move, { passive: false }); document.addEventListener("touchend", up);
    };
    host.addEventListener("mousedown", down, true);
    host.addEventListener("touchstart", down, { capture: true, passive: false });
    return () => {
      host.classList.remove("box-mode");
      host.removeEventListener("mousedown", down, true);
      host.removeEventListener("touchstart", down, true);
      document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up);
      document.removeEventListener("touchmove", move); document.removeEventListener("touchend", up);
      if (box && box.parentNode) box.parentNode.removeChild(box);
      if (!refs.current.mapLocked) { m.dragging.enable(); m.boxZoom.enable(); }
    };
  }, [boxSelect]);

  /* ---- redraw ---- */
  React.useEffect(() => {
    const L = window.L, m = map.current; if (!m) return;
    (lay.current.items || []).forEach(x => m.removeLayer(x));
    lay.current = { items: [], poly: null, fill: null, sidePolys: {}, sideLabels: {}, handles: {} };
    const add = (x) => { lay.current.items.push(x); return x.addTo(m); };

    if (parcel && !manual) {
      const cl = parcel.closed !== false;
      lay.current.poly = parcel.polygon.map(p => p.slice());
      lay.current.closed = cl;
      lay.current.sideHit = {};
      // faint reference: the TRUE county boundary, drawn UNDER the editable fence
      // so the setback gap (fence vs. surveyed line) reads at a glance
      if (showPropLine && Array.isArray(parcel.rawPolygon) && parcel.rawPolygon.length >= 3) {
        add(L.polygon(parcel.rawPolygon.map(v => v.slice()), { color: "#9fb2c9", weight: 1.5, dashArray: "3 7", fill: false, opacity: 0.6, interactive: false }));
      }
      // detected house footprint (amber) — context for the backyard tie-in & front/rear
      if (showHouse && Array.isArray(parcel.house) && parcel.house.length >= 3) {
        add(L.polygon(parcel.house.map(v => v.slice()), { color: "#f59e6b", weight: 1.5, dashArray: "4 4", fillColor: "#f59e6b", fillOpacity: 0.1, interactive: false }));
      }
      // boundary: filled polygon when closed, open polyline when the loop is broken
      if (cl) lay.current.fill = add(L.polygon(lay.current.poly, { color: "#19e3c2", weight: 1.5, dashArray: "5 5", fillColor: "#19e3c2", fillOpacity: 0.06 }));
      else lay.current.fill = add(L.polyline(lay.current.poly, { color: "#19e3c2", weight: 1.5, dashArray: "5 5", opacity: 0.5 }));
      const pn = parcel.polygon.length;
      // center MOVE handle — manual pointer drag (no Leaflet draggable, so a click never moves anything)
      {
        const ctr = centroid(parcel.polygon);
        const mh = add(L.marker(ctr, { draggable: false, keyboard: false, interactive: true, zIndexOffset: 1500, icon: L.divIcon({ className: "move-wrap", html: `<div class="move-handle" title="Drag to move the whole fence (or use arrow keys)"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#06121a" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v20M2 12h20M12 2l-3 3M12 2l3 3M12 22l-3-3M12 22l3-3M2 12l3-3M2 12l3 3M22 12l-3-3M22 12l-3 3"/></svg></div>`, iconSize: [34, 34], iconAnchor: [17, 17] }) }));
        lay.current.moveHandle = mh;
        mh.on("mousedown", (e) => {
          const oe = e.originalEvent; L.DomEvent.stop(e);
          const startLL = e.latlng, startPx = m.mouseEventToContainerPoint(oe);
          let real = false;
          const gates = (parcel.gates || []).map(g => { const a = parcel.polygon[g.side], b = parcel.polygon[(g.side + 1) % pn]; return { id: g.id, pos: [a[0] + (b[0] - a[0]) * g.t, a[1] + (b[1] - a[1]) * g.t] }; });
          lay.current.moveOrigin = { poly: parcel.polygon.map(v => v.slice()), center: ctr.slice(), gates };
          const wasDrag = m.dragging.enabled(); m.dragging.disable();
          const onMove = (ev) => {
            const px = m.mouseEventToContainerPoint(ev);
            if (!real && Math.hypot(px.x - startPx.x, px.y - startPx.y) < 8) return;   // ignore click jitter
            real = true; draggingRef.current = true;
            const ll = m.containerPointToLatLng(px);
            liveTranslateAll(ll.lat - startLL.lat, ll.lng - startLL.lng);
          };
          const onUp = (ev) => {
            document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp);
            if (wasDrag) m.dragging.enable();
            const o = lay.current.moveOrigin; lay.current.moveOrigin = null;
            if (real && o) { const px = m.mouseEventToContainerPoint(ev), ll = m.containerPointToLatLng(px); commitTranslate(ll.lat - startLL.lat, ll.lng - startLL.lng); }
            setTimeout(() => { draggingRef.current = false; }, 60);
          };
          document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
        });
        mh.on("touchstart", (e) => {
          const oe = e.originalEvent; if (!oe.touches || !oe.touches[0]) return; L.DomEvent.stop(e);
          const startLL = e.latlng, t0 = oe.touches[0], startPx = m.mouseEventToContainerPoint(t0);
          let real = false;
          // build the SAME full origin the mouse path does — liveTranslateAll reads
          // o.poly[k] and o.gates, so a center-only origin threw on the first move
          // and the touch drag silently did nothing.
          const gates = (parcel.gates || []).map(g => { const a = parcel.polygon[g.side], b = parcel.polygon[(g.side + 1) % pn]; return { id: g.id, pos: [a[0] + (b[0] - a[0]) * g.t, a[1] + (b[1] - a[1]) * g.t] }; });
          lay.current.moveOrigin = { poly: parcel.polygon.map(v => v.slice()), center: ctr.slice(), gates };
          const wasDrag = m.dragging.enabled(); m.dragging.disable();
          const onMove = (ev) => {
            const t = ev.touches[0]; if (!t) return;
            const px = m.mouseEventToContainerPoint(t);
            if (!real && Math.hypot(px.x - startPx.x, px.y - startPx.y) < 10) return;
            real = true; draggingRef.current = true; ev.preventDefault();
            const ll = m.containerPointToLatLng(px);
            liveTranslateAll(ll.lat - startLL.lat, ll.lng - startLL.lng);
          };
          const onEnd = (ev) => {
            document.removeEventListener("touchmove", onMove); document.removeEventListener("touchend", onEnd);
            if (wasDrag) m.dragging.enable();
            lay.current.moveOrigin = null;
            const t = ev.changedTouches && ev.changedTouches[0];
            if (real && t) { const px = m.mouseEventToContainerPoint(t), ll = m.containerPointToLatLng(px); commitTranslate(ll.lat - startLL.lat, ll.lng - startLL.lng); }
            setTimeout(() => { draggingRef.current = false; }, 60);
          };
          document.addEventListener("touchmove", onMove, { passive: false }); document.addEventListener("touchend", onEnd);
        });
      }
      parcel.sides.forEach((s) => {
        const isActive = s.i === activeSide;
        const pl = add(L.polyline([s.a, s.b], { color: s.selected ? "#2ff3d6" : "#19e3c2", weight: isActive ? 7 : (s.selected ? 5 : 2.5), opacity: s.selected ? 0.95 : 0.6, interactive: false }));
        if (isActive) add(L.polyline([s.a, s.b], { color: "#fff", weight: 1.5, opacity: 0.9, dashArray: "2 6", interactive: false }));
        lay.current.sidePolys[s.i] = pl;
        // wide invisible hit line carries all interactions (easy to grab)
        const hit = add(L.polyline([s.a, s.b], { color: "#fff", weight: 20, opacity: 0, interactive: true }));
        lay.current.sideHit[s.i] = hit;
        const lpClear = () => { if (lpRef.current) { clearTimeout(lpRef.current); lpRef.current = null; } };
        hit.on("dblclick", (e) => { L.DomEvent.stop(e); addGate(s.i, gateTOnSide(s.i, e.latlng)); });
        hit.on("click", (e) => {
          L.DomEvent.stop(e); const oe = e.originalEvent;
          if (gradeModeRef.current && s.selected) {   // grade mode: click the run to drop a measured point
            setActiveSide(s.i);
            setGradePop({ i: s.i, t: tOnSide(s.i, e.latlng), x: oe ? oe.clientX : window.innerWidth / 2, y: oe ? oe.clientY : window.innerHeight / 2 });
            return;
          }
          if (oe && (oe.metaKey || oe.ctrlKey)) { insertCornerAt(s.i, projOnSide(s.i, e.latlng)); } else setActiveSide(s.i);
        });
        hit.on("contextmenu", (e) => { L.DomEvent.stop(e); openSideMenu(s.i, e.originalEvent, e.latlng); });
        hit.on("mousedown", (e) => {
          const oe = e.originalEvent;
          if (oe.shiftKey) {   // Shift+drag the whole wall — neighbors stretch to follow
            L.DomEvent.stop(e); lpClear(); draggingRef.current = true; setActiveSide(s.i);
            const i = s.i, n = lay.current.poly.length, start = e.latlng;
            lay.current.edgeOrigin = { a: lay.current.poly[i].slice(), b: lay.current.poly[(i + 1) % n].slice() };
            const wasDrag = m.dragging.enabled(); m.dragging.disable(); m.boxZoom.disable();
            const onMove = (ev) => { const ll = ev.latlng || m.mouseEventToLatLng(ev); liveEdgeMove(i, ll.lat - start.lat, ll.lng - start.lng); };
            const onUp = () => { m.off("mousemove", onMove); document.removeEventListener("mousemove", onDoc); document.removeEventListener("mouseup", onUp); commitEdge(i); lay.current.edgeOrigin = null; if (wasDrag) m.dragging.enable(); setTimeout(() => { draggingRef.current = false; }, 80); };
            const onDoc = (ev) => { const ll = m.mouseEventToLatLng(ev); liveEdgeMove(i, ll.lat - start.lat, ll.lng - start.lng); };
            m.on("mousemove", onMove); document.addEventListener("mousemove", onDoc); document.addEventListener("mouseup", onUp);
            return;
          }
          lpClear(); const ll = e.latlng; lpRef.current = setTimeout(() => openSideMenu(s.i, oe, ll), 480);
        });
        hit.on("touchstart", (e) => { const oe = e.originalEvent, ll = e.latlng; lpClear(); lpRef.current = setTimeout(() => openSideMenu(s.i, oe, ll), 480); });
        hit.on("mouseup", lpClear); hit.on("touchend", lpClear);
        const mid = [(s.a[0] + s.b[0]) / 2, (s.a[1] + s.b[1]) / 2];
        // the ft label is the wall's control: click=include, drag=add a point, right-click/hold=menu
        const lbl = add(L.marker(mid, { draggable: true, zIndexOffset: 700, icon: L.divIcon({ className: "", html: `<div class="seg-label grab ${s.selected ? "on" : ""}">${Math.round(s.lf)} ft</div>`, iconSize: [0, 0] }) }));
        let lblMoved = false;
        lbl.on("dragstart", () => { lblMoved = false; clearLPlbl(); draggingRef.current = true; lay.current.midDrag = { i: s.i, a: s.a.slice(), b: s.b.slice() }; });
        lbl.on("drag", (e) => {
          lblMoved = true; const ll = e.target.getLatLng(), md = lay.current.midDrag; if (!md) return;
          if (!lay.current.tempLine) { lay.current.tempLine = L.polyline([md.a, [ll.lat, ll.lng], md.b], { color: "#ffcf4d", weight: 4, dashArray: "6 5" }).addTo(m); lay.current.items.push(lay.current.tempLine); }
          else lay.current.tempLine.setLatLngs([md.a, [ll.lat, ll.lng], md.b]);
        });
        lbl.on("dragend", (e) => {
          const ll = e.target.getLatLng(), md = lay.current.midDrag;
          if (lay.current.tempLine) { m.removeLayer(lay.current.tempLine); lay.current.tempLine = null; }
          lay.current.midDrag = null;
          if (md && lblMoved) insertCornerAt(md.i, [ll.lat, ll.lng]);
          setTimeout(() => { draggingRef.current = false; }, 80);
        });
        var clearLPlbl = () => { if (lpRef.current) { clearTimeout(lpRef.current); lpRef.current = null; } };
        lbl.on("click", (e) => { L.DomEvent.stop(e); if (!lblMoved) { setActiveSide(s.i); toggleSide(s.i); } });
        lbl.on("contextmenu", (e) => { L.DomEvent.stop(e); openSideMenu(s.i, e.originalEvent); });
        lbl.on("mousedown", (e) => { const oe = e.originalEvent; clearLPlbl(); lpRef.current = setTimeout(() => { if (!draggingRef.current) openSideMenu(s.i, oe); }, 480); });
        lbl.on("touchstart", (e) => { const oe = e.originalEvent; clearLPlbl(); lpRef.current = setTimeout(() => { if (!draggingRef.current) openSideMenu(s.i, oe); }, 480); });
        lbl.on("mouseup", clearLPlbl); lbl.on("touchend", clearLPlbl);
        lay.current.sideLabels[s.i] = lbl;
      });
      // gates: draw at real width with a swing arc, plus a draggable center handle
      lay.current.gateMarks = {};
      (parcel.gates || []).forEach((g) => {
        const a = parcel.polygon[g.side], b = parcel.polygon[(g.side + 1) % pn]; if (!a || !b) return;
        const pos = [a[0] + (b[0] - a[0]) * g.t, a[1] + (b[1] - a[1]) * g.t];
        const geo = gateGeometry(a, b, g.t, g.width, g.swing || 0);
        // opening span (gap), swing arc + leaf, hinge dot
        add(L.polyline([geo.p1, geo.p2], { color: "#0a0e16", weight: 9, opacity: 0.9, interactive: false }));
        add(L.polyline([geo.p1, geo.p2], { color: "#f5c451", weight: 3, dashArray: "2 4", opacity: 0.9, interactive: false }));
        add(L.polyline(geo.arc, { color: "#f5c451", weight: 2, dashArray: "4 4", opacity: 0.85, interactive: false }));
        add(L.polyline([geo.hinge, geo.leafEnd], { color: "#f5c451", weight: 3, opacity: 0.95, interactive: false }));
        add(L.circleMarker(geo.hinge, { radius: 3.5, color: "#f5c451", fillColor: "#f5c451", fillOpacity: 1, weight: 0, interactive: false }));
        const gm = add(L.marker(pos, { draggable: true, zIndexOffset: 1200, icon: L.divIcon({ className: "gate-wrap", html: `<div class="gate-mark" title="Drag to move · click to flip swing"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#06121a" stroke-width="2.4"><path d="M3 21V5l9-2 9 2v16M3 21h18M9 21v-7l3-1 3 1v7"/></svg></div>` }) }));
        gm.on("dragstart", () => { draggingRef.current = true; });
        gm.on("drag", (e) => moveGateLive(g.id, e.target.getLatLng()));
        gm.on("dragend", () => { commitGate(g.id); setTimeout(() => { draggingRef.current = false; }, 80); });
        gm.on("click", (e) => { L.DomEvent.stop(e); cycleGateSwing(g.id); });
        gm.on("contextmenu", (e) => { L.DomEvent.stop(e); removeGate(g.id); });
        lay.current.gateMarks[g.id] = gm;
      });
      // measured grade points: drop/rise shots along each selected run.
      // Drag slides a point along its run · click edits · right-click removes.
      lay.current.gpT = {};
      parcel.sides.forEach((s) => {
        if (!s.selected || !Array.isArray(s.profile) || !s.profile.length) return;
        const a = parcel.polygon[s.i], b = parcel.polygon[(s.i + 1) % pn]; if (!a || !b) return;
        s.profile.forEach((pt, idx) => {
          const pos = [a[0] + (b[0] - a[0]) * pt.t, a[1] + (b[1] - a[1]) * pt.t];
          const gp = add(L.marker(pos, { draggable: true, zIndexOffset: 1100, icon: L.divIcon({ className: "gp-wrap", html: `<div class="gp-mark${pt.deltaIn < 0 ? " drop" : ""}" title="Grade point — drag along the run · click to edit · right-click to remove"><span class="gp-dot"></span>${fmtIn(pt.deltaIn)}</div>`, iconSize: [0, 0] }) }));
          let gpMoved = false;
          const gpKey = s.i + ":" + idx;
          gp.on("dragstart", () => { gpMoved = false; draggingRef.current = true; });
          gp.on("drag", (e) => {   // constrain the drag to the run (like gates)
            gpMoved = true;
            const poly = lay.current.poly || parcel.polygon;
            const pa = poly[s.i], pb = poly[(s.i + 1) % poly.length];
            const ll = e.target.getLatLng();
            const B = toXY(pb, pa), C = toXY([ll.lat, ll.lng], pa);
            const len2 = (B[0] * B[0] + B[1] * B[1]) || 1;
            const t = Math.max(0.01, Math.min(0.99, (C[0] * B[0] + C[1] * B[1]) / len2));
            lay.current.gpT[gpKey] = t;
            e.target.setLatLng([pa[0] + (pb[0] - pa[0]) * t, pa[1] + (pb[1] - pa[1]) * t]);
          });
          gp.on("dragend", () => { const t = lay.current.gpT[gpKey]; if (gpMoved && t != null) updateGradePoint(s.i, idx, { t }); setTimeout(() => { draggingRef.current = false; }, 80); });
          gp.on("click", (e) => { L.DomEvent.stop(e); if (gpMoved) return; const oe = e.originalEvent; setGradePop({ i: s.i, idx, t: pt.t, deltaIn: pt.deltaIn, x: oe ? oe.clientX : window.innerWidth / 2, y: oe ? oe.clientY : window.innerHeight / 2 }); });
          gp.on("contextmenu", (e) => { L.DomEvent.stop(e); removeGradePoint(s.i, idx); flash("Grade point removed"); });
        });
      });
      // absolute corner elevations (parcel.cornerElev): a chip above each set
      // corner — "BASE 0" / "−72″". Click edits · right-click clears.
      {
        const ceMap = parcel.cornerElev || {};
        parcel.polygon.forEach((v, idx) => {
          const val = +ceMap[idx];
          if (!isFinite(val)) return;
          const html = `<div class="gp-mark${val < 0 ? " drop" : ""}" style="transform:translate(-50%,-180%);cursor:pointer" title="Corner elevation vs base — click to edit · right-click to clear"><span class="gp-dot"></span>${val === 0 ? "BASE 0" : fmtIn(val)}</div>`;
          const mk = add(L.marker(v, { interactive: true, keyboard: false, zIndexOffset: 1300, icon: L.divIcon({ className: "gp-wrap", html, iconSize: [0, 0] }) }));
          mk.on("click", (e) => { L.DomEvent.stop(e); const oe = e.originalEvent; setCornerElevPop({ idx, x: oe ? oe.clientX : window.innerWidth / 2, y: oe ? oe.clientY : window.innerHeight / 2 }); });
          mk.on("contextmenu", (e) => { L.DomEvent.stop(e); setCornerElev(idx, null); });
        });
      }
      // optional: show every post position from the product's panel spacing (skipping gate openings)
      if (showPosts) {
        const pw = (product && product.panelWidth) || 8;
        parcel.sides.forEach(s => {
          if (!s.selected) return;
          const Lf = segFeet(s.a, s.b), n = Math.max(1, Math.round(Lf / pw));
          const gspans = (parcel.gates || []).filter(g => g.side === s.i).map(g => { const h = (g.width / 2) / Lf; return [g.t - h, g.t + h]; });
          const inGate = (t) => gspans.some(([lo, hi]) => t > lo + 1e-3 && t < hi - 1e-3);
          const post = (t, gate) => { const pp = [s.a[0] + (s.b[0] - s.a[0]) * t, s.a[1] + (s.b[1] - s.a[1]) * t]; add(L.circleMarker(pp, { radius: gate ? 4 : 3.2, color: "#06121a", weight: 1.5, fillColor: gate ? "#f5c451" : "#2ff3d6", fillOpacity: 1, interactive: false })); };
          for (let k = 0; k <= n; k++) { const t = k / n; if (!inGate(t)) post(t, false); }
          gspans.forEach(([lo, hi]) => { if (lo > 0.005 && lo < 0.995) post(lo, true); if (hi > 0.005 && hi < 0.995) post(hi, true); });
        });
      }
      parcel.polygon.forEach((v, idx) => {
        const B = parcel.polygon[(idx - 1 + pn) % pn], D = parcel.polygon[(idx + 1) % pn];
        const sq = pn >= 3 && Math.abs(cornerAngle(B, v, D) - 90) <= 3.5;
        const h = add(L.marker(v, { draggable: true, zIndexOffset: 1000, icon: L.divIcon({ className: "vtx-wrap", html: `<div class="vtx gold${sq ? " sq" : ""}${idx === activeCorner ? " sel" : ""}"></div>`, iconSize: [30, 30], iconAnchor: [15, 15] }) }));
        const clearLP = () => { if (lpRef.current) { clearTimeout(lpRef.current); lpRef.current = null; } };
        h.on("dragstart", () => {
          clearLP();
          draggingRef.current = true;
          const poly = lay.current.poly, n = poly.length;
          if (lay.current.downMeta) { lay.current.clone = { idx, origPos: poly[idx].slice(), pos: null }; }
          else { lay.current.dragOrigin = { anchor: poly[(idx - 1 + n) % n].slice(), orig: poly[idx].slice() }; }
        });
        h.on("drag", (e) => {
          const cl = lay.current.clone;
          if (cl && cl.idx === idx) {
            const ll = e.target.getLatLng();
            h.setLatLng(cl.origPos);            // pin the source corner
            cl.pos = [ll.lat, ll.lng];
            if (!lay.current.tempLine) { lay.current.tempLine = L.polyline([cl.origPos, cl.pos], { color: "#ffcf4d", weight: 4, dashArray: "6 5" }).addTo(m); lay.current.items.push(lay.current.tempLine); }
            else lay.current.tempLine.setLatLngs([cl.origPos, cl.pos]);
            return;
          }
          liveMove(idx, e.target.getLatLng());
        });
        h.on("dragend", () => {
          const cl = lay.current.clone;
          if (cl && cl.idx === idx) {
            if (lay.current.tempLine) { m.removeLayer(lay.current.tempLine); lay.current.tempLine = null; }
            lay.current.clone = null;
            if (cl.pos) insertCornerAt(idx, cl.pos);
            setTimeout(() => { draggingRef.current = false; }, 80); return;
          }
          const p = lay.current.poly[idx]; commitVertex(idx, { lat: p[0], lng: p[1] }); lay.current.dragOrigin = null;
          if (squareDragRef.current) setTimeout(() => squareCornerAt(idx), 0);
          setTimeout(() => { draggingRef.current = false; }, 80);
        });
        h.on("click", (e) => { L.DomEvent.stop(e); if (!draggingRef.current) { setActiveCorner(idx); setActiveSide(null); } });   // select this corner (Delete removes it)
        h.on("contextmenu", (e) => { L.DomEvent.stop(e); openCornerMenu(idx, e.originalEvent); });
        h.on("mousedown", (e) => { const oe = e.originalEvent; lay.current.downMeta = oe.metaKey || oe.ctrlKey; lay.current.downShift = oe.shiftKey; shiftRef.current = oe.shiftKey; clearLP(); lpRef.current = setTimeout(() => { if (!draggingRef.current) openCornerMenu(idx, oe); }, 480); });
        h.on("touchstart", (e) => { const oe = e.originalEvent; lay.current.downMeta = false; clearLP(); lpRef.current = setTimeout(() => { if (!draggingRef.current) openCornerMenu(idx, oe); }, 480); });
        h.on("mouseup", clearLP); h.on("touchend", clearLP); h.on("move", clearLP);
        lay.current.handles[idx] = h;
      });
    }
    if (manual && mVerts.length) {
      if (mVerts.length > 1) { lay.current.fill = add(L.polyline(mVerts.map(p => p.slice()), { color: "#2ff3d6", weight: 4 })); lay.current.poly = mVerts.map(p => p.slice()); }
      mVerts.forEach((v, idx) => {
        const h = add(L.marker(v, { draggable: true, zIndexOffset: 1000, icon: L.divIcon({ className: "vtx-wrap", html: `<div class="vtx"></div>`, iconSize: [28, 28], iconAnchor: [14, 14] }) }));
        h.on("dragstart", () => { draggingRef.current = true; });
        h.on("drag", (e) => { const ll = e.target.getLatLng(); const poly = lay.current.poly; if (poly) { poly[idx] = [ll.lat, ll.lng]; if (lay.current.fill) lay.current.fill.setLatLngs(poly); } });
        h.on("dragend", (e) => { const ll = e.target.getLatLng(); setMVerts(p => p.map((q, k) => k === idx ? [ll.lat, ll.lng] : q)); setTimeout(() => { draggingRef.current = false; }, 80); });
      });
      for (let i = 1; i < mVerts.length; i++) {
        const a = mVerts[i - 1], b = mVerts[i], mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
        add(L.marker(mid, { interactive: false, icon: L.divIcon({ className: "", html: `<div class="seg-label on">${Math.round(segFeet(a, b))} ft</div>`, iconSize: [0, 0] }) }));
      }
    }
  }, [parcel, manual, mVerts, activeSide, activeCorner, showPosts, productId, showPropLine, showHouse]);

  function snapshot() { return { linearFeet: lf, runs, closed: closedLoop, gradePct, parcel, manual, mVerts, productId, corners, gates: gateCount, gateSizes, removal, clearingHours, slope, rockClause, permit, jchannel: isVinyl && jchannel, travelMiles, customer, quoteNotes, siteVisitRequired, rep: repName || undefined, ...addonAdj }; }
  function restore(s) {
    // tolerant of partial snapshots (a quote's inputs may lack fields the
    // estimator later grew) — every missing key falls back to its default
    setParcel(s.parcel || null); setManual(!!s.manual); setMVerts(s.mVerts || []);
    if (s.productId) setProductId(s.productId);
    setCorners(Number(s.corners) || 0); setGates(Number(s.gates) || 0);
    setRemoval(s.removal || "none"); setClearingHours(Number(s.clearingHours) || 0); setSlope(!!s.slope); setRockClause(!!s.rockClause); setPermit(!!s.permit); setJchannel(!!s.jchannel); setTravelMiles(Number(s.travelMiles) || 0);
    setSlopeAdj(s.slopeAdj && isFinite(+s.slopeAdj.value) ? { mode: s.slopeAdj.mode === "flat" ? "flat" : "pct", value: String(s.slopeAdj.value) } : { mode: "pct", value: "" });
    setPermitFeeVal(s.permitFee != null ? String(s.permitFee) : "");
    setUchannelCostVal(s.uchannelCost != null ? String(s.uchannelCost) : "");
    setRockNoteVal(s.rockNote || "");
    setCustomer(s.customer || customer); setQuoteNotes(s.quoteNotes || ""); setSiteVisitRequired(!!s.siteVisitRequired);
    if (s.rep) setRepName(s.rep);   // keep the original rep's credit when reopening (blank = current user via the auth effect)
    const poly = s.parcel ? s.parcel.polygon : s.mVerts;
    if (poly && poly.length && map.current) setTimeout(() => { const mm = map.current; if (!mm) return; try { mm.fitBounds(poly, { padding: [80, 80] }); } catch { /* unmounted before the fit */ } }, 120);
  }
  /* ---- multiple fences on one estimate ---- */
  function addFence() {
    if (lf < 1) { flash("Draw a fence first, then add another"); return; }
    setFences(fs => [...fs, { id: "f" + Date.now(), name: "Fence " + (fs.length + 1), snapshot: snapshot() }]);
    setParcel(null); setMVerts([]); setManual(false); setActiveSide(null);
    flash("Saved. Search/draw the next fence — it adds to the same estimate.");
  }
  function renameFence(id, name) { setFences(fs => fs.map(f => f.id === id ? { ...f, name } : f)); }
  function deleteFence(id) { setFences(fs => fs.filter(f => f.id !== id)); flash("Fence removed"); }
  function editFence(id) {
    const f = fences.find(x => x.id === id); if (!f) return;
    const carry = lf >= 1 ? [{ id: "f" + Date.now(), name: "Fence " + (fences.length + 1), snapshot: snapshot() }] : [];
    restore(f.snapshot);
    setFences(fs => fs.filter(x => x.id !== id).concat(carry));
    flash("Editing " + f.name);
  }
  // turn a hand-drawn line into a fully-editable parcel (gold corners, side cards, gates, right-click)
  function promoteManualToParcel() {
    if (mVerts.length < 2) { flash("Click at least 2 points on the map first"); return; }
    const poly = mVerts.map(v => v.slice());
    let closed = false;
    if (poly.length >= 3 && segFeet(poly[0], poly[poly.length - 1]) < 15) { poly.pop(); closed = true; }
    setParcel({ polygon: poly, sides: buildSides(poly, null, closed), closed, owner: "Hand-drawn", parcelId: "—",
      address: customer.address || (parcel && parcel.address) || "Hand-drawn fence", provider: "Manual drawing", providerStatus: "manual", gates: [] });
    setManual(false); setMVerts([]); setMapLocked(true);
    flash("Now drag corners, right-click for actions, drop gates — just like a parcel.");
  }
  function saveProject() {
    if (grandLF < 1) { flash("Outline a property or draw a line first"); return; }
    const snaps = allFenceSnaps();
    const matchId = (editingProject && editingProject.id) || (params && params.loadProject);
    const existing = matchId && store.projects.find(p => p.id === matchId);
    const id = existing ? existing.id : newId("p");
    const job = existing ? (existing.job || (editingProject && editingProject.job)) : nextJobNumber(store.projects);
    store.addProject({ id, job, customer: customer.name, customerId: customer.customerId, address: customer.address || (parcel && parcel.address) || "Address not set",
      lf: grandLF, total: grandTotal, productName: snaps.length > 1 ? `${snaps.length} fences` : product.name, productId, rep: repName || (existing && existing.rep) || undefined,
      createdAt: (existing && existing.createdAt) || Date.now(), status: (existing && existing.status) || "draft", snapshot: { ...snapshot(), fences: snaps } });
    setEditingProject({ id, job });
    flash(existing ? "Updated " + job : "Saved as " + job);
  }
  async function deleteCurrentProject() {
    const matchId = (editingProject && editingProject.id) || (params && params.loadProject);
    const existing = matchId && store.projects.find(p => p.id === matchId);
    if (!existing) return;
    if (!(await confirmDialog({ title: `Delete ${existing.job || "this project"} (${existing.customer || "untitled"})?`, body: "This can't be undone.", confirmLabel: "Delete project", danger: true }))) return;
    store.deleteProject(existing.id);
    go("dashboard");
  }
  function sendQuote() {
    const snaps = allFenceSnaps();
    if (!snaps.length) { flash("Outline a property or draw a line first"); return; }
    const primary = snaps[snaps.length - 1].snapshot;
    const projId = (editingProject && editingProject.id) || (params && params.loadProject) || null;
    // reuse this estimate's existing quote so repeated sends don't pile up
    const existing = (params && params.quoteId && store.quotes.find(q => q.id === params.quoteId))
      || (projId && store.quotes.find(q => q.projectId === projId))
      || (sentQuoteId && store.quotes.find(q => q.id === sentQuoteId));
    const fields = {
      customer: customer.name || "New customer", customerId: customer.customerId, phone: customer.phone, email: customer.email,
      address: customer.address || (parcel && parcel.address), projectId: projId,
      lf: grandLF, total: grandTotal, productName: snaps.length > 1 ? `${snaps.length} fences` : product.name, productId,
      source: leadSource || undefined,
      rep: repName || (existing && existing.rep) || undefined,   // leaderboard credit — never clobber a prior rep with blank
      inputs: { ...primary, fences: snaps.length > 1 ? snaps : null, siteVisitRequired },
    };
    // Generating the estimate builds the quote and opens it — nothing is
    // emailed here. The real send (quote page → "Send to customer") is what
    // stamps sends[] via store.logQuoteSend, so the "sent ×N" counters and the
    // follow-up cadence only count messages the customer actually received.
    if (existing) {
      store.updateQuote(existing.id, fields);
      // regenerated under a typed (unlinked) name → upsert/link that customer so
      // the quote doesn't fall off the CRM; a picked customer is already linked
      if (!customer.customerId && (customer.name || customer.email) && store.linkQuoteCustomer)
        store.linkQuoteCustomer(existing.id, { name: customer.name, phone: customer.phone, email: customer.email, address: fields.address });
      store.addActivity(`Updated estimate for ${customer.name || "customer"} — ${E_money0(grandTotal)}`);
      setSentQuoteId(existing.id);
      go("quote", { quoteId: existing.id });
    } else {
      // plan cap: a NEW estimate past the month's allowance asks to upgrade.
      // The drawing stays on screen (and in the device draft) — Save keeps it
      // as a project; regenerating an existing estimate above never counts.
      if (estimateRoom(store).atLimit) { limitPrompt(store, go, "estimates"); return; }
      const id = newId("q");
      if (!store.addQuote({ id, ...fields, status: "pending", createdAt: Date.now() })) return;
      store.addActivity(`Estimate ready for ${customer.name || "customer"} — ${E_money0(grandTotal)}`);
      setSentQuoteId(id);
      go("quote", { quoteId: id });
    }
  }

  /* Phone: the side panel is a bottom sheet over a full-height map. Closed, only
     the price bar shows; "Details" (or a swipe up on the bar) opens the panel;
     the grab handle or a swipe down closes it. Desktop is untouched. */
  const sheetMode = useNarrowScreen(880);
  const [sheetOpen, setSheetOpen] = React.useState<any>(false);
  const swipeY = React.useRef<any>(null);
  const swipe = {
    onTouchStart: (e: any) => { swipeY.current = e.touches && e.touches[0] ? e.touches[0].clientY : null; },
    onTouchEnd: (e: any) => {
      const y0 = swipeY.current; swipeY.current = null;
      const y1 = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : null;
      if (y0 == null || y1 == null) return;
      if (y1 - y0 > 50) setSheetOpen(false); else if (y0 - y1 > 50) setSheetOpen(true);
    },
  };
  // the map fills whatever is left below the bars above it (top bar, and the
  // sandbox bar when there is one) — measured, because the bars' heights
  // differ by shell and by phone width
  const wrapRef = React.useRef<any>(null);
  React.useEffect(() => {
    const el = wrapRef.current; if (!el) return;
    if (!sheetMode) { el.style.height = ""; return; }
    const fit = () => { const top = el.getBoundingClientRect().top + (window.scrollY || 0); el.style.height = Math.max(320, window.innerHeight - top) + "px"; };
    fit();
    window.addEventListener("resize", fit); window.addEventListener("orientationchange", fit);
    const t = setTimeout(fit, 300);   // after the bars settle
    return () => { window.removeEventListener("resize", fit); window.removeEventListener("orientationchange", fit); clearTimeout(t); el.style.height = ""; };
  }, [sheetMode]);
  return (
    <div ref={wrapRef} className={"est-wrap" + (sheetMode ? " est-sheet-mode" + (sheetOpen ? " sheet-open" : "") : "")}>
      {/* EST-05: what you were in the middle of, offered back rather than lost */}
      {resume && !(lf > 0) && (
        <div className="draft-resume" role="status">
          <Icon name="undo" size={15} />
          <span><b>You were quoting {resume.label}</b> — {draftAge(resume.at)}, not sent. It's still here.</span>
          <span className="row gap-8" style={{ marginLeft: "auto" }}>
            <button className="btn btn-primary btn-sm" onClick={resumeDraft}><Icon name="undo" size={13} />Pick it back up</button>
            <button className="btn btn-ghost btn-sm" onClick={discardDraft}>Start fresh</button>
          </span>
        </div>
      )}
      <aside className="est-side">
        {sheetMode && (
          <button className="est-sheet-handle" aria-label="Close details" onClick={() => setSheetOpen(false)} {...swipe}>
            <span className="est-sheet-grip" /><span>Property, customer &amp; fence</span><Icon name="chevdown" size={14} />
          </button>
        )}
        {draftState !== "idle" && lf > 0 && !sentQuoteId && (
          <div className={"draft-badge " + draftState} title="Kept on this device so an interruption doesn't lose it. Generating the estimate is what stores it for good.">
            <span className="draft-dot" />{DRAFT_LABEL[draftState]}
          </div>
        )}
        <div className="est-side-scroll">
          {/* PROPERTY */}
          <Section icon="mappin" title="Property">
            {parcel && !manual ? (
              <div>
                <div className="row between" style={{ gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, minWidth: 0 }}>{parcel.address || "Dropped pin"}</div>
                  <ParcelBadge status={parcel.providerStatus} />
                </div>
                <div className="muted mono" style={{ fontSize: 11.5, marginTop: 3 }}>Parcel {parcel.parcelId} · {parcel.owner}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Perimeter <b style={{ color: "var(--fg-2)" }}>{Math.round(parcel.sides.reduce((t, s) => t + s.lf, 0))} ft</b> · {parcel.sides.length} sides · <b style={{ color: "var(--teal)" }}>{selCount} selected</b>
                </div>
                <div className="mono faint" style={{ fontSize: 10.5, marginTop: 6 }}>{parcel.provider}</div>
              </div>
            ) : (
              <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
                Search an address or <b style={{ color: "var(--fg-2)" }}>click any property</b> on the satellite view — we auto-load its parcel boundary.
              </p>
            )}
            <div className="row gap-8" style={{ marginTop: 10, flexWrap: "wrap" }}>
              <button className={"btn btn-sm " + (manual ? "btn-primary" : "btn-ghost")} onClick={() => { setManual(m => !m); if (!manual) setParcel(null); }}>
                <Icon name="pen" size={13} />{manual ? "Manual draw on" : "Draw Manually"}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={undo} disabled={hist.current.idx <= 0} title="Undo (⌘/Ctrl+Z)"><Icon name="undo" size={13} />Undo</button>
              <button className="btn btn-ghost btn-sm" onClick={redo} disabled={hist.current.idx >= hist.current.stack.length - 1} title="Redo (⌘/Ctrl+Shift+Z)"><Icon name="undo" size={13} style={{ transform: "scaleX(-1)" }} />Redo</button>
              {parcel && !manual && <button className={"btn btn-sm " + (mapLocked ? "btn-outline" : "btn-ghost")} onClick={() => setMapLocked(l => !l)} title="Lock the map so stray clicks don't replace the parcel"><Icon name="lock" size={13} />{mapLocked ? "Map Locked" : "Lock Map"}</button>}
              {manual && <button className="btn btn-ghost btn-sm" onClick={() => setMVerts(v => v.slice(0, -1))} disabled={!mVerts.length}><Icon name="undo" size={13} />Last</button>}
              {manual && <button className="btn btn-ghost btn-sm" onClick={() => setMVerts([])} disabled={!mVerts.length}><Icon name="trash" size={13} />Clear</button>}
              {manual && mVerts.length >= 2 && <button className="btn btn-primary btn-sm" onClick={promoteManualToParcel}><Icon name="check" size={13} />Finish &amp; edit</button>}
            </div>
            {/* no address yet (a walk-up, a text with a photo): the yard preview
                renders the fence on their photo now; the parcel can follow */}
            {!parcel && !manual && go && (
              <button className="est-photo-link" onClick={() => go("visualizer", { from: "estimator" })}><Icon name="image" size={13} />No address yet? Start from a photo of the yard<Icon name="chevright" size={12} /></button>
            )}
            {/* the resolver couldn't reach a live parcel layer — the drawn box is a
                synthetic placeholder, NOT the surveyed lot. Say so loudly and make
                retry one tap (blips at the free statewide layer are common). */}
            {parcel && !manual && parcel.live === false && (
              <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, background: "var(--red-tint)", border: "1px solid var(--red)", display: "grid", gap: 8 }}>
                <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                  <b style={{ color: "var(--red)" }}>⚠ Placeholder outline — not the real lot.</b>{" "}
                  {parcel.noCoverage
                    ? "The parcel layer answered but has no record at this point."
                    : "The live parcel service didn't answer (free layers rate-limit sometimes)."}{" "}
                  Don't trust these lines{parcel.owner && parcel.parcelId && String(parcel.parcelId).startsWith("EST-") ? " — the owner/parcel shown are sample data too" : ""}.
                </div>
                {parcel.error && <div className="mono" style={{ fontSize: 10.5, color: "var(--fg-3)", wordBreak: "break-word" }}>service said: {String(parcel.error).slice(0, 160)}</div>}
                <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                  <button className="btn btn-primary btn-sm" onClick={() => { const qp = parcel.queryPoint || parcel.center; if (qp) loadParcel(qp[0], qp[1], parcel.queryAddress || parcel.address); }}>
                    <Icon name="undo" size={13} />Retry live lookup
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setManual(true); setParcel(null); }}><Icon name="pen" size={13} />Draw manually instead</button>
                </div>
              </div>
            )}
            {parcel && !manual && <div className="faint" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>Drag a gold corner to reshape · <b style={{ color: "var(--fg-3)" }}>drag the ft label</b> to add a point · <b style={{ color: "var(--fg-3)" }}>Shift-drag a wall</b> to slide it · <b style={{ color: "var(--fg-3)" }}>right-click / hold</b> a corner or the ft label for actions · <b style={{ color: "var(--fg-3)" }}>Delete</b> removes the selected wall · <b style={{ color: "var(--fg-3)" }}>double-click a wall</b> to drop a gate.</div>}
          </Section>

          {/* FENCES on this estimate */}
          {(fences.length > 0 || lf >= 1) && (
            <Section icon="layers" title="Fences on this Estimate" right={fences.length > 0 ? <span className="display tnum" style={{ fontSize: 16, color: "var(--teal)" }}>{E_money0(grandTotal)}</span> : null}>
              <div style={{ display: "grid", gap: 6 }}>
                {fences.map((f, i) => (
                  <div key={f.id} className="fence-row">
                    <span className="fence-dot" style={{ background: FENCE_COLORS[i % FENCE_COLORS.length] }} />
                    <input className="fence-name" value={f.name} onChange={e => renameFence(f.id, e.target.value)} />
                    <span className="tnum mono faint" style={{ fontSize: 11 }}>{Math.round(savedQuotes[i] ? savedQuotes[i].q.LF : 0)} ft</span>
                    <button className="fence-mini" onClick={() => editFence(f.id)} title="Load back to edit"><Icon name="edit" size={12} /></button>
                    <button className="fence-mini del" onClick={() => deleteFence(f.id)} title="Remove fence"><Icon name="trash" size={12} /></button>
                  </div>
                ))}
                <div className="fence-row active">
                  <span className="fence-dot" style={{ background: FENCE_COLORS[fences.length % FENCE_COLORS.length], boxShadow: "0 0 0 2px rgba(255,255,255,.15)" }} />
                  <span className="fence-name" style={{ fontWeight: 700 }}>{activeName} <span className="faint" style={{ fontWeight: 500 }}>· editing</span></span>
                  <span className="tnum mono" style={{ fontSize: 11, color: "var(--teal)" }}>{Math.round(lf)} ft</span>
                </div>
              </div>
              <button className="btn btn-ghost btn-sm btn-block" style={{ marginTop: 8 }} onClick={addFence} disabled={lf < 1}><Icon name="plus" size={13} />Save &amp; start another fence</button>
              <p className="faint" style={{ fontSize: 10.5, marginTop: 6, lineHeight: 1.4 }}>Each fence keeps its own product, runs &amp; gates. They're grouped on the quote with a combined total.</p>
            </Section>
          )}

          {/* CUSTOMER */}
          <Section icon="user" title="Customer & Job Info">
            <CustomerPicker store={store} customer={customer} setCustomer={setCustomer} />
            {pastEstimates.length > 0 && (
              <div style={{ margin: "8px 0 0", padding: "10px 12px", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 12 }}>
                <div className="kicker" style={{ marginBottom: 8 }}>Past estimates · {pastEstimates.length}</div>
                <div style={{ display: "grid", gap: 6 }}>
                  {pastEstimates.slice(0, 6).map(e => (
                    <div key={e.key} className="row between" style={{ gap: 8, padding: "6px 9px", borderRadius: 8, background: "var(--surface)", border: "1px solid var(--line)" }}>
                      <button className="row gap-8" style={{ minWidth: 0, flex: 1, background: "none", border: 0, cursor: "pointer", textAlign: "left", padding: 0 }}
                        onClick={() => e.quoteId ? go("quote", { quoteId: e.quoteId }) : go("estimator", { loadProject: e.projectId })}>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: "block", fontWeight: 600, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.label} · {E_money0(e.total)}</span>
                          <span className="muted" style={{ fontSize: 11, textTransform: "capitalize" }}>{e.at ? new Date(e.at).toLocaleDateString() : ""} · {e.status}</span>
                        </span>
                      </button>
                      <span className="row gap-4" style={{ flexShrink: 0 }}>
                        {e.projectId && <button className="btn btn-ghost btn-sm" title="Reopen in estimator" onClick={() => go("estimator", { loadProject: e.projectId })}><Icon name="edit" size={12} /></button>}
                        {e.quoteId && <button className="btn btn-ghost btn-sm" title="View quote" onClick={() => go("quote", { quoteId: e.quoteId })}><Icon name="clipboard" size={12} /></button>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              <input className="input" placeholder="Customer name" value={customer.name} onChange={e => setCustomer({ ...customer, name: e.target.value, customerId: undefined, _fromParcel: false })} />
              <div className="row gap-10">
                <input className="input" placeholder="Phone" value={customer.phone} onChange={e => setCustomer({ ...customer, phone: e.target.value })} />
                <input className="input" placeholder="Email" value={customer.email} onChange={e => setCustomer({ ...customer, email: e.target.value })} />
              </div>
              <input className="input" placeholder="Street address" value={customer.street || ""} onChange={e => setCustomer(c => { const n = { ...c, street: e.target.value }; return { ...n, address: composeAddress(n) || c.address || "" }; })} />
              <div className="row gap-10">
                <input className="input" placeholder="City" value={customer.city || ""} style={{ flex: 2 }} onChange={e => setCustomer(c => { const n = { ...c, city: e.target.value }; return { ...n, address: composeAddress(n) || c.address || "" }; })} />
                <input className="input" placeholder="State" value={customer.state || ""} style={{ flex: 1 }} maxLength={2} onChange={e => setCustomer(c => { const n = { ...c, state: e.target.value.toUpperCase() }; return { ...n, address: composeAddress(n) || c.address || "" }; })} />
                <input className="input" placeholder="ZIP" value={customer.zip || ""} style={{ flex: 1 }} inputMode="numeric" onChange={e => setCustomer(c => { const n = { ...c, zip: e.target.value }; return { ...n, address: composeAddress(n) || c.address || "" }; })} />
              </div>
              <div className="row gap-10">
                <div style={{ flex: 1.2, minWidth: 0 }}>
                  <label className="field-label">Lead source — how did they find you?</label>
                  <select className="select" value={leadSource} onChange={e => setLeadSource(e.target.value)}>
                    {[["", "Unknown / not set"], ["referral", "Referral / word of mouth"], ["google", "Google / search / LSA"], ["website", "Website / online estimate"], ["social", "Facebook / Instagram"], ["repeat", "Repeat customer"], ["yardsign", "Yard sign / drive-by"], ["homeshow", "Home show / event"], ["other", "Other"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label className="field-label" title="Who gets credit on the dashboard sales leaderboard">Sales rep</label>
                  <input className="input" list="fw-rep-options" placeholder="Rep name" value={repName} onChange={e => setRepName(e.target.value)} />
                  <datalist id="fw-rep-options">{repOptions.map(r => <option key={r} value={r} />)}</datalist>
                </div>
              </div>
              <button className={"toggle-row" + (siteVisitRequired ? " on" : "")} onClick={() => setSiteVisitRequired(v => !v)} style={{ width: "100%" }}>
                <span className={"sw" + (siteVisitRequired ? " on" : "")}><span className="knob" /></span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Remote estimate — site visit required</span>
                <span className="muted" style={{ fontSize: 11, marginLeft: "auto" }}>confirm clearing/slope on-site</span>
              </button>
              <textarea className="textarea" placeholder="Quote notes — warranty, timeline, site prep (customer sees these)" value={quoteNotes} onChange={e => setQuoteNotes(e.target.value)} />
            </div>
          </Section>

          {/* PRODUCT */}
          <Section icon="box" title="Default Fence Type">
            <select className="select" value={productId} onChange={e => setProductId(e.target.value)}>
              {window.VFP.PRODUCTS.map(p => <option key={p.id} value={p.id}>{p.name} — {p.sub}</option>)}
            </select>
            <div className="row gap-12" style={{ marginTop: 12 }}>
              {parcel && !manual
                ? <div style={{ flex: 1 }}>
                    <label className="field-label">Gates</label>
                    <div className="gate-count-box"><span className="display tnum" style={{ fontSize: 17, color: gateCount ? "var(--teal)" : "var(--fg-3)" }}>{gateCount}</span><span className="muted" style={{ fontSize: 11.5 }}>placed on drawing — dbl-click a side or “+ Gate”</span></div>
                  </div>
                : <Stepper label="Gates" value={gates} set={setGates} />}
              <Stepper label="Corners" value={corners} set={setCorners} />
            </div>
          </Section>

          {/* FENCE RUNS */}
          {parcel && !manual && (
            <Section icon="ruler" title="Fence Runs" right={<span className="display tnum" style={{ fontSize: 18, color: "var(--teal)" }}>{Math.round(lf)} ft</span>}>
              <div className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>Click a side to include it · drag gold corners to adjust · drag the teal center dot (or arrow keys) to move the whole fence · split, set exact length &amp; grade per run.</div>
              {parcel.rawPolygon && (
                <>
                  <div className="square-row" style={{ flexWrap: "wrap" }}>
                    <button className={"btn btn-sm " + (showPropLine ? "btn-outline" : "btn-ghost")} onClick={() => setShowPropLine(v => !v)} title="Show the true county lot line as a faint reference under the fence (L)"><Icon name="mappin" size={13} />Property line</button>
                    <button className={"btn btn-sm " + (boxSelect ? "btn-outline" : "btn-ghost")} onClick={() => setBoxSelect(v => !v)} title="Drag a box on the map to delete every corner inside it (B)"><Icon name="trash" size={13} />Box-delete</button>
                    <button className="btn btn-ghost btn-sm est-keys-btn" onClick={() => setShowKeys(true)} title="Keyboard shortcuts (?)"><Icon name="sliders" size={13} />Keys</button>
                  </div>
                  {boxSelect && <p className="faint" style={{ fontSize: 10.5, margin: "0 0 4px", color: "var(--amber)", lineHeight: 1.4 }}>Box-delete on — drag a rectangle over the corners to remove them. Map pan is paused; press <b style={{ color: "var(--fg-3)" }}>V</b> (or the button) to go back to selecting.</p>}
                  <div className="square-row" style={{ flexWrap: "wrap" }}>
                    <span className="faint" style={{ fontSize: 11, fontWeight: 700, marginRight: 2 }}>Shape</span>
                    <button className="btn btn-ghost btn-sm" onClick={() => relayout("square")} title="Straighten jagged runs to the lot's axis, keeping its real shape (Q)"><Icon name="ruler" size={13} />Square up</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => fitToLotLine()} title="Slide each wall onto the lot line it runs along — walls far off the line (like a front run you moved up) stay where you put them (F · Shift+F fits just the highlighted wall)"><Icon name="mappin" size={13} />Fit to line</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => relayout("rectangle")} title="Snap to a clean bounding rectangle"><Icon name="box" size={13} />Rectangle</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => relayout("cleanup")} title="Re-lay cleanly from the property line (undo manual edits &amp; gates)"><Icon name="undo" size={13} />Reset</button>
                  </div>
                  <div className="square-row" style={{ flexWrap: "wrap" }}>
                    <span className="faint" style={{ fontSize: 11, fontWeight: 700, marginRight: 2 }}>House</span>
                    <button className="btn btn-ghost btn-sm" onClick={() => detectHouseFor(parcel.rawPolygon || parcel.polygon)} disabled={houseBusy} title="Auto-detect the house footprint (OpenStreetMap, then imagery)">
                      {houseBusy ? <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> : <Icon name="search" size={13} />}{parcel.house ? "Re-detect" : "Detect house"}
                    </button>
                    {parcel.house && <button className="btn btn-ghost btn-sm" onClick={fitToHouse} title="Tie the fence's ends / front wall into the house sides, halfway up — every other corner stays where you put it (H)"><Icon name="mappin" size={13} />Fit to house</button>}
                    {parcel.house && <button className="btn btn-ghost btn-sm" onClick={backyardLayout} title="Re-lay the WHOLE backyard from scratch off the lot line + house — replaces your current shape"><Icon name="map" size={13} />Backyard</button>}
                    {parcel.house && <button className={"btn btn-sm " + (showHouse ? "btn-outline" : "btn-ghost")} onClick={() => setShowHouse(v => !v)} title="Show / hide the house outline">Show</button>}
                    {parcel.house && <button className="btn btn-ghost btn-sm" onClick={() => setParcel(p => ({ ...p, house: null, houseSource: null }))} title="Clear the detected house"><Icon name="trash" size={13} /></button>}
                  </div>
                  {parcel.house && <p className="faint" style={{ fontSize: 10.5, margin: "0 0 4px", lineHeight: 1.4 }}>{parcel.houseSource === "cv" ? "House estimated from imagery — drag to fit." : parcel.houseSource === "usa" ? "House footprint from USA Structures (FEMA)." : "House from OpenStreetMap."} <b style={{ color: "var(--fg-3)" }}>Fit to house (H)</b> ties just the fence ends into the house, keeping your corners · <b style={{ color: "var(--fg-3)" }}>Backyard</b> re-lays the whole shape.</p>}
                  <div className="square-row" style={{ flexWrap: "wrap" }}>
                    <span className="faint" style={{ fontSize: 11, fontWeight: 700, marginRight: 2 }}>Set back</span>
                    <button className={"btn btn-sm " + (setbackScope === "all" ? "btn-outline" : "btn-ghost")} onClick={() => setSetbackScope("all")} title="Apply the setback to every side">All sides</button>
                    <button className={"btn btn-sm " + (setbackScope === "side" ? "btn-outline" : "btn-ghost")} onClick={() => setSetbackScope("side")} title="Apply only to the highlighted side — click the rear lot line first">Selected</button>
                    <div className="len-input" title={setbackScope === "side" ? "Move the highlighted wall this far inward" : "Inset the whole fence this far inside the property line"}>
                      <input className="tnum" type="number" min="0" max="50" step="0.5" placeholder="0" style={{ width: 50 }}
                        value={setbackStr} onChange={e => setSetbackStr(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); applySetback(setbackStr); } }} />
                      <span>ft{setbackScope === "side" ? " in" : " off line"}</span>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={() => applySetback(setbackStr)}>Apply</button>
                  </div>
                  <p className="faint" style={{ fontSize: 10.5, margin: "0 0 4px", lineHeight: 1.4 }}>
                    {setbackScope === "side"
                      ? <>Insets just the <b style={{ color: "var(--fg-3)" }}>highlighted</b> side (e.g. the rear lot line) — click a wall to pick it. 0.5 = 6&Prime;.</>
                      : <>Insets every side that far inside the lot line (0.5 = 6&Prime;). Pick <b style={{ color: "var(--fg-3)" }}>Selected</b> for the rear line only.</>}
                  </p>
                </>
              )}
              <div className="slope-disclaimer"><Icon name="bolt" size={12} style={{ color: "var(--teal)", flexShrink: 0 }} />Slope auto-detected from public elevation data (~terrain-level accuracy). Verify on-site for retaining walls, berms, or sharp drops.</div>
              <div className="square-row">
                <button className={"sq-toggle" + (squareDrag ? " on" : "")} onClick={() => setSquareDrag(v => !v)} title="When on, a corner snaps to 90° after you release it">
                  <span className={"sw" + (squareDrag ? " on" : "")}><span className="knob" /></span>
                  <Icon name="ruler" size={13} />Snap to 90° on release
                </button>
                <button className="btn btn-ghost btn-sm" onClick={squareAllCorners} title="Snap every corner to a right angle">Square all</button>
                <button className={"btn btn-sm " + (showPosts ? "btn-outline" : "btn-ghost")} onClick={() => setShowPosts(v => !v)} title="Show each post position from the product spacing"><Icon name="box" size={13} />Posts</button>
              </div>
              <div className="square-row" style={{ marginTop: -2, flexWrap: "wrap" }}>
                <span className="faint" style={{ fontSize: 11, fontWeight: 700, marginRight: 2 }}>Rotate</span>
                <button className="btn btn-ghost btn-sm" onClick={() => rotateParcel(-2)} title="Rotate −2°">−2°</button>
                <button className="btn btn-ghost btn-sm" onClick={() => rotateParcel(-0.5)} title="Rotate −0.5°">−0.5°</button>
                <button className="btn btn-ghost btn-sm" onClick={() => rotateParcel(-0.1)} title="Rotate −0.1°">−0.1°</button>
                <button className="btn btn-ghost btn-sm" onClick={() => rotateParcel(0.1)} title="Rotate +0.1°">+0.1°</button>
                <button className="btn btn-ghost btn-sm" onClick={() => rotateParcel(0.5)} title="Rotate +0.5°">+0.5°</button>
                <button className="btn btn-ghost btn-sm" onClick={() => rotateParcel(2)} title="Rotate +2°">+2°</button>
              </div>
              <div style={{ display: "grid", gap: 7 }}>
                {parcel.sides.map((s) => (
                  <div key={s.i} className={"side-card" + (s.selected ? " on" : "") + (s.i === activeSide ? " active" : "")} onClick={() => setActiveSide(s.i)} onContextMenu={(e) => { e.preventDefault(); openSideMenu(s.i, e); }}>
                    <button className="side-row" onClick={() => toggleSide(s.i)}>
                      <span className={"cbox" + (s.selected ? " on" : "")}>{s.selected && <Icon name="check" size={11} />}</span>
                      <span style={{ fontWeight: 600, fontSize: 13.5 }}>Side {s.i + 1} <span className="muted">({s.name}{s.suffix ? " — " + s.suffix : ""})</span></span>
                      <span className="tnum mono" style={{ marginLeft: "auto", fontSize: 12.5 }}>{Math.round(s.lf)} ft</span>
                    </button>
                    {s.selected &&
                      <div className="side-tools">
                        <div className="len-input" title="Type the exact run length">
                          <input className="tnum" type="number" min="1" defaultValue={Math.round(s.lf)} key={s.i + "-" + Math.round(s.lf)}
                            onBlur={e => setSideLength(s.i, e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); setSideLength(s.i, (e.target as HTMLInputElement).value); (e.target as HTMLInputElement).blur(); } }} />
                          <span>ft</span>
                        </div>
                        {isSplitPair(parcel, s.i)
                          ? <button className="side-btn merge" onClick={() => mergeSide(s.i)}><Icon name="undo" size={12} />Merge</button>
                          : <button className="side-btn" onClick={() => splitSide(s.i)}><Icon name="x" size={12} style={{ transform: "rotate(45deg)" }} />Split</button>}
                        <button className="side-btn gate" onClick={() => addGate(s.i)} title="Add a gate to this run"><Icon name="plus" size={12} />Gate</button>
                        <button className="side-btn del" onClick={() => deleteSide(s.i)} title="Delete this wall (opens the fence)"><Icon name="trash" size={12} />Delete</button>
                      </div>}
                    {s.selected && (() => {   // near-perfect fit: a small nudge lands this run on whole panels
                      const pw = (product && product.panelWidth) || 8;
                      const fit = panelFitSuggestion(s.lf, pw);
                      if (!fit) return null;
                      const ft1 = (v) => String(+(+v).toFixed(1));
                      return (
                        <div className="fit-hint">
                          <Icon name="bolt" size={12} style={{ color: "var(--teal)", flexShrink: 0 }} />
                          <span className="muted" style={{ fontSize: 11.5, minWidth: 0 }}>
                            {ft1(s.lf)} ft &rarr; {fit.dir} <b>{ft1(fit.delta)} ft</b> for {fit.panels} full {ft1(pw)}&prime; panel{fit.panels === 1 ? "" : "s"} (no cut)
                          </span>
                          <button className="fit-snap"
                            title={`Sets this run's field-verified length to ${ft1(fit.target)} ft (changes the run length, so panels, materials & price update like a manual length edit)`}
                            onClick={(e) => { e.stopPropagation(); setSideLength(s.i, fit.target); flash(`Side ${s.i + 1} snapped to ${ft1(fit.target)} ft (${fit.panels} full panels) — estimate updated`); }}>
                            Snap to fit &middot; {ft1(fit.target)} ft
                          </button>
                        </div>
                      );
                    })()}
                    {s.selected && gatesOnSide(s.i).length > 0 &&
                      <div className="gate-list">
                        {gatesOnSide(s.i).map((g, gi) => (
                          <span key={g.id} className="gate-chip">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M3 21V5l9-2 9 2v16M3 21h18M9 21v-7l3-1 3 1v7" /></svg>
                            Gate {gi + 1}
                            <select className="gate-w" value={g.width} onChange={e => setGateWidth(g.id, +e.target.value)} title="Gate width (scales on the map)">
                              {[3, 4, 5, 6, 10, 12, 16].map(w => <option key={w} value={w}>{w}′</option>)}
                            </select>
                            <button className="gate-swing" onClick={() => cycleGateSwing(g.id)} title="Flip swing direction"><Icon name="undo" size={11} style={{ transform: "scaleX(-1)" }} /></button>
                            <button className="gate-swing" onClick={() => splitPastGate(g)} title="Drop a point just past this gate (to delete a stub through the house)"><Icon name="plus" size={11} /></button>
                            <button onClick={() => removeGate(g.id)} title="Remove gate"><Icon name="x" size={11} /></button>
                          </span>
                        ))}
                      </div>}
                    {s.selected && s.autoSlope && s.autoSlope.status === "loading" &&
                      <div className="slope-auto"><span className="slope-spin"><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />Detecting slope…</span></div>}
                    {s.selected && s.autoSlope && s.autoSlope.status === "done" && (() => {
                      const col = s.autoSlope.tier === "steep" ? "var(--red)" : s.autoSlope.tier === "moderate" ? "var(--amber)" : "var(--green)";
                      const bg = s.autoSlope.tier === "steep" ? "var(--red-tint)" : s.autoSlope.tier === "moderate" ? "var(--amber-tint)" : "var(--green-tint)";
                      return (
                        <div className="slope-auto">
                          <span className="slope-dot" style={{ background: col }} />
                          <span className="slope-pill" style={{ color: col, background: bg }}>{Math.round(s.autoSlope.pct)}% · {s.autoSlope.label}</span>
                          <span className="slope-meta">+{s.autoSlope.riseEnds.toFixed(1)} ft / {Math.round(s.autoSlope.distFt)} ft</span>
                          <span className={"slope-tag-auto" + (s.gradeAuto ? "" : " slope-tag-manual")}>{s.gradeAuto ? "Auto" : "Manual"}</span>
                        </div>
                      );
                    })()}
                    {s.selected && s.autoSlope && s.autoSlope.status === "failed" &&
                      <div className="slope-auto"><span className="slope-meta">Couldn't auto-detect slope — set grade manually.</span></div>}
                    {s.selected &&
                      <div className="side-grade">
                        <span className="grade-lbl">Grade</span>
                        <select className="select grade-select" value={s.grade || "level"} onChange={e => setGrade(s.i, e.target.value)}>
                          {Object.entries(GRADES).map(([k, g]) => <option key={k} value={k}>{g.label}</option>)}
                        </select>
                        {s.grade && s.grade !== "level" && <span className="grade-tag">+{Math.round(GRADES[s.grade].factor * 100)}%</span>}
                      </div>}
                    {s.selected && (() => {
                      const prof = s.profile || [];
                      const mg = measuredGrade(prof, s.lf);
                      const open = !!gpOpen[s.i];
                      const measuredPricing = !!(settings && settings.quoting && settings.quoting.priceSlopeFromGrade);
                      const col = mg.tier === "steep" ? "var(--red)" : mg.tier === "moderate" ? "var(--amber)" : "var(--green)";
                      const bg = mg.tier === "steep" ? "var(--red-tint)" : mg.tier === "moderate" ? "var(--amber-tint)" : "var(--green-tint)";
                      return (
                        <div className="gp-box">
                          <button className="gp-head" onClick={(e) => { e.stopPropagation(); setGpOpen(o => ({ ...o, [s.i]: !o[s.i] })); }}>
                            <Icon name={open ? "chevdown" : "chevright"} size={12} />
                            <span>Grade points{prof.length ? ` · ${prof.length}` : ""}</span>
                            {prof.length >= 2 && <span className="gp-badge" style={{ color: col, background: bg }} title={measuredPricing ? "Measured grade — sets this run's slope surcharge" : "Display only — enable Settings → Quoting → Price slope from measured grade points"}>{+mg.pct.toFixed(1)}% · {mg.label}</span>}
                            {prof.length >= 2 && measuredPricing && <span className="gp-priced">priced</span>}
                          </button>
                          {open && (
                            <div className="gp-body">
                              {prof.length === 0 && <p className="faint" style={{ fontSize: 11, margin: 0, lineHeight: 1.45 }}>No measured points. Toggle <b style={{ color: "var(--fg-3)" }}>Grade points</b> on the map and click this run, or add by distance below. − = drop, + = rise (inches vs the previous point).</p>}
                              {prof.map((pt, idx) => (
                                <GradePointRow key={idx + ":" + pt.t + ":" + pt.deltaIn} pt={pt} lf={s.lf}
                                  onChange={(patch) => updateGradePoint(s.i, idx, patch)}
                                  onRemove={() => removeGradePoint(s.i, idx)} />
                              ))}
                              <GradePointAddRow lf={s.lf} onAdd={(t, deltaIn) => addGradePoint(s.i, t, deltaIn)} />
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
              <div className="row gap-8" style={{ marginTop: 10 }}>
                <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setAll(true)}>Select All</button>
                <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setAll(false)}>Deselect All</button>
              </div>
            </Section>
          )}
          {manual && (
            <Section icon="ruler" title="Measured Run" right={<span className="display tnum" style={{ fontSize: 18, color: "var(--teal)" }}>{Math.round(lf)} ft</span>}>
              <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>{mVerts.length < 2 ? "Click points on the map to trace the fence line." : `${mVerts.length - 1} segment${mVerts.length > 2 ? "s" : ""} drawn — hit `}{mVerts.length >= 2 && <b style={{ color: "var(--teal)" }}>Finish &amp; edit</b>}{mVerts.length >= 2 ? " to drag corners, drop gates & set grade per run. Close the loop by ending near your first point." : ""}</p>
            </Section>
          )}

          {/* ADD-ONS */}
          <Section icon="sliders" title="Add-ons & Tear-out">
            <label className="field-label">Old fence removal</label>
            <select className="select" value={removal} onChange={e => setRemoval(e.target.value)}>
              {Object.entries<any>(window.VFP.REMOVAL).map(([k, v]) => <option key={k} value={k}>{v.label}{v.rate ? ` — $${v.rate}/ft` : ""}</option>)}
            </select>
            <div className="row gap-12" style={{ marginTop: 12 }}>
              <div style={{ flex: 1 }}><label className="field-label">Clearing (hrs)</label><input className="input tnum" type="number" min="0" value={clearingHours} onChange={e => setClearingHours(Math.max(0, +e.target.value || 0))} /></div>
              <div style={{ flex: 1 }}><label className="field-label">Travel (miles)</label><input className="input tnum" type="number" min="0" value={travelMiles} onChange={e => setTravelMiles(Math.max(0, +e.target.value || 0))} /></div>
            </div>
            <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
              {(() => {
                // workspace defaults shown as placeholders — typing overrides them for THIS estimate only
                const st = store.settings || {};
                const defSlopePct = Math.round(((+st.slopePct) || 0.15) * 100);
                const defPermit = (+st.permitFee) || 100;
                const defUch = (product && product._editor && +product._editor.uchannelCost) || 4;
                const defRock = st.rockNote || "Quote assumes drivable soil. Subsurface rock encountered during install is billed at $75/post.";
                const slopeNote = slope && slopeAdj.value !== ""
                  ? (slopeAdj.mode === "flat" ? `+$${Math.max(0, +slopeAdj.value || 0)}` : `+${Math.max(0, +slopeAdj.value || 0)}%`)
                  : `+${defSlopePct}%`;
                const permitNote = "$" + (permit && permitFeeVal !== "" ? Math.max(0, +permitFeeVal || 0) : defPermit);
                return <>
                  {isVinyl && (
                    <ToggleAdj on={jchannel} set={setJchannel} label="U-channel post trim (premium)"
                      note={jchannel && uchannelCostVal !== "" ? `$${Math.max(0, +uchannelCostVal || 0)}/channel` : "clean edges"}>
                      <label className="field-label" style={{ margin: 0 }}>$ / channel</label>
                      <input className="input tnum" type="number" min="0" step="0.5" style={{ width: 90 }} value={uchannelCostVal}
                        placeholder={String(defUch)} onChange={e => setUchannelCostVal(e.target.value)} />
                      <span className="faint" style={{ fontSize: 11 }}>material each, marked up with labor · blank = ${defUch}</span>
                    </ToggleAdj>
                  )}
                  <ToggleAdj on={slope} set={setSlope} label="Slope / grade surcharge" note={slopeNote}>
                    <select className="select" style={{ width: 110 }} value={slopeAdj.mode}
                      onChange={e => setSlopeAdj((x: any) => ({ ...x, mode: e.target.value }))}>
                      <option value="pct">% uplift</option>
                      <option value="flat">$ flat</option>
                    </select>
                    <input className="input tnum" type="number" min="0" style={{ width: 90 }} value={slopeAdj.value}
                      placeholder={slopeAdj.mode === "flat" ? "e.g. 250" : String(defSlopePct)}
                      onChange={e => setSlopeAdj((x: any) => ({ ...x, value: e.target.value }))} />
                    <span className="faint" style={{ fontSize: 11 }}>{slopeAdj.mode === "flat" ? "fixed $ added to this quote" : `on the running total · blank = ${defSlopePct}%`}</span>
                  </ToggleAdj>
                  <ToggleAdj on={permit} set={setPermit} label="Permit fee" note={permitNote}>
                    <label className="field-label" style={{ margin: 0 }}>Fee $</label>
                    <input className="input tnum" type="number" min="0" style={{ width: 100 }} value={permitFeeVal}
                      placeholder={String(defPermit)} onChange={e => setPermitFeeVal(e.target.value)} />
                    <span className="faint" style={{ fontSize: 11 }}>passed through at cost · blank = ${defPermit}</span>
                  </ToggleAdj>
                  <ToggleAdj on={rockClause} set={setRockClause} label="Rock / obstruction clause" note="adds note">
                    <textarea className="input" rows={2} style={{ flex: "1 1 100%", fontSize: 12 }} value={rockNoteVal}
                      placeholder={defRock} onChange={e => setRockNoteVal(e.target.value)} />
                    <span className="faint" style={{ fontSize: 11 }}>printed on the quote · blank = the default wording above</span>
                  </ToggleAdj>
                </>;
              })()}
            </div>
          </Section>

        </div>

        {/* DOCK */}
        <div className="est-dock">
          {(() => {
            /* price sanity: this quote's $/ft against your own history for the
               same product (won jobs first, then everything). Quiet unless
               there are three comparables and the gap is 15% or more. */
            const cur = fences.length > 0 ? grandTotal : quote.customerTotal;
            if (!(lf > 0) || !(cur > 0)) return null;
            const hist = (store.quotes || []).filter((q: any) => q.productId === productId && Number(q.lf) > 0 && Number(q.total) > 0);
            const pool = hist.filter((q: any) => q.status === "signed" || q.status === "paid");
            const rates = (pool.length >= 3 ? pool : hist).map((q: any) => Number(q.total) / Number(q.lf)).sort((a: number, b: number) => a - b);
            if (rates.length < 3) return null;
            const med = rates[Math.floor(rates.length / 2)];
            const mine = cur / lf;
            const pct = Math.round(((mine - med) / med) * 100);
            if (Math.abs(pct) < 15) return null;
            return (
              <div className="price-check" role="status" title={"Your usual on this product: $" + med.toFixed(0) + "/ft across " + rates.length + (pool.length >= 3 ? " won jobs" : " quotes")}>
                <Icon name="activity" size={13} />
                <span><b>{Math.abs(pct)}% {pct < 0 ? "under" : "over"} your usual</b> — ${mine.toFixed(0)}/ft vs ${med.toFixed(0)}/ft on {rates.length} {pool.length >= 3 ? "won jobs" : "quotes"}. {pct < 0 ? "Check markup, gates and grade before you send." : "Worth a second look before you send."}</span>
              </div>
            );
          })()}
          <button className="cost-toggle" onClick={() => setShowCost(s => !s)}>
            <span className="kicker">Internal cost breakdown</span><Icon name={showCost ? "chevdown" : "chevright"} size={14} />
          </button>
          {lf > 0 && (
            <div className="mat-strip">
              <span title="Panels @ panel width"><b className="tnum">{quote.panels}</b> panels<span className="faint"> @ {quote.panelWidth}ft</span></span>
              <span title="Posts (line + end/corner)"><b className="tnum">{quote.posts}</b> posts</span>
              <span><b className="tnum">{quote.concreteBags}</b> bags</span>
              {quote.gates > 0 && <span><b className="tnum">{quote.gates}</b> gates</span>}
            </div>
          )}
          {showCost && (
            <div className="cost-grid">
              <CostCell label="Material" value={E_money0(quote.materialTotal)} />
              <CostCell label={`Labor · ${quote.laborHours.toFixed(1)}h`} value={E_money0(quote.laborCost)} />
              <CostCell label="Operator cost" value={E_money0(quote.operatorCost)} />
              <CostCell label="Gross profit" value={E_money0(quote.grossProfit)} accent="var(--green)" />
            </div>
          )}
          <div className="margin-bar">
            <div className="row between" style={{ fontSize: 12 }}>
              <span className="muted">Margin</span>
              <span className="tnum" style={{ fontWeight: 700, color: quote.marginPct >= 45 ? "var(--green)" : quote.marginPct >= 30 ? "var(--amber)" : "var(--red)" }}>{quote.marginPct.toFixed(0)}%</span>
            </div>
            <div style={{ height: 6, borderRadius: 99, background: "var(--surface-3)", overflow: "hidden", marginTop: 5 }}>
              <div style={{ width: Math.min(100, quote.marginPct) + "%", height: "100%", background: quote.marginPct >= 45 ? "var(--green)" : quote.marginPct >= 30 ? "var(--amber)" : "var(--red)", transition: "width .3s" }} />
            </div>
          </div>
          <div className="row between dock-total" style={{ alignItems: "flex-end", margin: "12px 2px" }}>
            <span className="kicker">{fences.length > 0 ? `Estimate total · ${fences.length + (lf >= 1 ? 1 : 0)} fences` : "Customer price"}</span>
            <span className="display tnum" style={{ fontSize: 30, color: "var(--teal)" }}>{E_money(fences.length > 0 ? grandTotal : quote.customerTotal)}</span>
          </div>
          <div className="row gap-8 dock-actions">
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={saveProject}><Icon name="download" size={15} />{editingProject && store.projects.find(p => p.id === editingProject.id) ? "Update" : "Save"}</button>
            <button className="btn btn-primary" style={{ flex: 1.4 }} data-tour="generate" onClick={sendQuote}><Icon name="send" size={15} />Generate Estimate</button>
          </div>
          {(() => {
            // plan cap, said before the drawing is done rather than after
            const r = estimateRoom(store);
            const projId = (editingProject && editingProject.id) || (params && params.loadProject) || null;
            const regen = !!((params && params.quoteId) || sentQuoteId || (projId && store.quotes.some((q: any) => q.projectId === projId)));
            return r.atLimit && !regen && (
              <p className="faint" style={{ fontSize: 11.5, margin: "8px 2px 0" }}>
                {r.used} of {r.limit} estimates this month on {r.plan.name} — measure and Save freely; a new estimate needs an upgrade.
              </p>
            );
          })()}
          {editingProject && store.projects.find(p => p.id === editingProject.id) && (
            <div className="row between" style={{ marginTop: 8 }}>
              <span className="mono faint" style={{ fontSize: 11 }}>Editing {editingProject.job} · changes overwrite this project</span>
              <button className="btn btn-sm" style={{ background: "transparent", color: "var(--fg-3)", border: "1px solid var(--line-2)" }} onClick={deleteCurrentProject}><Icon name="trash" size={12} />Delete</button>
            </div>
          )}
        </div>
      </aside>

      {/* MAP */}
      <div className="est-map">
        {/* Phone-only map toolbar. The side panel sits BELOW the map on a phone,
            so Draw / Undo / Redo were a screen away from the thing being drawn.
            Same handlers as the panel; hidden on desktop via CSS. */}
        <div className="est-map-tools" role="toolbar" aria-label="Drawing tools">
          <button className={"btn btn-sm " + (manual ? "btn-primary" : "btn-outline")} onClick={() => { setManual(m => !m); if (!manual) setParcel(null); }}><Icon name="pen" size={13} />{manual ? "Drawing" : "Draw"}</button>
          {/* the shortcuts (H, G, grade points) as buttons — desktop and phone alike */}
          {!manual && parcel && <button className="btn btn-outline btn-sm" onClick={fitToHouse} title="Tie the fence into the house sides (H)"><Icon name="building" size={13} />House</button>}
          {!manual && parcel && <button className="btn btn-outline btn-sm" title="Add a gate on the highlighted wall (G)" onClick={() => { const cur = activeSideRef.current; if (cur == null) flash("Click a wall first, then add the gate"); else addGate(cur); }}><Icon name="plus" size={13} />Gate</button>}
          {!manual && parcel && <button className={"btn btn-sm " + (gradeMode ? "btn-primary" : "btn-outline")} aria-pressed={!!gradeMode} title="Drop measured rise / drop points on a run" onClick={() => setGradeMode(g => { const n = !g; flash(n ? "Grade points on — click a run to drop a measured rise/drop" : "Grade points off"); return n; })}><Icon name="ruler" size={13} />Grade</button>}
          {manual && <button className="btn btn-outline btn-sm" onClick={() => setMVerts(v => v.slice(0, -1))} disabled={!mVerts.length} aria-label="Remove last point"><Icon name="undo" size={13} />Last</button>}
          {manual && mVerts.length >= 2 && <button className="btn btn-primary btn-sm" onClick={promoteManualToParcel}><Icon name="check" size={13} />Finish</button>}
          {!manual && <button className="btn btn-outline btn-sm" onClick={undo} disabled={hist.current.idx <= 0} aria-label="Undo"><Icon name="undo" size={13} /></button>}
          {!manual && <button className="btn btn-outline btn-sm" onClick={redo} disabled={hist.current.idx >= hist.current.stack.length - 1} aria-label="Redo"><Icon name="undo" size={13} style={{ transform: "scaleX(-1)" }} /></button>}
        </div>
        <div ref={mapEl} className="leaflet-host" />
        <div className="map-search-wrap" data-tour="address">
          <form className="map-search" onSubmit={doSearch}>
            <Icon name="search" size={16} className="muted" />
            <input value={search} onChange={e => onSearchChange(e.target.value)} onKeyDown={onSearchKey}
              onFocus={() => { if (search.trim().length < 3) { const r = loadRecents(); if (r.length) { setSugg(r.map((x) => ({ ...x, _recent: true }))); setShowSugg(true); } } else if (sugg.length) setShowSugg(true); }} onBlur={() => setTimeout(() => setShowSugg(false), 160)}
              placeholder="Search an address or click any property…" />
            {loading && <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />}
            {!loading && search && <button type="button" className="search-x" onClick={() => { setSearch(""); setSugg([]); setShowSugg(false); }}><Icon name="x" size={14} /></button>}
          </form>
          {showSugg && sugg.length > 0 &&
            <div className="sugg-menu">
              {sugg.map((s, i) => (
                <button key={i} className={"sugg-item" + (i === suggIdx ? " on" : "")} onMouseDown={e => e.preventDefault()} onClick={() => pickSugg(s)} onMouseEnter={() => setSuggIdx(i)}>
                  <Icon name={s._recent ? "undo" : "mappin"} size={14} className="muted" />
                  <div style={{ minWidth: 0 }}><div className="sugg-l1">{s.line1}</div><div className="sugg-l2">{s.line2 || (s._recent ? "Recent search" : "")}</div></div>
                </button>
              ))}
              <div className="sugg-foot">Powered by OpenStreetMap</div>
            </div>}
        </div>
        {!parcel && !manual && !loading &&
          <div className="map-hint"><Icon name="mappin" size={15} /> Search an address or click a property to auto-outline its parcel</div>}
        {parcel && !manual &&
          <button className={"map-lock" + (mapLocked ? " on" : "")} onClick={() => setMapLocked(l => !l)}>
            <Icon name="lock" size={14} />{mapLocked ? "Map locked — click to pan / pick new" : "Lock map"}
          </button>}
        {parcel && !manual &&
          <button className={"map-grade" + (gradeMode ? " on" : "")} title="Mark surveyor-style drop/rise points along each run — they show on the site plan and can drive slope pricing (Settings → Quoting)"
            onClick={() => setGradeMode(g => { const n = !g; flash(n ? "Grade points on — click a run to drop a measured rise/drop" : "Grade points off"); return n; })}>
            <Icon name="ruler" size={14} />{gradeMode ? "Grade points on — click a run" : "Grade points"}
          </button>}
        {parcel && !manual && (showMapKeys ? (
          <div className="map-keys">
            <div className="map-keys-head">
              <span><Icon name="sliders" size={12} />Shortcuts</span>
              <button onClick={() => setShowMapKeys(false)} title="Hide shortcuts"><Icon name="x" size={12} /></button>
            </div>
            <div className="map-keys-body">
              {[["B", "box-delete"], ["V", "select"], ["[ ]", "prev / next side"], ["I / O", "side in / out"], ["G", "gate"], ["S", "split"], ["E", "extend run"], ["Q", "square up"], ["F", "fit to lot line"], ["⇧F", "fit picked wall"], ["H", "fit to house"], ["L", "lot line"], ["?", "all keys"]].map(([k, d]) => (
                <span className={"mk" + (k === "B" && boxSelect ? " hot" : "") + (k === "V" && !boxSelect ? " hot" : "")} key={k}><kbd>{k}</kbd>{d}</span>
              ))}
            </div>
          </div>
        ) : (
          <button className="map-keys-chip" onClick={() => setShowMapKeys(true)} title="Show keyboard shortcuts"><Icon name="sliders" size={14} /></button>
        ))}
        {toast && <div className="est-toast fade-up">{toast}</div>}
        {cornerMenu && (
          <>
            <div className="cm-scrim" onClick={() => setCornerMenu(null)} onContextMenu={(e) => { e.preventDefault(); setCornerMenu(null); }} />
            <div className="corner-menu" style={{ left: Math.min(cornerMenu.x, window.innerWidth - 210), top: Math.min(cornerMenu.y, window.innerHeight - 230) }}>
              <div className="cm-head">
                Corner {cornerMenu.idx + 1}
                {cornerMenu.angle != null && <span className={"cm-ang" + (Math.abs(cornerMenu.angle - 90) <= 3.5 ? " sq" : "")}>{Math.round(cornerMenu.angle)}°</span>}
              </div>
              {parcel && parcel.closed === false && (cornerMenu.idx === 0 || cornerMenu.idx === (parcel.polygon || []).length - 1) && (
                <button className="cm-item" onClick={() => { extendFrom(cornerMenu.idx); setCornerMenu(null); }} title="Add one more ~8 ft section straight on from this end — then drag it wherever it goes (E)">
                  <Icon name="plus" size={15} />Extend run — add a section</button>
              )}
              <button className="cm-item" onClick={() => { squareCornerAt(cornerMenu.idx); setCornerMenu(null); }}><Icon name="ruler" size={15} />Square to 90°</button>
              <button className="cm-item" onClick={() => { insertCornerAfter(cornerMenu.idx); setCornerMenu(null); }}><Icon name="plus" size={15} />Insert point after</button>
              <button className="cm-item" onClick={() => { addGate(sideForCorner(cornerMenu.idx)); setCornerMenu(null); }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21V5l9-2 9 2v16M3 21h18M9 21v-7l3-1 3 1v7" /></svg>Add gate on next run</button>
              {/* grade survey from the post: corners carry ABSOLUTE elevations
                  ("−72″ vs base") — the gradual hill needs no mid-run points.
                  Mid-run drop/rise bumps still come from clicking the run line. */}
              <button className="cm-item" onClick={() => { setCornerElevPop({ idx: cornerMenu.idx, x: cornerMenu.x, y: cornerMenu.y }); setCornerMenu(null); }}>
                <Icon name="ruler" size={15} />{ceOf(cornerMenu.idx) != null ? <>Corner elevation · {fmtIn(ceOf(cornerMenu.idx))}</> : <>Set corner elevation…</>}
              </button>
              <button className="cm-item" onClick={() => { setCornerElev(cornerMenu.idx, 0); setCornerMenu(null); }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2" fill="currentColor" /></svg>Set as base 0 point</button>
              <div className="cm-sep" />
              <button className="cm-item danger" onClick={() => { deleteCorner(cornerMenu.idx); setCornerMenu(null); }}><Icon name="trash" size={15} />Delete corner</button>
            </div>
          </>
        )}
        {sideMenu && (() => {
          const s = parcel && parcel.sides.find(x => x.i === sideMenu.i);
          return (
            <>
              <div className="cm-scrim" onClick={() => setSideMenu(null)} onContextMenu={(e) => { e.preventDefault(); setSideMenu(null); }} />
              <div className="corner-menu" style={{ left: Math.min(sideMenu.x, window.innerWidth - 210), top: Math.min(sideMenu.y, window.innerHeight - 250) }}>
                <div className="cm-head">Side {sideMenu.i + 1}{s ? ` · ${Math.round(s.lf)} ft` : ""}</div>
                <button className="cm-item" onClick={() => { addGate(sideMenu.i, sideMenu.t != null ? sideMenu.t : 0.5); setSideMenu(null); }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21V5l9-2 9 2v16M3 21h18M9 21v-7l3-1 3 1v7" /></svg>Add gate here</button>
                <button className="cm-item" onClick={() => { setGradePop({ i: sideMenu.i, t: sideMenu.t != null ? sideMenu.t : 0.5, x: sideMenu.x, y: sideMenu.y }); setSideMenu(null); }}><Icon name="ruler" size={15} />Add grade point here</button>
                <button className="cm-item" onClick={() => { if (sideMenu.pos) insertCornerAt(sideMenu.i, sideMenu.pos); else splitSide(sideMenu.i); setSideMenu(null); }}><Icon name="plus" size={15} />Split / add point</button>
                <button className="cm-item" onClick={() => { toggleSide(sideMenu.i); setSideMenu(null); }}><Icon name={s && s.selected ? "x" : "check"} size={15} />{s && s.selected ? "Exclude from fence" : "Include in fence"}</button>
                <div className="cm-sep" />
                <div className="cm-label">Setback off lot line</div>
                <button className="cm-item" onClick={() => { fitToLotLine(sideMenu.i); setSideMenu(null); }} title="Slide this wall out onto the lot line it runs along (Shift+F)"><Icon name="mappin" size={15} />Fit onto lot line</button>
                <button className="cm-item" onClick={() => offsetSide(sideMenu.i, 0.5)} title="Move this wall 6 inches onto the lot — tap again to keep insetting"><Icon name="ruler" size={15} />In 6&Prime; &nbsp;<span className="faint" style={{ marginLeft: "auto", fontSize: 11 }}>away from line</span></button>
                <button className="cm-item" onClick={() => offsetSide(sideMenu.i, -0.5)} title="Move this wall 6 inches back toward / over the lot line"><Icon name="ruler" size={15} />Out 6&Prime; &nbsp;<span className="faint" style={{ marginLeft: "auto", fontSize: 11 }}>toward line</span></button>
                <div className="cm-sep" />
                <button className="cm-item danger" onClick={() => { deleteSide(sideMenu.i); setSideMenu(null); }}><Icon name="trash" size={15} />Delete side</button>
              </div>
            </>
          );
        })()}
        {gradePop && parcel && (() => {
          const s = parcel.sides.find(x => x.i === gradePop.i);
          if (!s) return null;
          return (
            <>
              <div className="cm-scrim" onClick={() => setGradePop(null)} onContextMenu={(e) => { e.preventDefault(); setGradePop(null); }} />
              <GradePointPop key={gradePop.i + ":" + (gradePop.idx != null ? gradePop.idx : "new") + ":" + gradePop.t} pop={gradePop} side={s}
                style={{ left: Math.min(gradePop.x, window.innerWidth - 250), top: Math.min(gradePop.y, window.innerHeight - 250) }}
                onCommit={(t, deltaIn) => { if (gradePop.idx != null) updateGradePoint(gradePop.i, gradePop.idx, { t, deltaIn }); else addGradePoint(gradePop.i, t, deltaIn); setGradePop(null); }}
                onDelete={gradePop.idx != null ? () => { removeGradePoint(gradePop.i, gradePop.idx); setGradePop(null); } : null}
                onClose={() => setGradePop(null)} />
            </>
          );
        })()}
        {cornerElevPop && parcel && (
          <>
            <div className="cm-scrim" onClick={() => setCornerElevPop(null)} onContextMenu={(e) => { e.preventDefault(); setCornerElevPop(null); }} />
            <CornerElevPop key={"ce" + cornerElevPop.idx} idx={cornerElevPop.idx} current={ceOf(cornerElevPop.idx)}
              style={{ left: Math.min(cornerElevPop.x, window.innerWidth - 250), top: Math.min(cornerElevPop.y, window.innerHeight - 230) }}
              onCommit={(v) => { setCornerElev(cornerElevPop.idx, v); setCornerElevPop(null); }}
              onClear={ceOf(cornerElevPop.idx) != null ? () => { setCornerElev(cornerElevPop.idx, null); setCornerElevPop(null); } : null}
              onClose={() => setCornerElevPop(null)} />
          </>
        )}
        {showKeys && (
          <>
            <div className="cm-scrim" onClick={() => setShowKeys(false)} />
            <div className="keys-pop">
              <div className="cm-head" style={{ justifyContent: "space-between" }}>Keyboard shortcuts
                <button onClick={() => setShowKeys(false)} style={{ background: "none", border: 0, cursor: "pointer", color: "var(--fg-3)", padding: 0 }}><Icon name="x" size={15} /></button>
              </div>
              <div className="keys-grid">
                {[["[ / ]", "Previous / next side"], ["Enter / Space", "Include / exclude the side"], ["I / O", "Set the side in / out 6″ (Shift = 1 ft)"], ["G", "Add a gate to the side"], ["S", "Split the side"], ["Q", "Square up the whole fence"], ["F", "Fit walls onto the property line (walls far off the line stay put)"], ["Shift F", "Fit only the highlighted wall onto its lot line"], ["H", "Fit to house — tie the fence ends into the house sides, keeping your other corners"], ["L", "Toggle the property-line overlay"], ["B", "Box-delete corners (drag a box)"], ["V", "Back to the standard selector"], ["← ↑ → ↓", "Move the whole fence (Shift = faster)"], ["⌘/Ctrl Z", "Undo (Shift = redo)"], ["Delete", "Remove the selected corner / wall"], ["?", "Show / hide this list"]].map(([k, d]) => (
                  <React.Fragment key={k}><kbd className="kbd">{k}</kbd><span>{d}</span></React.Fragment>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Mobile-only sticky action bar. On a phone the side panel sits BELOW the
          map (order 2), so the dock's Save / Generate buttons are a long scroll
          away. This keeps the live total and the primary "Generate Estimate"
          action one tap away no matter where you are. Hidden on desktop via CSS
          (the dock already carries these). */}
      <div className="est-mobile-actions" {...swipe}>
        <button className="btn btn-outline ema-details" aria-expanded={!!sheetOpen} aria-label={sheetOpen ? "Hide details" : "Show details"} onClick={() => setSheetOpen((o: any) => !o)}>
          <Icon name="chevdown" size={15} style={{ transform: sheetOpen ? "none" : "rotate(180deg)" }} />
        </button>
        <div className="ema-total">
          <span className="ema-label">{fences.length > 0 ? `Total · ${fences.length + (lf >= 1 ? 1 : 0)} fences` : "Customer price"}</span>
          <span className="display tnum ema-price">{E_money(fences.length > 0 ? grandTotal : quote.customerTotal)}</span>
        </div>
        <button className="btn btn-ghost ema-save" onClick={saveProject} title={editingProject && store.projects.find(p => p.id === editingProject.id) ? "Update this project" : "Save this estimate"} aria-label="Save estimate">
          <Icon name="download" size={17} />
        </button>
        <button className="btn btn-primary ema-generate" data-tour="generate" onClick={sendQuote}>
          <Icon name="send" size={15} />Generate
        </button>
      </div>
    </div>
  );
}

/* ---- small parts ---- */
function ParcelBadge({ status }: any) {
  const map = {
    "wi-live":     { label: "WI Live",     cls: "live" },
    "fl-live":     { label: "FL Live",     cls: "live" },
    "county-live": { label: "County Live", cls: "live" },
    "sample":      { label: "Sample",      cls: "sample" },
    "none":        { label: "No record",   cls: "sample" },
  };
  const m = map[status] || map["sample"];
  return (
    <span className={"parcel-badge " + m.cls}>
      <span className="pb-dot" />{m.label}
    </span>
  );
}
function CustomerPicker({ store, customer, setCustomer }: any) {
  const customers = store.customers || [];
  const [open, setOpen] = React.useState<any>(false);
  const [q, setQ] = React.useState<any>("");
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h); return () => window.removeEventListener("mousedown", h);
  }, [open]);
  if (customers.length === 0) return null;
  const filtered = q ? customers.filter(c => ((c.name || "") + " " + (c.email || "") + " " + (c.address || "")).toLowerCase().includes(q.toLowerCase())) : customers;
  function pick(c) {
    setCustomer(seedCustomer(c));
    setOpen(false); setQ("");
  }
  return (
    <div ref={ref} style={{ position: "relative", marginBottom: 10 }}>
      <button className="btn btn-ghost btn-sm btn-block" style={{ justifyContent: "space-between" }} onClick={() => setOpen(o => !o)}>
        <span className="row gap-8">{customer.customerId ? <><Icon name="check" size={14} style={{ color: "var(--teal)" }} />{customer.name}</> : <><Icon name="user" size={14} />Pick existing customer</>}</span>
        <Icon name="chevdown" size={14} className="muted" />
      </button>
      {open && (
        <div className="cust-pick-pop">
          <div className="cust-search" style={{ borderRadius: "var(--r-md) var(--r-md) 0 0" }}>
            <Icon name="search" size={14} className="muted" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${customers.length} customers…`} />
          </div>
          <div className="cust-pick-rows">
            {filtered.length === 0
              ? <p className="muted" style={{ fontSize: 12.5, textAlign: "center", padding: "16px" }}>No matches</p>
              : filtered.map(c => (
                <button key={c.id} className="cust-pick-row" onClick={() => pick(c)}>
                  <div className="cust-ava" style={{ width: 28, height: 28, fontSize: 13 }}>{(c.name || "?").trim().charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name || "Unnamed"}</div>
                    <div className="muted" style={{ fontSize: 11.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.email || c.phone || c.address || "No contact"}</div>
                  </div>
                </button>
              ))}
          </div>
          {customer.customerId && <button className="cust-pick-clear" onClick={() => { setCustomer({ name: "", phone: "", email: "", address: customer.address, street: customer.street, city: customer.city, state: customer.state, zip: customer.zip, customerId: undefined }); setOpen(false); }}>Clear &amp; enter new customer</button>}
        </div>
      )}
    </div>
  );
}

function Section({ icon, title, right, children, defaultOpen = true }: any) {
  const [open, setOpen] = React.useState(defaultOpen !== false);
  return (
    <div className={"est-section" + (open ? "" : " collapsed")}>
      <div className="row between" style={{ marginBottom: open ? 11 : 0 }}>
        <button type="button" className="row gap-8 kicker kicker-teal" aria-expanded={open} onClick={() => setOpen((v: any) => !v)}
          style={{ background: "none", border: 0, padding: 0, cursor: "pointer", fontFamily: "inherit" }}>
          <Icon name="chevright" size={11} style={{ transition: "transform .15s", transform: open ? "rotate(90deg)" : "rotate(0deg)" }} />
          <Icon name={icon} size={14} />{title}
        </button>{right}
      </div>
      {open && children}
    </div>
  );
}
function Stepper({ label, value, set }: any) {
  return (
    <div style={{ flex: 1 }}>
      <label className="field-label">{label}</label>
      <div className="stepper"><button onClick={() => set(Math.max(0, value - 1))}>–</button><span className="tnum">{value}</span><button onClick={() => set(value + 1)}>+</button></div>
    </div>
  );
}
function Toggle({ on, set, label, note }: any) {
  return (
    <button className={"toggle-row" + (on ? " on" : "")} onClick={() => set(!on)}>
      <span className={"sw" + (on ? " on" : "")}><span className="knob" /></span>
      <span style={{ fontSize: 13.5, fontWeight: 600 }}>{label}</span>
      <span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>{note}</span>
    </button>
  );
}
/* a Toggle that expands a compact editor row when ON — so the amount behind
   an add-on (%, $, wording) is adjustable per estimate right where it's
   switched on, instead of being locked to the workspace default. */
function ToggleAdj({ on, set, label, note, children }: any) {
  return (
    <div>
      <Toggle on={on} set={set} label={label} note={note} />
      {on && children && (
        <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center", padding: "8px 10px 2px 34px" }}>
          {children}
        </div>
      )}
    </div>
  );
}
function CostCell({ label, value, accent }: any) {
  return (
    <div className="cost-cell">
      <div className="muted" style={{ fontSize: 11.5 }}>{label}</div>
      <div className="display tnum" style={{ fontSize: 17, color: accent || "var(--fg)" }}>{value}</div>
    </div>
  );
}

/* ---- measured grade point widgets ---- */
// inline add/edit popover for a grade point dropped on the map
function GradePointPop({ pop, side, style, onCommit, onDelete, onClose }: any) {
  const lfv = Math.max(1, lfFeet(side.lf) || 1);
  const [ft, setFt] = React.useState<any>(() => String(Math.round((pop.t || 0) * lfv)));
  const [inches, setInches] = React.useState<any>(() => pop.deltaIn != null ? String(+Math.abs(pop.deltaIn).toFixed(1)) : "");
  const [sign, setSign] = React.useState<any>(() => (pop.deltaIn != null && pop.deltaIn > 0) ? 1 : -1);
  const inRef = React.useRef<any>(null);
  React.useEffect(() => { if (inRef.current) inRef.current.focus(); }, []);
  function commit() {
    const mag = Math.abs(parseFloat(inches));
    if (!isFinite(mag)) { if (inRef.current) inRef.current.focus(); return; }
    const t = Math.max(0.01, Math.min(0.99, (parseFloat(ft) || 0) / lfv));
    onCommit(t, sign * mag);
  }
  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } else if (e.key === "Escape") onClose(); };
  return (
    <div className="corner-menu grade-pop" style={style}>
      <div className="cm-head">{pop.idx != null ? "Edit grade point" : "Add grade point"}<span className="cm-ang">Side {pop.i + 1}</span></div>
      <div className="gp-fields">
        <div className="gp-num"><input className="tnum" type="number" min="0" max={Math.round(lfv)} value={ft} onChange={e => setFt(e.target.value)} onKeyDown={onKey} /><span>ft from run start</span></div>
        {/* quick-snap to the run's ends — surveyors shoot the corners first */}
        <div className="seg" style={{ marginTop: 6 }}>
          <button className={"seg-btn" + ((parseFloat(ft) || 0) <= 0 ? " on" : "")} onClick={() => setFt("0")}>Start</button>
          <button className={"seg-btn" + (Math.round(parseFloat(ft) || 0) >= Math.round(lfv) ? " on" : "")} onClick={() => setFt(String(Math.round(lfv)))}>End · {Math.round(lfv)}′</button>
        </div>
        <div className="row gap-8">
          <div className="seg" style={{ flexShrink: 0 }}>
            <button className={"seg-btn" + (sign < 0 ? " on" : "")} onClick={() => setSign(-1)}>− Drop</button>
            <button className={"seg-btn" + (sign > 0 ? " on" : "")} onClick={() => setSign(1)}>+ Rise</button>
          </div>
          <div className="gp-num" style={{ flex: 1 }}><input ref={inRef} className="tnum" type="number" min="0" step="0.5" placeholder="0" value={inches} onChange={e => setInches(e.target.value)} onKeyDown={onKey} /><span>in</span></div>
        </div>
        <div className="row gap-8">
          <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={commit}><Icon name="check" size={13} />{pop.idx != null ? "Save" : "Add point"}</button>
          {onDelete && <button className="btn btn-ghost btn-sm" onClick={onDelete} title="Remove this point"><Icon name="trash" size={13} /></button>}
        </div>
        <p className="faint" style={{ fontSize: 10.5, margin: 0, lineHeight: 1.4 }}>Inches vs the previous point (the first point is vs the run start).</p>
      </div>
    </div>
  );
}
// inline popover for an ABSOLUTE corner elevation (parcel.cornerElev):
// "−72″ vs base". Set base 0 on one corner, drops/rises on the others —
// every run slopes gradually between its two corners, no mid-run points.
function CornerElevPop({ idx, current, style, onCommit, onClear, onClose }: any) {
  const [inches, setInches] = React.useState<any>(() => (current != null && current !== 0) ? String(+Math.abs(current).toFixed(1)) : "");
  const [sign, setSign] = React.useState<any>(() => (current != null && current > 0) ? 1 : -1);
  const inRef = React.useRef<any>(null);
  React.useEffect(() => { if (inRef.current) inRef.current.focus(); }, []);
  function commit() {
    const mag = Math.abs(parseFloat(inches));
    if (!isFinite(mag)) { if (inRef.current) inRef.current.focus(); return; }
    onCommit(sign * mag);
  }
  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } else if (e.key === "Escape") onClose(); };
  return (
    <div className="corner-menu grade-pop" style={style}>
      <div className="cm-head">Corner elevation<span className="cm-ang">Corner {idx + 1}</span></div>
      <div className="gp-fields">
        <div className="row gap-8">
          <div className="seg" style={{ flexShrink: 0 }}>
            <button className={"seg-btn" + (sign < 0 ? " on" : "")} onClick={() => setSign(-1)}>− Below</button>
            <button className={"seg-btn" + (sign > 0 ? " on" : "")} onClick={() => setSign(1)}>+ Above</button>
          </div>
          <div className="gp-num" style={{ flex: 1, minWidth: 96 }}><input ref={inRef} className="tnum" type="number" min="0" step="0.5" placeholder="72" value={inches} onChange={e => setInches(e.target.value)} onKeyDown={onKey} style={{ minWidth: 44 }} /><span>in</span></div>
        </div>
        <div className="row gap-8">
          <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={commit}><Icon name="check" size={13} />Set elevation</button>
          <button className="btn btn-ghost btn-sm" onClick={() => onCommit(0)} title="Mark this corner as the base 0 datum">Base 0</button>
          {onClear && <button className="btn btn-ghost btn-sm" onClick={onClear} title="Clear this corner's elevation"><Icon name="trash" size={13} /></button>}
        </div>
        <p className="faint" style={{ fontSize: 10.5, margin: 0, lineHeight: 1.4 }}>Inches vs your base 0 corner (−72″ = 6 ft lower). Runs slope gradually between set corners — unset corners are interpolated.</p>
      </div>
    </div>
  );
}
// one editable row in a side's "Grade points" list (distance ft · ±inches).
// lf may be a NUMBER (estimator sides) or a STRING — possibly "" mid-edit —
// (the quote's Edit-estimate panel binds its controlled length input), so it
// is coerced via lfFeet; a blank/invalid length refuses the ft commit rather
// than silently parking the point at the wrong t.
function GradePointRow({ pt, lf, onChange, onRemove }: any) {
  const lfn = lfFeet(lf);
  const lfv = Math.max(1, lfn || 1);
  const commitFt = (v) => { const ft = parseFloat(v); if (isFinite(ft) && lfn > 0) onChange({ t: Math.max(0.01, Math.min(0.99, ft / lfn)) }); };
  const commitIn = (v) => { const d = parseFloat(v); if (isFinite(d)) onChange({ deltaIn: d }); };
  return (
    <div className="gp-row">
      <div className="gp-num"><input className="tnum" type="number" min="0" defaultValue={Math.round(pt.t * lfv)} onBlur={e => commitFt(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { commitFt((e.target as HTMLInputElement).value); (e.target as HTMLInputElement).blur(); } }} /><span>ft</span></div>
      <div className="gp-num"><input className="tnum" type="number" step="0.5" defaultValue={+(+pt.deltaIn).toFixed(1)} onBlur={e => commitIn(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { commitIn((e.target as HTMLInputElement).value); (e.target as HTMLInputElement).blur(); } }} /><span>in</span></div>
      <span className={"gp-chip" + (pt.deltaIn < 0 ? " drop" : "")}>{fmtIn(pt.deltaIn)}</span>
      <button className="gp-del" onClick={onRemove} title="Remove point"><Icon name="x" size={11} /></button>
    </div>
  );
}
// the "Add point" row: distance ft + drop/rise inches. lf tolerates a string
// (quote Edit-estimate panel) — Add stays disabled until the run length is a
// real number so a point can never land at the wrong distance.
function GradePointAddRow({ lf, onAdd }: any) {
  const [ft, setFt] = React.useState<any>("");
  const [din, setDin] = React.useState<any>("");
  const lfn = lfFeet(lf);
  const valid = lfn > 0 && isFinite(parseFloat(ft)) && isFinite(parseFloat(din));
  function addPt() {
    if (!valid) return;
    onAdd(Math.max(0.01, Math.min(0.99, parseFloat(ft) / lfn)), parseFloat(din));
    setFt(""); setDin("");
  }
  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); addPt(); } };
  return (
    <div className="gp-row">
      <div className="gp-num"><input className="tnum" type="number" min="0" placeholder="ft" value={ft} onChange={e => setFt(e.target.value)} onKeyDown={onKey} /><span>ft</span></div>
      <div className="gp-num"><input className="tnum" type="number" step="0.5" placeholder="−drop +rise" value={din} onChange={e => setDin(e.target.value)} onKeyDown={onKey} /><span>in</span></div>
      <button className="gp-add" onClick={addPt} disabled={!valid}><Icon name="plus" size={11} />Add point</button>
    </div>
  );
}

Object.assign(window, { Estimator });

export { toXY, toLL, cornerAngle, squareCorner, centroid, dirName, gateGeometry, buildSides, Estimator, ParcelBadge, CustomerPicker, Section, Stepper, Toggle, CostCell, E_money, M_TO_FT, segFeet, COMPASS, GRADES, GradePointRow, GradePointAddRow };
